import { findHeaderCol as findC } from "../lib/xlsxHeaders"; // A15, brief 65
import { tempId } from "../lib/tempId"; // 207 §1.2 — a temporary id is never a server id
import React from "react"; // react/react-in-jsx-scope is counted per JSX element; the rule's own remedy (182 C.1)
import { useState, useRef, useEffect, useCallback, Fragment, type CSSProperties, type Dispatch, type ReactNode } from "react";
import {
  Pencil,
  Trash2,
  Plus,
  X,
  Search,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Upload,
  Download,
  FileText,
  Play,
  Archive,
  Pin,
  PinOff,
  Send,
  MessageCircle,
  Phone,
  AlertTriangle,
  Gavel,
  Info,
  Calendar,
  Clock,
  Users,
  CalendarClock,
  Mail,
  HardDrive,
  Cloud,
  Folder,
  Globe,
  Link2,
  Database,
  ExternalLink as ExtLink2,
  BookOpen,
  Paperclip,
  Server,
  Lock,
  CalendarPlus,
  StickyNote,
} from "lucide-react";
import {
  fc,
  fq,
  fd,
  DOC_STATUSES,
  itemLevelMap,
  itemLevelOrder,
  itemLevelBase,
  TASK_STATUSES,
  evDayClr,
  pm,
  contrastText,
  REMINDER_OPTS,
  scrollToEv,
  queueScrollToEv,
  checkScrollToEv,
  peekScrollToEv,
  checkScrollToTk,
  queueScrollToTk,
  getLatestRegNum,
  getLatestRegStage,
  eisenQuadrant,
  eisenAuto,
  EISEN_QUADRANTS,
  ctc,
} from "../utils";
import {
  LockButton,
  MyAllToggle,
  SegmentedControl,
  InitialsBadge,
  AddButton,
  SectionHeader,
  ModalLayout,
} from "../components/SharedUI";
import { ArchivedHoleBadge, LoadedOnUse, SaveButton, SortCaret } from "../components/SharedUI"; // 208 §1 — LoadedOnUse: the bulk mailer, on use; §8 — the app's one sort caret
import { choosableCo } from "../lib/collaboratorState";
// 141 §1/§2.1 — the lifecycle has ONE owner. `KNOWN_STATUSES` was imported
// here to build the picker's rows; §2.2 moved that construction to
// `lib/linkStatus.ts`, which imports it from the same place, so this view now
// takes only the date slot each value stamps.
import { linkStatusDateCol, KNOWN_STATUSES } from "../lib/collectiveSegments.mjs"; // 208 §8 — the status column sorts in the lifecycle's order
// 191B §A (item 8) — the collective form displays the one success-fee default
// the money counts with (src/lib/successFee.mjs); `?? 15` stood here before.
import { successFeeRateOf } from "../lib/successFee.mjs";
import StatusPill from "../components/StatusPill"; // 141 §1 — the 136 §1 pill, not a second one
// 141 §2.2 — the pill's colours and its option rows, shared with CaseView.
import { linkStatusCfg, linkStatusOptions } from "../lib/linkStatus";
import { EisenBadge, EISEN_LABELS, eisenLabel } from "../EisenBadge";
import SubtaskCounter, { EventSubtasks } from "../components/SubtaskCounter";
import CaseTimeEntriesSection from "../components/CaseTimeEntriesSection"; // 86 Phase 2 step 1
import CaseEmailsPane from "../components/CaseEmailsPane"; // 86 Phase 2 step 2
import CallAvatar from "../components/CallAvatar"; // 86 Phase 2 step 3
import CaseInfoSection from "../components/CaseInfoSection"; // 86 Phase 2 step 3
import CaseTasksSection from "../components/CaseTasksSection"; // 86 Phase 2 step 4
import CaseStageTimeline, { stageMatchesQuery, type Stage as StageRow } from "../components/CaseStageTimeline"; // 86 Phase 2 step 5 · 186 (e)
import SectionSearch from "../components/SectionSearch"; // 186 (e) [RULED 186 (16)]
import CaseAgendaSection from "../components/CaseAgendaSection"; // 86 Phase 2 step 6
import CaseTeamSection from "../components/CaseTeamSection"; // 86 Phase 2 step 7
import CaseDescriptionCard from "../components/CaseDescriptionCard"; // 86 Phase 2 step 10
import { TaskMatrix } from "../TaskMatrix";
import { Grid2x2 } from "lucide-react";
import { loadXlsx, onUse, openOneDrivePicker } from "../lib/loadOnUse.mjs"; // 208 §1 — the Excel library loads the first time a sheet is read or written; the picker and the mailer, when opened
// Phase 43 (item 4) — shared archived-import cell coercion + batch dedup.
import { parseDateCell } from "../lib/importCells.mjs";
import { makeImportVocabularyReport } from "../lib/importVocabulary.mjs";
import { xlsxRow, xlsxAoa } from "../lib/xlsxCells.mjs";
import {
  parseRetainerHeader,
  reconcileRounds,
} from "../lib/retainerImport.mjs";
import { batchClientDuplicates } from "../lib/coiMatch.mjs";
import ProFormaModal, {
  getInvoiceHeaderLabel,
} from "../components/modals/ProFormaModal";
import EntityList from "../components/EntityList";
import AmountField from "../components/AmountField"; // 207C §2 — the one amount field (the quantum, twice)
import { normalizeLang, storedClientLanguage, storedCaseLanguage } from "../lib/docLang.mjs"; // 207C §6 — a client's language as stored; 209 §5 — the case's
import { languageOptions, OPEN_MATTER_FORM, takePendingMatterForm, importedCaseLanguage, type MatterRef } from "../lib/caseLanguage"; // 209 §5, §19
import type { ToastFn } from "../components/Toast"; // 209 §5
import { buildMatterListCfg, columnGateOf } from "../lib/matterListCfg";
// 213B §22 [RULED 50] — the adapted due of a round and its note: pure helpers over the links the view holds as `any`
import { dueNoteOf, membersWithoutOverride, dueInResult, linksWithAdaptedDue, linksWithFormulaDue, parseAdaptedAmount } from "../lib/dueAdapt";
import { NO_AUTOFILL, noAutofillClass } from "../lib/noAutofill"; // 213B §18's bundle on §22's note field
import type { ProspectFilterValue } from "../lib/matterListCfg"; // 139 §2.7
import { useTaxonomy, useTaxonomyList } from "../hooks/useTaxonomy";
import { customFieldColumns } from "../lib/customFieldColumns"; // 189 §B
import { firstMeetingTypeCode, meetingDefaultModes } from "../utils/taxonomyMap"; // 123 §3 / 124 §1
import { clientsFamilyKey } from "../lib/matterLabel"; // 123 §4
// RULED 2026-07-25 — the CC modal pre-fills from FirmSettings.ccRefPattern,
// through the SAME pure grammar the server allocates with.
import { nextRefFrom } from "../lib/caseRefPattern.mjs";
import { resolveTaxonomyLabel, resolveLongLabel, resolveTaxonomyEntry } from "../lib/taxonomyHelpers";
import { firmCountryCode } from "../lib/firmAddress.mjs"; // 207 §13 — a new client's country
import TaxonomyOrphanMark from "../components/TaxonomyOrphanMark";
import FieldSelect from "../components/FieldSelect";
import Combobox from "../components/Combobox";
import InvoiceTable from "../components/InvoiceTable";
import CaseEmailsSection from "../components/CaseEmailsSection";
import TimeEntryTable from "../components/TimeEntryTable";
import DocTrackingMatrix from "./DocTrackingMatrix";
import CollectiveContactListModal from "../components/CollectiveContactListModal";
import type { BulkMailerMode } from "../components/CollectiveBulkMailerModal"; // 208 §1 — a TYPE, erased at build: the mailer itself loads on use (`loadBulkMailer`)
import CddCaseSection from "./CddCaseSection";
import CaseFinancialPanel from "../components/CaseFinancialPanel";
import CaseCalculatorSection from "./CaseCalculatorSection";
import {
  useConfirmAndUndo,
  ConfirmDeleteDialog,
  UndoToast,
  useRefLatest,
} from "../components/useConfirmAndUndo";
import { usePermissions } from "../hooks/usePermissions";
import {
  api,
  upsertChatReadReceipt,
  // Phase 30 (D-2) — the inline CC chat persists through the real chat API.
  createChatMessage,
  updateChatMessage,
  deleteChatMessage,
  // 164 §A.6 — replace-mode orphans are archived, never hard-deleted.
  archiveClient,
  convertClient, // 168 §1e — the promotion clears the person's own tag
  // C.2 — the retainer-round money engine, relocated server-side.
  computeRetainerRounds,
  // C.2 — the case/CC financials engine, relocated server-side.
  computeCaseFinancials,
  // 161 §9 — the matter's overheads share (R9), the recap's input.
  getOverheadsShare,
  // 140 §3.3 — the collective half of the switch (brief 04 unit 8).
  convertCollectiveCase,
  convertCollectiveCaseUndo,
  convertCollectiveCaseFinalize,
} from "../api";
import type { TeBulkUpdateResult } from "../api"; // 145 §1 — the bulk-edit result the table hands back
import { ListLoadContext, PAGE_LISTS } from "../lib/listLoad"; // 211 §10 — the timeline's own lists, when they could not load
import { usePageList, usePageListContext, readList } from "../hooks/usePageList";
import { GenerateDocButton } from "../components/GenerateDocModal";
import CaseDocumentsSection from "../components/CaseDocumentsSection";
import CaseTimelineSection from "../components/CaseTimelineSection";
// 182 A.3 — the Notes section (the contact timeline), mounted after the timeline.
import InteractionSection from "../components/InteractionSection";
import CaseTaskRow from "../components/CaseTaskRow";
import CaseEventRow from "../components/CaseEventRow";
import { useRoleLabels } from "../hooks/useRoleLabels";
import { redMatterFin, redEntryFin, redCoFin, redRoleRows,
  redFinInv, redRecapInv,
} from "../lib/financialsClient";
import { useTranslation, tImperative } from "../i18n";
import { CT, RAD, FS, Z } from "../lib/tokens";
import { TYPE_CHIP } from "../components/TypeTagBox"; // 205 §1 — the activity chip, the one definition
import {
  TH as TH2,
  MC as MC2,
  ML as ML2,
  HC as HC2,
  TC,
  SH,
  EC,
  pctCo,
  ratCo,
  pct1,
} from "../lib/moneyTableCells"; // A1, brief 65 (HC: 86 §2b); TC · SH · the two faces: 199 §F; EC · pct1: 214 addendum 1
import { foreignOf } from "../components/MoneyStack"; // 169 R7
import type { Translate } from "../lib/interestRefusal"; // 199 §F — the translator's one shape, narrowed at the recap's seam
import { fmtFormula, formulaIsBlank } from "../lib/formulaParser"; // 208 §2 — a tranche without a formula says so
import { MEMBER_SORT_DEFAULT, memberMatches, memberNameMatches, nextMemberSort, orderIdsByParty, orderParties, sortMemberRows, trancheSortKey, type MemberMoney, type MemberSort, type NamedClient } from "../lib/memberOrder"; // 216 §4 [RULED 71] — orderParties / orderIdsByParty: every list of the case's people in the one order // 208 §6 + §8 — the list by name, A→Z; every column sorts; §7 — the search
import { teamWithRpFirst, RP_BADGE_STYLE } from "../lib/teamOrder"; // brief 48 §2
import { readMatterMetrics } from "../lib/matterMetrics"; // A4, brief 65
import { meetingSeed } from "../lib/meetingSeed"; // A6, brief 65
import { matterCast } from "../lib/matterCast"; // 119 — §1c's pre-step
// 118 §6 (the flatten) + 119 §5 (the three groups, one grouping function)
import {
  meetingRoster,
  teamIdSet,
  PEOPLE_GROUP_KEY,
} from "../lib/meetingAttendees";
import { mailDialogSeed } from "../lib/mailDialogSeed"; // A8, brief 65
import { nextEventDate } from "../lib/nextEventDate"; // A16, brief 65
import { sortStagesByStart } from "../lib/stageOrder"; // A18, brief 65
import { localTodayStr } from "../lib/localToday"; // A20, brief 65
import { makeImportCell } from "../lib/importCell"; // A10, brief 65
import {
  applyFormulaReleases,
  dupKey,
  importColumns,
  nameOf,
  parseImportRows,
  planImport,
  importOutcomeCheck,
  importReadCells,
  clearImportMark,
  holdConflictingQuantums, // 211 §18
  quantumsAfterPlan, // 211 §18
  terminatedDatesAfterPlan, // 214 addendum 2 §20 [RULED 60]
  type CellToCheck,
  type ImportLinkRow,
  type ImportParsedRow,
} from "../lib/ccClientImport"; // 207 §1.3 — the importer's pure half, moved
// 211 Part Eight [RULED 24] — a member's numbers and quantum in THIS case: its link's, never the client's
import { memberNumbers, memberQuantum, numbersShown, numbersWritten, parseNumbers, quantumWritten, withMemberNumbers, withOwnQuantum } from "../lib/memberRefs.mjs";
import { normFormDates } from "../lib/formDates"; // A13, brief 65
import { useConfirm } from "../components/useConfirmAndUndo";
import { ClientContactsSublist, OpponentContactsSublist, OpponentCounselSublist } from "../components/GroupedContactsSublist"; // B24, brief 84; 214 §2 the counsel by party
import { counselChipExtra, clientCounselChipExtra } from "../components/CounselChipExtra"; // 214 §2–§4
import LinkSuspendDialog from "../components/LinkSuspendDialog"; // 214 §3 — « Suspendre » asks the date
import { actingFirst, ccElementOf, ccElementsWith, ccLinksOf, ccMembersAdded, freshPartyElement, isClientCounsel, isSuspended, linkOf, mergeCcElements, todayIso, type CounselLink, type IdToBool, type IdToNode, type LinkMap } from "../lib/counselLinks"; // 214; addendum 2 §18 the sheet's merge; addendum 3 §21 the other columns; addendum 4 §26 the members added, never removed
import { refusedReasons, sheetRowMatch, withoutAbsentColumns, type RefusedRow } from "../lib/sheetColumns"; // 214 addendum 3 §24 — a column the sheet lacks leaves the stored value; addendum 5 §29 — where a row lands
import ImportLossDialog from "../components/ImportLossDialog"; // 214 addendum 4 §25 [RULED 63] — an import asks before it loses anything
import { addLoss, addRecordLosses, addRowsTaken, addUnlinkedMembers, entryId, hasEmptied, hasLoss, linkIds, listPools, matterLabel, matterTitle, newLossPlan, poolNamer, rowLosses, withoutEmptyCells, withoutEmptying, type DiffField, type LossPlan } from "../lib/importLoss"; // 214 addendum 4 §25
import { pname as pnameUtil } from "../utils"; // 214 addendum 4 §25 — the hoisted importers name a list entry (the view's `pname` is its prop)
import type { NameOf } from "../lib/fnTypes"; // 214 — `pname` read typed once
import type { TFn, TaxList } from "../lib/taxonomyTypes";
import { ImportErrorModal } from "../components/ImportErrorModal"; // B48, brief 84
import { ImportLoadingModal } from "../components/ImportLoadingModal"; // B60, brief 84
import { ImportTriggerButton } from "../components/ImportTriggerButton"; // B44, brief 84
import { SEC_COL_DEFAULTS } from "../lib/secColDefaults"; // C80, brief 84
import MenuSelect from "../components/MenuSelect"; // 154B §2
// 190 §D — the board over the collective list, a view over the same rows; a move writes a Bento placement.
import CaseBoardView from "./CaseBoardView";
import { BentoPicker } from "../components/BentoBoard"; // 190 [I] — the picker beside the list icon
import type { BoardCase, BoardField } from "./CaseBoardView";
/* [C] — what the pane reads off one of the list's column declarations
   (matterListCfg.tsx `colsT`, customFieldColumns.tsx): the key, the header
   label or its key, and the cell renderer EntityList calls the same way. */
type BoardColumn = { k: string; l?: string; lk?: string; raw?: boolean; r: (c: unknown, ctx: unknown) => React.ReactNode };
import { useBento } from "../hooks/useBento";
import type { Dispatch as BentoDispatch } from "react";
import type { TFn as BentoTFn } from "../lib/taxonomyTypes";
import Table from "../components/Table";
import CustomFieldsBlock from "../components/CustomFieldsBlock";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count` // 189 §B

/* 208 §1 — THE BULK MAILER LOADS WHEN IT OPENS: its module is reached only
   through this loader (the one name the page takes from it is a type), so
   nothing of it rides with the page until « Bulk mailer » or « Broadcast
   invitation » is clicked. */
const loadBulkMailer = onUse(() => import("../components/CollectiveBulkMailerModal"));

/* 208 §5 — the fields of a client of the firm the add window reads: its completion
   list (the name, the identifier) and a pick (the details it fills in). */
interface PickableClient {
  id: number;
  type?: string | null;
  title?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  name?: string | null;
  vat?: string | null;
  bceNumber?: string | null;
  nationalRegister?: string | null;
  nationalRegisterId?: string | null;
  contractNumber?: string | null;
  quantum?: number | string | null;
  email?: string | null;
  phone?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  notes?: string | null;
}

/* 208 §8 — A SORTABLE HEADER OF THE MEMBERS TABLE. InvoiceTable's family
   (its hoisted `TH`; 188 (6): the sortable headers get no primitive): the
   table's own header dress, the pointer, and the app's one caret after the
   words. Module scope, so it keeps its identity across renders (Bug A). */
function MemberTh({ k, sort, onSort, align = "left", children }: Readonly<{ k: string; sort: MemberSort; onSort: Dispatch<string>; align?: "left" | "right" | "center"; children?: ReactNode }>) {
  return (
    <th
      style={{ padding: "6px 8px", fontSize: FS.s9, fontWeight: 600, color: "var(--tm)", textTransform: "uppercase", letterSpacing: ".4px", textAlign: align, cursor: "pointer", userSelect: "none" }}
      onClick={() => {
        onSort(k);
      }}
    >
      {children}
      <SortCaret active={sort.k === k} d={sort.d} />
    </th>
  );
}

/* 211 Part Eight §17 [RULED 24] — THE CONTRACT CELL: THIS CASE'S NUMBERS, shown
   « , »-separated (src/lib/memberRefs.mjs). Locked, the text; unlocked, edited in
   place as the Quantum cell beside it edits — typed with « , » or « ; » between
   two, saved when the cell is left (Enter leaves it, Escape puts back what it
   showed). Module scope, so it keeps its identity across renders (Bug A). */
function MemberNumbersCell({ numbers, locked, onSave, label }: Readonly<{ numbers: readonly string[]; locked: boolean; onSave: Dispatch<string[]>; label: string }>) {
  const shown = numbersShown(numbers);
  const [typed, setTyped] = useState<string | null>(null);
  const mono: CSSProperties = { fontSize: FS.s11, fontFamily: "'JetBrains Mono',monospace" };
  if (locked) return <span style={{ ...mono, color: shown ? "var(--tp)" : "var(--tm)" }}>{shown || "—"}</span>;
  const leave = () => {
    if (typed === null) return;
    const next = parseNumbers(typed);
    setTyped(null);
    if (numbersShown(next) !== shown) onSave(next);
  };
  return (
    <input
      className="field-input"
      aria-label={label}
      data-member-numbers=""
      value={typed ?? shown}
      placeholder="—"
      onChange={(e) => {
        setTyped(e.target.value);
      }}
      onBlur={leave}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        else if (e.key === "Escape") setTyped(null);
      }}
      style={{ ...mono, width: 140, padding: "2px 5px", margin: 0 }}
    />
  );
}

/* LEGAL-LANE (round 2) — THE IMPORT MODAL NOW HAS A STABLE IDENTITY.
   It used to be `const ClImp = () => {...}` declared INSIDE CollectiveCaseView's
   render body and mounted as <ClImp />. That is the exact anti-pattern CLAUDE.md
   records as Bug A: a component declared in a render body is a NEW component
   TYPE on every render, so React unmounts and remounts it — losing every piece
   of its state — whenever its parent re-renders.
   The import is precisely the flow that triggers a parent render: commit() calls
   s(SK.cl, …), which is App's setClients, which re-renders App → CCV → a brand
   new ClImp type → unmount. `phase`, `prog` and `summary` were destroyed by the
   very writes they exist to report on, and the modal snapped back to its preview
   state with Cancel restored. That is the failed browser pass, exactly.
   Hoisted to module scope, the type is stable, the fiber survives, and the
   lifecycle added in fix 1 can actually be observed. Everything it used to close
   over arrives as props — no behaviour change beyond surviving a re-render. */
// Collapse a match list to one entry per distinct client, with a count:
// ["Alpha Aardvark", "Alpha Aardvark", …] → ["Alpha Aardvark ×5"]. The
// duplicate report exists to say "this person is already here"; repeating the
// same name five times says "here are five people", which is the opposite.
/* 207 §4 [RULED 2026-09-23, ruling 1] — THE `?` BESIDE A CELL AN IMPORT COULD
   NOT READ. The row was imported and that one cell left EMPTY (stored exactly as
   an empty cell stores); the file's own text rides the member's link as a mark
   (`importMarks`), which no calculation reads. The mark shows here until a
   value is set for the cell, a later import reads it cleanly, or somebody
   clicks this to say it was checked (some cells rightly stay empty). The hover
   gives the file's text. Clicking is a roster write, so it takes the list's
   lock like every other one; locked, the hover says so. A button, so the
   keyboard reaches it; it stops the click, because a round cell's own click
   opens the payment dialog. Module scope: a component declared in a render
   body remounts on every render (Bug A). */
function ImportMarkBadge({
  raw,
  locked,
  onCheck,
  t,
}: Readonly<{
  raw: string;
  locked: boolean;
  onCheck: () => void;
  t: Translate;
}>) {
  return (
    <button
      type="button"
      className="link-btn"
      aria-label={t("cc.retainer.mark_aria", { fallback: "Cell to check" })}
      title={
        locked
          ? t("cc.retainer.mark_tip_locked", {
              raw,
              fallback: 'The file read "{raw}" here — imported empty. Unlock the list to mark it checked.',
            })
          : t("cc.retainer.mark_tip", {
              raw,
              fallback: 'The file read "{raw}" here — imported empty. Click once it is checked.',
            })
      }
      onClick={(e) => {
        e.stopPropagation();
        if (!locked) onCheck();
      }}
      style={{
        marginLeft: 4,
        padding: "0 4px",
        fontSize: FS.s10,
        fontWeight: 700,
        lineHeight: 1.2,
        color: "var(--am)",
        border: "1px solid var(--am)",
        borderRadius: RAD.r6,
        background: "transparent",
        cursor: locked ? "help" : "pointer",
      }}
    >
      ?
    </button>
  );
}

function countedNames(matches: any) {
  const seen = new Map();
  for (const c of matches || []) {
    const name =
      c.companyName ||
      [c.firstName, c.lastName].filter(Boolean).join(" ") ||
      "#" + c.id;
    const key = c.id != null ? "id:" + c.id : "nm:" + name.toLowerCase();
    const prev = seen.get(key);
    if (prev) prev.n += 1;
    else seen.set(key, { name, n: 1 });
  }
  return [...seen.values()].map((e) => (e.n > 1 ? `${e.name} ×${e.n}` : e.name));
}

/* 164 §E (eslint ratchet) — the import's row model: `ImportedPaymentRow`,
   `ImportParsedRow`, `ImportLinkRow` live in src/lib/ccClientImport (207 §1.3). */
type ImportSummaryState = {
  mode: string;
  total: number;
  imported: number;
  created: number;
  overwritten: number;
  linkedExisting: number;
  // 207B §2 — whom « Linked existing » counts, row by row, and the rows whose
  // client was already on the case.
  linked?: { rn: number; clId: number; name: string }[];
  alreadyOnCase?: number;
  // 167 R14 — Add mode only: Id rows linked to their client under "new".
  idLinked?: number;
  preservedPayments: number;
  droppedLinks: number;
  skipped: { rn: number | string; name: string; reason: string }[];
  failed?: { rn: number | string; name: string; reason: string }[];
  // batchClientDuplicates rows — candidate/matches are client shapes the
  // render reads loosely; kept any at this one boundary.
  matches: any[];
  roundsCreated: { roundNumber: number; label: string; formula?: string }[];
  rosterFailed?: boolean;
  orphansArchived?: number;
  orphanFailures?: { rn: number | string; name: string; reason: string }[];
  retainer?: {
    applied: boolean;
    frozen: number;
    released: number;
    payments: number;
  } | null;
  orderMatched?: { name: string; n: number }[];
  // 207 §3 — each imported row and its client (the plan's), and, once the
  // writes answered, the check's inputs: every row imported or refused.
  rowClIds?: { rn: number; clId: number }[];
  rowCheck?: { total: number; imported: { rn: number; clId: number }[]; refused: number[] };
  // 207 §4 — imported with a cell left empty and marked: row, column, the file's text.
  cellsToCheck?: CellToCheck[];
};

function ClientImportModal({
  clImportFile,
  setClImportFile,
  cl,
  sCl,
  s,
  SK,
  links,
  rounds,
  cs,
  cc,
  ca,
  updCs,
  t,
  toast,
}: any) {
  const askConfirm = useConfirm();
  // The modal's `t` arrives untyped (its props are `any`); 207's own lines read
  // it typed — both branches now (207B §1's choice sits in the preview).
  const tt = t as Translate;
  // 05n §4 — see src/lib/importVocabulary.mjs.
  const _vTitle = useTaxonomy("person_title").entries;
  const _vCountry = useTaxonomy("country").entries;
  const _vClientType = useTaxonomy("client_type").entries;
  const [rows, setRows] = useState<any[]>([]);
  const [hdrs, setHdrs] = useState<any[]>([]);
  // LEGAL-LANE (fix 3) — the sheet's TRUE header names, positional.
  const [rawHdrs, setRawHdrs] = useState<any[]>([]);
  /* 164 §A.5(c) — the file's own round definitions, when it carries the
     export's "Rounds" sheet: {roundNumber → {label, formula, dueDate}}. */
  const [sheetRoundDefs, setSheetRoundDefs] = useState<any>(null);
  const [parsed, setParsed] = useState(false);
  const [error, setError] = useState<any>(null);
  const [mode, setMode] = useState("add");
  // 207B §1 — the default: a name the firm already knows goes on the case, the
  // client's record unchanged ("link"); the other three — "overwrite", "new",
  // "skip" — are chosen, in either mode.
  const [dupMode, setDupMode] = useState("link");
  // 207B §2 — « Linked existing » opens onto the names it linked.
  const [showLinked, setShowLinked] = useState(false);
  /* 164 §A.1 — retainer columns are applied only when the importer says so.
     Off by default: the "(info)" header marker no longer decides anything
     (RULINGS 2026-09-03). Unticked, the importer behaves byte-for-byte as it
     does for a file without retainer columns. */
  const [applyRetainer, setApplyRetainer] = useState(false);
  /* 164 §A.3 — doImport awaits the server compute before writing, so a
     double-click during that await must not start a second run. */
  const importBusy = useRef(false);
  const [summary, setSummary] = useState<ImportSummaryState | null>(null);
  /* LEGAL-LANE (fix 1) — the modal now has a LIFECYCLE. Before, the
     action button fired doImport() and the summary appeared in the
     same tick: the writes were still in flight, so "imported" was a
     client-side claim, not a fact. `phase` drives the button:
       preview → importing ("Importing… 7/15") → done ("… — Close").
     The counter is the SERVER's — persist()'s per-row callback ticks
     it as each POST is confirmed (App.tsx `opts.onRow`).
     NO CANCEL, deliberately. The loop is not row-sequential: the s()
     engine builds one op per row and fans them out through
     Promise.all, so by the time the button could be clicked every
     POST is already in flight and there is no "current row" to stop
     after. Cancel therefore disappears the moment the import starts
     — the honest state, rather than a button that would abandon the
     UI while the server kept writing. (Nothing is rolled back
     either way: the no-deletions ruling stands, and each client's
     SharePoint folder is provisioned inline by POST /api/clients.) */
  const [phase, setPhase] = useState("preview");
  const [prog, setProg] = useState<any>({ done: 0, total: 0 });
  // 214 addendum 4 §25 [RULED 63] — the window's plan and the run it waits for (« Tout importer » / « Importer sans vider »)
  const [lossAsk, setLossAsk] = useState<{ plan: LossPlan; run: Dispatch<boolean> } | null>(null);
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = async (ev2) => {
      try {
        const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
        const wb = XLSX.read((ev2.target as any).result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!json.length) {
          setError(
            t("cc.import.error_empty_file", {
              fallback: "Empty file",
            }),
          );
          return;
        }
        /* LEGAL-LANE (fix 3) — the RAW header row, read separately.
           sheet_to_json's object keys are de-duplicated ("Compta",
           "Compta_1", "Compta_2"), so the retainer file's repeated
           "Compta" headers survive only here. Index-aligned with
           Object.keys(json[0]), so `vals[i]` still addresses column
           i — what changes is that column i's NAME is now the one
           actually in the sheet. */
        const raw =
          XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" })[0] ||
          [];
        setRawHdrs((raw as any[]).map((x) => String(x ?? "")));
        /* 164 §A.5(c) — read the Rounds sheet if present. A DueDate cell may
           come back as an Excel serial or Date after a hand edit; render it
           ISO the way the app stores round dates. */
        const rws2 = wb.Sheets["Rounds"];
        if (rws2) {
          const rjson = XLSX.utils.sheet_to_json(rws2, {
            defval: "",
          }) as Record<string, unknown>[];
          const map: Record<
            number,
            { label: string; formula: string; dueDate: string }
          > = {};
          rjson.forEach((rr) => {
            const n2 = Number(rr.RoundNumber);
            if (!Number.isFinite(n2) || n2 <= 0) return;
            const rawDue = rr.DueDate;
            const dueIso =
              typeof rawDue === "number" || rawDue instanceof Date
                ? (parseDateCell(rawDue) || "").slice(0, 10)
                : String(rawDue || "").trim();
            map[n2] = {
              label: String(rr.Label || "").trim(),
              formula: String(rr.Formula ?? "").trim(),
              dueDate: dueIso,
            };
          });
          setSheetRoundDefs(Object.keys(map).length ? map : null);
        }
        setHdrs(Object.keys(json[0] as any));
        setRows(json);
        setParsed(true);
      } catch (err2: any) {
        setError("Failed: " + err2.message);
      }
    };
    reader.readAsArrayBuffer(clImportFile);
  }, []);
  /* LEGAL-LANE (fix 3) — the retainer shape is read at RENDER time,
     not only at import time, so the preview can state exactly what
     the import is about to create before anyone commits to it.
     doImport consumes these same values — one parse, one plan. */
  const hdrNames = rawHdrs.length ? rawHdrs : hdrs;
  // 164 §E — the parser and reconciler live in an untyped .mjs; their shapes
  // are asserted once here so every downstream read is typed.
  const { rounds: hdrRounds, warnings: hdrWarnings } = parseRetainerHeader(
    hdrNames,
  ) as {
    rounds: {
      roundNumber: number;
      label: string;
      dueCol: number | null;
      paidCol: number | null;
      comptaCol: number | null;
    }[];
    warnings: string[];
  };
  const { merged: mergedRounds, created: createdRounds } = reconcileRounds(
    rounds,
    hdrRounds,
    (n: number) => t("cc.rounds.label_n", { n, fallback: "Round " + n }),
    // 164 §A.5(c) — a round the case lacks takes the file's definition
    // instead of formula "0"; a round the case has is untouched.
    sheetRoundDefs,
  ) as {
    merged: { roundNumber: number; label: string; dueDate: string; formula: string }[];
    created: { roundNumber: number; label: string; dueDate: string; formula: string }[];
  };
  /* 164 §A.1 — creating rounds is a retainer effect too: box unticked, the
     case's round structure is untouched. */
  const roundsToWrite =
    applyRetainer && createdRounds.length ? mergedRounds : null;
  /* LEGAL-LANE (fix 1) — commit + CONFIRM. Both modes funnel here:
     the two writes go out, the per-row callback drives the counter,
     and the summary is shown only once the server has answered for
     every row. Per-row failures are collected and land in the
     summary's skipped table with their reason — a failure mid-run
     never stops the run and is never silent. */
  const commit = async (
    finalCls: unknown[],
    newLinks: ImportLinkRow[],
    summ: ImportSummaryState,
    newRounds?: unknown,
    orphanIds?: number[],
  ) => {
    setPhase("importing");
    setProg({ done: 0, total: 0 });
    /* Removing Cancel means "importing" MUST be an escapable state:
       an unexpected throw would otherwise strand the modal on a
       disabled progress button with no way out. Anything unhandled
       lands in the error view, which closes. */
    try {
      const failedRows: {
        rn: number | string;
        name: string;
        reason: string;
      }[] = [];
      const failedCreateIds = new Set<number>();
      // 207 §3 — a refused create or update is attributed to its ROW: the plan
      // knows which row each client id (pool or temporary) came from.
      const failedUpdateIds = new Set<number>();
      const rnByClId = new Map<number, number>(
        (summ.rowClIds ?? []).map((r) => [r.clId, r.rn]),
      );
      const idMap = new Map<number, number>();
      let lastCreated: { id?: number | string | null } | null = null;
      let fileRowFailures = 0;
      let done = 0;
      let total = 0;
      /* 164 §A.6(a) — MEASURED ORDERING. POST /api/clients DELETES the body
         id (`delete data.id`, clients.js:310), so the genId() guesses that
         newLinks carries for new members never persist — the old shape
         fired the roster PUT concurrently and pointed those links at ids
         that may not exist. So: (1) the client sync lands FIRST;
         onCreatedRow (the 111 §5b pattern, App.tsx:4878) hands back each
         create's server row and onRow fires synchronously right after it
         for the same create, which is what pairs the server id to the
         row's temp id. (2) THEN the roster + rounds go out in ONE write
         (unchanged — two writes would leave a window in which a member
         carries a due for a round the case does not have yet), links
         rewritten to real ids, links of failed creates dropped (they would
         point at nothing). (3) The archive step is LAST in every ordering. */
      await s(SK.cl, finalCls, sCl, {
        onStart: (n: number) => {
          total = n;
          setProg({ done, total });
        },
        onCreatedRow: (row: { id?: number | string | null } | null) => {
          lastCreated = row;
        },
        onRow: (r: {
          ok: boolean;
          op: string;
          id: number;
          item: unknown;
          error?: { message?: string } | null;
        }) => {
          done++;
          if (r.op === "create") {
            if (r.ok && lastCreated) {
              if (
                typeof lastCreated.id === "number" &&
                lastCreated.id !== r.id
              )
                idMap.set(r.id, lastCreated.id);
              lastCreated = null;
            } else if (!r.ok) {
              failedCreateIds.add(r.id);
            }
          }
          if (r.op === "update" && !r.ok) failedUpdateIds.add(r.id);
          if (!r.ok) {
            // 164 §A.6(b) — only failed creates/updates of the file's own
            // rows reduce the imported count (this path issues no deletes
            // any more; the guard keeps the counter honest if one returns).
            if (r.op !== "delete") fileRowFailures++;
            failedRows.push({
              rn: rnByClId.get(r.id) ?? "—",
              name: nameOf(r.item || {}),
              reason:
                "Server refused the " +
                r.op +
                ": " +
                ((r.error && r.error.message) || "unknown error"),
            });
          }
          setProg({ done, total });
        },
      });
      const fixedLinks = newLinks
        .filter((lk) => !failedCreateIds.has(lk.clId))
        .map((lk) =>
          idMap.has(lk.clId)
            ? { ...lk, clId: idMap.get(lk.clId) as number }
            : lk,
        );
      let ccFailed = 0;
      try {
        const ccOut = (await updCs({
          clientLinks: fixedLinks,
          ...(newRounds ? { rounds: newRounds } : {}),
        })) as { failed?: number } | null;
        ccFailed = (ccOut && ccOut.failed) || 0;
      } catch {
        ccFailed = 1;
      }
      /* 164 §A.6 — orphans are ARCHIVED, sequentially, after the roster is
         written (RULINGS 2026-09-03: archived, never hard-deleted; the
         roster first; outcomes never reduce the imported count). The
         returned row merges into `cl` exactly as doArchiveClient merges it.
         A failed roster write archives NOTHING: the roster is what unlinks
         these members, and archiving a still-linked member on a refused
         write would archive an active one. */
      const orphanFailures: any[] = [];
      let orphansArchived = 0;
      const wantArchive = orphanIds || [];
      const clById = cl as { id: number }[];
      if (wantArchive.length > 0 && ccFailed > 0) {
        for (const oid of wantArchive) {
          const c2 = clById.find((x) => x.id === oid);
          orphanFailures.push({
            rn: "—",
            name: c2 ? nameOf(c2) : "#" + oid,
            reason:
              "Not archived — the roster write itself failed, so this member is still linked.",
          });
        }
      } else if (wantArchive.length > 0) {
        total += wantArchive.length;
        setProg({ done, total });
        for (const oid of wantArchive) {
          try {
            const updated = (await archiveClient(
              oid,
            )) as Record<string, unknown>;
            sCl((prev: any) =>
              Array.isArray(prev)
                ? prev.map((c: any) =>
                    c.id === oid ? { ...c, ...updated } : c,
                  )
                : prev,
            );
            orphansArchived++;
          } catch (e) {
            const c2 = clById.find((x) => x.id === oid);
            orphanFailures.push({
              rn: "—",
              name: c2 ? nameOf(c2) : "#" + oid,
              reason:
                "Could not archive: " +
                ((e as { message?: string } | null)?.message ||
                  "unknown error"),
            });
          }
          done++;
          setProg({ done, total });
        }
      }
      if (ccFailed > 0) {
        failedRows.push({
          rn: "—",
          name: t("case.parties.clients"),
          reason:
            "The roster itself did not save — the clients above were created, but their link to this collective case was refused. Re-run the import.",
        });
      }
      // 207 §3 — the check's inputs: each imported row with the SERVER's id for
      // its client (a create's temporary id mapped through the answer), the rows
      // the server refused taken out of it and into the refused ones.
      const rowCheck = {
        total: summ.total,
        imported: (summ.rowClIds ?? [])
          .filter((r) => !failedCreateIds.has(r.clId) && !failedUpdateIds.has(r.clId))
          .map((r) => ({ rn: r.rn, clId: idMap.get(r.clId) ?? r.clId })),
        refused: [...summ.skipped, ...failedRows]
          .map((sk) => sk.rn)
          .filter((rn): rn is number => typeof rn === "number"),
      };
      setSummary({
        ...summ,
        rowCheck,
        // 164 §A.6(b) — reduced ONLY by failed creates/updates of the
        // file's own rows; never by orphan outcomes.
        imported: Math.max(0, summ.imported - fileRowFailures),
        rosterFailed: ccFailed > 0,
        /* "Created N retainer rounds" reports what THIS run wrote, not what the
           header happened to describe. Two ways it could otherwise lie: rounds
           that already existed (nothing was created), and a roster write the
           server refused — the rounds ride in that same PUT, so if it failed
           they do not exist. Both collapse to an empty list here. */
        roundsCreated: newRounds && ccFailed === 0 ? summ.roundsCreated || [] : [],
        orphansArchived,
        orphanFailures,
        failed: failedRows,
        skipped: [...summ.skipped, ...failedRows],
      });
      setPhase("done");
    } catch (e: any) {
      setPhase("done");
      setError(
        t("cc.import.error_during", {
          msg: e?.message || "unknown error",
          fallback:
            "The import stopped: {msg}. Clients already created were kept — nothing is deleted. Re-run the import; existing clients are skipped as duplicates.",
        }),
      );
    }
  };
  const doImport = async () => {
    if (importBusy.current) return;
    importBusy.current = true;
    try {
      await doImportInner();
    } finally {
      importBusy.current = false;
    }
  };
  const doImportInner = async () => {
    /* LEGAL-LANE (fix 3) — NAMES come from the raw header row,
       CELLS from the de-duplicated keys. The two are index-aligned,
       so `vals[i]` is still column i; what changes is that column
       i's name is now the one actually in the sheet rather than
       XLSX's "_1"-suffixed rewrite of it. */
    const h = hdrNames;
    // 164 §E — the one typed view of the client pool this importer needs.
    const clTyped = cl as { id: number }[];
    const idx = importColumns(h);
    /* LEGAL-LANE (fix 3) — the retainer triplets (parsed above,
       positionally, in src/lib/retainerImport.mjs) REPLACE the old
       name-keyed /R(\d+)_paid/ scan: that scan could not see a due
       column at all, and on this file it would have bound all three
       rounds to the first "Compta". */
    // 164 §A.1 — every retainer effect reads through these two, and both are
    // false unless the checkbox is ticked.
    const hasPaidCols =
      applyRetainer && hdrRounds.some((r: any) => r.paidCol != null);
    const hasMoneyCols = applyRetainer && hdrRounds.length > 0;
    const today = new Date().toISOString().slice(0, 10);
    const skipped: any[] = [];
    const parsedRows: ImportParsedRow[] = [];
  // 05n §4 — one resolver, one report, per import run. `cell()` ALWAYS returns
  // what should be written, so no call site has to remember which states mean
  // "write it anyway"; the report is what tells somebody it needs a human.
  const vocabReport = makeImportVocabularyReport();
  // A10 (brief 65) — src/lib/importCell.
  const cell = makeImportCell(vocabReport);

    // 207 §4 — the cells imported EMPTY because they could not be read.
    const cellsToCheck: CellToCheck[] = [];
    parsedRows.push(
      ...parseImportRows({
        rows,
        hdrs,
        idx,
        applyRetainer,
        hdrRounds,
        clTyped,
        cell,
        _vTitle,
        _vCountry,
        _vClientType, // 214 addendum 4 §25 — the Type cell through its vocabulary, never coerced
        today,
        skipped,
        hdrNames: h as string[],
        checks: cellsToCheck,
      }),
    );
    /* 164 §A.3 — FREEZE ONLY WHAT DIFFERS; EQUAL RELEASES (RULINGS
       2026-09-03). One server-compute call for the members-to-be — the same
       call downloadClTemplate makes — with the reconciled rounds and each
       row's quantum, payments: [] and NO dueByRound posted, so `due` comes
       from the round's FORMULA, which is the question. perRound is paired by
       roundNumber, never by index. Per due cell:
         · |cell − formula due| < 0.005 (the half-cent tolerance
           retainerRounds.js's paymentDue already uses) ⇒ the cell leaves
           dueByRound and the round is recorded in dueReleases — a matched
           member's existing stated due for that round is RELEASED at merge.
         · otherwise the cell stays in dueByRound and freezes.
       Rounds this import creates carry formula "0" (or the Rounds sheet's,
       §A.5), so for them every cell differing from that formula freezes —
       no special case needed.
       If the compute cannot be reached, the import REFUSES to run: deciding
       freeze-vs-release without the formula's value would be guessing at
       money. */
    /* 211 §18 [ADVISOR] — two rows of one member (one Id) stating two quantums:
       neither is chosen; the member's stays as it was, the cell marked and listed. */
    holdConflictingQuantums(parsedRows, cellsToCheck, idx.q >= 0 ? ((h as string[])[idx.q] ?? "Quantum") : "Quantum");
    if (
      hasMoneyCols &&
      parsedRows.some((pr) => Object.keys(pr.dueByRound).length > 0)
    ) {
      /* 211 §18 — the test compares each stated due with the formula at the
         quantum each member READS ONCE THE IMPORT IS WRITTEN — its case's own,
         else its client's — read off a dry plan of the same rows (planImport is
         pure). It used the FILE's quantum: in « link » mode the case then applied
         the formula to the client's stored one, and a released due silently
         differed from the file. */
      const dryPlan = planImport({
        mode,
        dupMode,
        parsedRows,
        skipped: [],
        cl,
        links,
        cc,
        ca,
        cs,
        hasMoneyCols,
        hasPaidCols,
        genId: tempId,
        readCells: importReadCells(idx, hdrRounds, applyRetainer),
      });
      const readQ = quantumsAfterPlan(dryPlan);
      /* 214 addendum 2 §20 [RULED 60; ADVISOR] — AND THE DATE THE FORMULA IS GATED BY, off the same dry plan:
         `dueFor` generates no due for a round dated after the member's `terminatedDate`, and the export writes
         THAT figure (a 0), so the comparison must ask the formula with everything `dueFor` reads — the very figure
         the summary shows. Without it a terminated member's post-termination 0 differed from the ungated formula
         and came back as a stated 0, blue (the reproduction, docs/214-deploy-note.md §8′). `dueByRound` stays
         out: the formula is the question. */
      const readT = terminatedDatesAfterPlan(dryPlan);
      let cmp: {
        perMember?: {
          perRound?: { roundNumber: number | null; due: number }[];
        }[];
      } | null = null;
      try {
        cmp = (await computeRetainerRounds({
          rounds: mergedRounds,
          members: parsedRows.map((pr) => {
            const term = readT.get(pr.rn);
            return {
              clId: pr.rn,
              quantum: readQ.has(pr.rn) ? readQ.get(pr.rn) : pr.data.quantum,
              payments: [],
              ...(term ? { terminatedDate: term } : {}), // 214 addendum 2 §20 — posted as the summary posts it
            };
          }),
        })) as {
          perMember?: {
            perRound?: { roundNumber: number | null; due: number }[];
          }[];
        };
      } catch (e: any) {
        setError(
          t("cc.import.error_compute", {
            msg: e?.message || "unknown error",
            fallback:
              "Could not compute the rounds' formula values ({msg}) — nothing was imported. Try again.",
          }),
        );
        return;
      }
      applyFormulaReleases(parsedRows, cmp);
    }
    // 05n §4 — cells that named no entry ride the SUMMARY this modal already
    // has, not a toast: a toast behind a summary panel is a message nobody
    // reads. `skipped` is what feeds `summ.skipped`, and these rows were NOT
    // skipped — they were imported with the cell kept as written, which the
    // reason line says.
    const _vocabMsg = vocabReport.summary(t);
    if (_vocabMsg) skipped.push({ rn: "—", name: t("common.type", { fallback: "Type" }), reason: _vocabMsg });
    // 164 §A.5(b) — same names pair in order, never collapse: see `planImport`.
    // 214 addendum 4 §25 — ONE plan maker: the window's « Importer sans vider » re-plans the same rows less their
    // empty cells, from the refusals as they stood before the first plan (`skipped` is truncated to that count).
    const nSkippedBeforePlan = skipped.length;
    const planWith = (rows: ImportParsedRow[]) => planImport({
      mode,
      dupMode,
      parsedRows: rows,
      skipped,
      cl,
      links,
      cc,
      ca,
      cs,
      hasMoneyCols,
      hasPaidCols,
      genId: tempId,
      readCells: importReadCells(idx, hdrRounds, applyRetainer),
    });
    const plan = planWith(parsedRows);
    /* 214 addendum 4 §25 [RULED 63 — Laurent, 04/10] — WHAT THIS RUN WOULD LOSE, before anything is written: a
       Replace run's unlinked members — every member the sheet does not name, with his payments and stated dues —
       and the stored fields a record merge would empty (« overwrite », or every match of a Replace run; the
       money is silent-safe since 164 §A.1). The window waits; « Importer sans vider » re-plans the same rows with
       their empty cells left out, so a merge leaves those fields; « Annuler » writes nothing. */
    const proceed = (plan: ReturnType<typeof planImport>, skippedList: typeof skipped) => {
    const {
      newCls,
      newLinks,
      imported,
      created,
      overwritten,
      linkedExisting,
      idLinked,
      preservedPayments,
      duesFrozen,
      duesReleased,
      paymentsWritten,
      orderMatched,
      rowClIds,
      alreadyOnCase,
      linked,
      onExisting,
    } = plan;
    // Phase 43 — import COI/dedup at scale: batch-match imported rows
    // against the existing client pool (name + email, accent+space
    // normalized) for the completion summary — no per-row modal loop.
    // 167 R14 — a row that carries an Id IS that client (164 §A.5(a)); it is
    // never a row that "may match" one, so it stays out of this list.
    // 207B §2 — nor is a row the plan put on an existing client (a member, or
    // a client of the firm): the report is what was NOT put on one — skipped,
    // or created — though a client with that name and e-mail exists.
    const onExistingRns = new Set(onExisting);
    const clientMatches = batchClientDuplicates(
      parsedRows.filter((pr) => pr.sheetId == null && !onExistingRns.has(pr.rn)).map((pr) => pr.data),
      cl,
    );
    if (mode === "add") {
      commit(
        newCls,
        newLinks,
        {
          mode,
          total: rows.length,
          imported,
          created,
          overwritten,
          linkedExisting,
          linked,
          alreadyOnCase,
          idLinked,
          preservedPayments: 0,
          droppedLinks: 0,
          skipped: skippedList,
          matches: clientMatches,
          roundsCreated: createdRounds,
          // 164 §A.1 — which the summary states: columns ignored (box off)
          // or applied, with what applying did.
          retainer:
            hdrRounds.length === 0
              ? null
              : {
                  applied: applyRetainer,
                  frozen: duesFrozen,
                  released: duesReleased,
                  payments: paymentsWritten,
                },
          // 164 §A.5(b) — every key that paired by order, with its count.
          orderMatched: [...orderMatched.entries()].map(
            ([name, n2]) => ({ name, n: n2 }),
          ),
          rowClIds,
          cellsToCheck,
        },
        roundsToWrite,
      );
      return;
    } else {
      // replace mode — 207 §1.3: the plan is `planImport` (src/lib/ccClientImport).
      const { dropped, trueOrphans } = plan;
      // 157 §2 — THE ONE SITE THAT IS NOT A YES/NO GATE, and the reason the
      // shared confirm grew an `onNo`. Declining here is an ACTION: OK
      // ARCHIVES the now-unlinked client records (164 §A.6 — the engine
      // issues no DELETE: every row stays in finalCls, so a row present on
      // both sides yields an update at most, measured at App.tsx:4825-4844),
      // Cancel KEEPS them as unlinked clients, and BOTH then commit the
      // import. A plain gate whose Cancel merely dismisses would have
      // thrown the whole import away.
      const finish = (archiveOrphans: boolean) => {
        commit(
          newCls,
          newLinks,
          {
            mode,
            total: rows.length,
            imported,
            created,
            overwritten,
            linkedExisting,
            linked,
            alreadyOnCase,
            preservedPayments,
            droppedLinks: dropped,
            skipped: skippedList,
            matches: clientMatches,
            roundsCreated: createdRounds,
            // 164 §A.1 — see the add-mode summ.
            retainer:
              hdrRounds.length === 0
                ? null
                : {
                    applied: applyRetainer,
                    frozen: duesFrozen,
                    released: duesReleased,
                    payments: paymentsWritten,
                  },
            // 164 §A.5(b) — see the add-mode summ.
            orderMatched: [...orderMatched.entries()].map(
              ([name, n2]) => ({ name, n: n2 }),
            ),
            rowClIds,
            cellsToCheck,
          },
          roundsToWrite,
          // 164 §A.6 — the archive list; commit() archives AFTER the roster.
          archiveOrphans ? trueOrphans : [],
        );
      };
      if (trueOrphans.length === 0) {
        finish(false);
        return;
      }
      const names =
        trueOrphans
          .slice(0, 10)
          .map((id: any) => {
            const c2 = cl.find((x: any) => x.id === id);
            return c2 ? nameOf(c2) : "#" + id;
          })
          .join(", ") +
        (trueOrphans.length > 10
          ? ` (+${trueOrphans.length - 10} more)`
          : "");
      const plural = trueOrphans.length === 1 ? "" : "s";
      askConfirm({
        // 161C §3 — the two-outcome dialog (157 §2's `onNo`): declining is
        // itself an action, so the title states the question the OK button
        // answers and the two buttons carry the rest.
        // 164 §A.6(a) — the verb changes: ARCHIVE, never delete (RULINGS
        // 2026-09-03). New keys — the seed only inserts, so changed text
        // takes a new key; the old ones are censused and dropped.
        title: t("cc.orphans.archive_title", {
          fallback: "Archive the members not in the file?",
        }),
        message: t("cc.orphans.confirm_archive", {
          count: trueOrphans.length,
          names,
          fallback: `${trueOrphans.length} client${plural} will no longer be linked to any case:\n\n${names}\n\nOK  = Archive the record${plural} (reversible)\nCancel = Keep as unlinked client${plural}`,
        }),
        confirmLabel: t("cc.orphans.archive_records", {
          fallback: "Archive them",
        }),
        cancelLabel: t("cc.orphans.keep_unlinked", {
          fallback: "Keep as unlinked clients",
        }),
        onConfirm: () => finish(true),
        onNo: () => finish(false),
      });
      return;
    }
    };
    const losses = newLossPlan();
    const hdrOf = (i: number, name: string): string => (i >= 0 ? (h as string[])[i] ?? name : name);
    const RECORD_FIELDS: DiffField[] = [
      ["type", hdrOf(idx.tp, "Type")], ["title", hdrOf(idx.tt, "Title")], ["firstName", hdrOf(idx.fn, "FirstName")], ["lastName", hdrOf(idx.ln, "LastName")],
      ["companyName", hdrOf(idx.cn, "CompanyName")], ["email", hdrOf(idx.em, "Email")], ["phone", hdrOf(idx.ph, "Phone")], ["street", hdrOf(idx.st, "Street")],
      ["postalCode", hdrOf(idx.pc, "PostalCode")], ["city", hdrOf(idx.ct, "City")], ["country", hdrOf(idx.co, "Country")], ["vat", hdrOf(idx.vt, "VAT")],
      ["nationalRegisterId", hdrOf(idx.nr, "National Register")], ["lang", hdrOf(idx.lg, "Language")], ["formerCaseRef", hdrOf(idx.cs, "Case")],
    ];
    const clientName = (row: unknown): string => String(nameOf(row));
    const memberName = (clId: number): string => { const c2 = clTyped.find((x) => x.id === clId); return c2 ? clientName(c2) : `#${String(clId)}`; };
    // a member's payments and stated dues, the words the roster already uses
    const linkDetail = (lk: unknown): string => {
      const o = lk as { payments?: unknown; dueByRound?: unknown };
      const pays = Array.isArray(o.payments) ? o.payments.length : 0;
      const dues = typeof o.dueByRound === "object" && o.dueByRound !== null ? Object.entries(o.dueByRound as Record<string, unknown>) : [];
      return [
        `${tt("cc.payments.title", { fallback: "Payments" })} : ${String(pays)}`,
        ...dues.map(([n, a]) => `${tt("cc.payments.due", { fallback: "Due" })} R${n} : ${String(a)}`),
      ].join(" · ");
    };
    if (mode === "replace") addUnlinkedMembers(losses, links, linkIds(plan.newLinks), memberName, linkDetail);
    addRecordLosses(losses, cl, plan.newCls, RECORD_FIELDS, clientName);
    if (!hasLoss(losses)) {
      proceed(plan, skipped);
      return;
    }
    setLossAsk({
      plan: losses,
      run: (keep) => {
        setLossAsk(null);
        if (!keep) {
          proceed(plan, skipped);
          return;
        }
        const kept = parsedRows.map((pr) => ({ ...pr, data: withoutEmptyCells(pr.data, ["quantum"]) as ImportParsedRow["data"] }));
        skipped.length = nSkippedBeforePlan;
        proceed(planWith(kept), skipped);
      },
    });
  };
  const importing = phase === "importing";
  if (error)
    return (
      <ImportErrorModal error={error} setCaImportFile={setClImportFile} t={t} />
    );
  if (!parsed)
    return (
      <ImportLoadingModal t={t} />
    );
  if (lossAsk)
    return (
      <ImportLossDialog
        plan={lossAsk.plan}
        t={tt}
        groupsSentence={tt("import.loss.clients", withCount(lossAsk.plan.groups.length, "count", { fallback: `${String(lossAsk.plan.groups.length)} clients` }))}
        onAll={() => { lossAsk.run(false); }}
        onKeep={hasEmptied(lossAsk.plan) ? () => { lossAsk.run(true); } : undefined}
        onCancel={() => { setLossAsk(null); }}
      />
    );
  if (summary) {
    const s2 = summary;
    /* 207 §3 — the summary's counts are ROWS. The skipped table also carries
       lines that are not rows (the vocabulary note, a roster refusal — `rn`
       "—"), and counting them made « 260 of 260 imported, 1 skipped » add up
       to 261. `refusedN` counts the file's rows the import did not import; the
       table still shows the notes. */
    const refusedN = s2.skipped.filter((sk: { rn: number | string }) => typeof sk.rn === "number").length;
    // 207 §3 — the check, on EVERY render, against the LIVE list: `links` is
    // the case's roster and `cl` the page's clients, the two the members table
    // joins. It converges as the writes land; a row it names is a row the
    // summary counted and the list does not show.
    const chk = s2.rowCheck
      ? importOutcomeCheck({ ...s2.rowCheck, links: links as { clId: number }[], clients: cl as { id: number }[] })
      : null;
    const chkBad = chk ? chk.notOnCase.length + chk.unaccounted.length + chk.twice.length : 0;
    const allOk = refusedN === 0 && chkBad === 0;
    /* LEGAL-LANE (fix 1) — the action button IS the completion
       state: "Imported N clients — Close", and it closes. The
       duplicate count is called out separately from other skips
       because it is the one the operator re-runs against. */
    const dupSkipped = (s2.skipped || []).filter((sk: any) =>
      /duplicate/i.test(String(sk.reason || "")),
    ).length;
    // 207B §2 — the number after its label, as the summary's own lines read
    // (« Créés : {n} », « Aangemaakt: {n} »): no plural in brackets.
    const doneLabel =
      tt("cc.import.done_imported", { n: s2.imported, fallback: "Imported: {n}" }) +
      (dupSkipped > 0
        ? " · " + tt("cc.import.done_dups_skipped", { n: dupSkipped, fallback: "Skipped as duplicates: {n}" })
        : "") +
      " — " +
      t("common.close", { fallback: "Close" });
    return (
      <ModalLayout
        title={t("modal.import.summary_title", {
          fallback: "Import Summary",
        })}
        onClose={() => setClImportFile(null)}
        showX={false}
        closeOnOverlay
        maxWidth={640}
        footer={
          <div className="modal-footer">
            <button
              type="button"
              className="modal-save"
              onClick={() => {
                toast(
                  t("toast.imported_n_of_m_clients", withCount(s2.imported, "n", { m: s2.total,
                    fallback: "Imported {n} of {m} clients",
                  })),
                );
                setClImportFile(null);
              }}
            >
              {doneLabel}
            </button>
          </div>
        }
      >
            {allOk ? (
              <p
                style={{
                  fontSize: FS.s12,
                  color: "var(--gn)",
                  fontWeight: 600,
                  marginBottom: 10,
                }}
              >
                ✓{" "}
                {t("cc.import.all_imported", {
                  count: s2.total,
                  fallback:
                    "All " +
                    s2.total +
                    " client" +
                    (s2.total !== 1 ? "s" : "") +
                    " imported successfully.",
                })}
              </p>
            ) : (
              <p style={{ fontSize: FS.s12, marginBottom: 10 }}>
                {/* 209 §7 [RULED 16] — one sentence per number, each in its language's plural */}
                {[
                  tt("cc.import.partial_imported", { count: s2.imported, total: s2.total, fallback: "{count} of {total} imported" }),
                  tt("cc.import.partial_skipped", { count: refusedN, fallback: "{count} skipped" }),
                ].join(", ") + "."}
              </p>
            )}
            {/* 207 §3 — by construction: every row imported or refused, and
                every imported row on the case's list; any that is not is NAMED. */}
            {chk && chkBad === 0 && (
              <p style={{ fontSize: FS.s10_5, color: "var(--gn)", marginBottom: 8 }}>
                {/* 209 §7 [RULED 16] — one sentence per number, in the sentence's frame */}
                {tt("cc.import.check_ok_frame", {
                  parts: [
                    tt("cc.import.check_imported", { count: s2.rowCheck ? s2.rowCheck.imported.length : 0, fallback: "{count} imported" }),
                    tt("cc.import.check_refused", { count: refusedN, fallback: "{count} refused" }),
                    tt("cc.import.check_total", { count: s2.total, fallback: "{count} rows in the file" }),
                  ].join(", "),
                  fallback: "Checked: every imported row is on the case's list — {parts}.",
                })}
              </p>
            )}
            {chk && chkBad > 0 && (
              <div
                role="alert"
                style={{ fontSize: FS.s10_5, color: "var(--rd)", marginBottom: 10, lineHeight: 1.6 }}
              >
                <strong>
                  {tt("cc.import.check_failed_title", {
                    fallback: "The summary and the case's list disagree:",
                  })}
                </strong>
                {chk.notOnCase.map((r) => (
                  <div key={"n" + String(r.rn)}>
                    {tt("cc.import.check_not_on_case", {
                      rn: r.rn,
                      why:
                        r.why === "roster"
                          ? tt("cc.import.check_why_roster", {
                              fallback: "the case's roster does not link its client",
                            })
                          : tt("cc.import.check_why_client", {
                              fallback: "its client is not in this page's client list",
                            }),
                      fallback: "Row {rn}: counted as imported, but not on the case's list ({why}).",
                    })}
                  </div>
                ))}
                {chk.unaccounted.map((rn) => (
                  <div key={"u" + String(rn)}>
                    {tt("cc.import.check_unaccounted", {
                      rn,
                      fallback: "Row {rn}: neither imported nor refused.",
                    })}
                  </div>
                ))}
                {chk.twice.map((rn) => (
                  <div key={"t" + String(rn)}>
                    {tt("cc.import.check_twice", {
                      rn,
                      fallback: "Row {rn}: counted twice.",
                    })}
                  </div>
                ))}
              </div>
            )}
            <div
              style={{
                fontSize: FS.s10_5,
                color: "var(--tm)",
                marginBottom: 10,
                lineHeight: 1.7,
              }}
            >
              {t("cc.import.summary_mode", { fallback: "Mode" })}:{" "}
              <strong>
                {s2.mode === "replace"
                  ? t("cc.import.mode_replace", {
                      fallback: "Replace",
                    })
                  : t("cc.import.mode_add", { fallback: "Add" })}
              </strong>
              {" · "}
              {t("cc.import.summary_created", {
                n: s2.created,
                fallback: "Created: " + s2.created,
              })}
              {s2.overwritten > 0 &&
                " · " +
                  t("cc.import.summary_overwritten", {
                    n: s2.overwritten,
                    fallback: "Overwritten: " + s2.overwritten,
                  })}
              {/* 207B §2 — « Linked existing » opens onto the names it linked,
                  so a namesake who is not the same person is seen at once. */}
              {s2.linkedExisting > 0 && (
                <>
                  {" · "}
                  <button
                    type="button"
                    aria-expanded={showLinked}
                    onClick={() => setShowLinked((v) => !v)}
                    style={{
                      display: "inline",
                      padding: 0,
                      font: "inherit",
                      color: "var(--ac)",
                      background: "transparent",
                      border: 0,
                      cursor: "pointer",
                    }}
                  >
                    {tt("cc.import.summary_linked", {
                      n: s2.linkedExisting,
                      fallback: "Linked existing: {n}",
                    })}{" "}
                    {showLinked ? "▾" : "▸"}
                  </button>
                </>
              )}
              {(s2.alreadyOnCase ?? 0) > 0 &&
                " · " +
                  tt("cc.import.summary_already_on_case", {
                    n: s2.alreadyOnCase,
                    fallback: "Already on the case: {n}",
                  })}
              {/* 167 R14 — the summary names how many Id rows were linked
                  rather than re-created. */}
              {(s2.idLinked || 0) > 0 &&
                " · " +
                  t("cc.import.summary_id_linked", {
                    n: s2.idLinked,
                    fallback: "Linked by Id: " + s2.idLinked,
                  })}
              {/* LEGAL-LANE (fix 3) — what the import did to the
                  case's own retainer structure, not just its
                  members. */}
              {(s2.roundsCreated || []).length > 0 && (
                <>
                  <br />
                  <span style={{ color: "var(--gn)" }}>
                    {/* 164 §A.5(c) — the list is DATA (labels + the formula
                        each round was created with), built outside the
                        translation so fr/nl readers see it too. */}
                    {t("cc.import.summary_rounds_created", {
                      n: s2.roundsCreated.length,
                      count: s2.roundsCreated.length, // 208 §20
                      fallback:
                        "Created " +
                        s2.roundsCreated.length +
                        " retainer round" +
                        (s2.roundsCreated.length !== 1 ? "s" : ""),
                    })}
                    {": " +
                      s2.roundsCreated
                        .map(
                          (r: any) =>
                            "R" +
                            r.roundNumber +
                            " " +
                            r.label +
                            " (" +
                            (r.formula || "0") +
                            ")",
                        )
                        .join(", ")}
                  </span>
                  {/* 208 §2 [ADVISOR] — created without a formula: say what that leaves at 0, and where the formula is set */}
                  {s2.roundsCreated.some((r) => formulaIsBlank(r.formula)) && (
                    <>
                      <br />
                      <span style={{ color: "var(--am)" }}>
                        {(t as Translate)("cc.import.summary_rounds_no_formula", {
                          list: s2.roundsCreated
                            .filter((r) => formulaIsBlank(r.formula))
                            .map((r) => "R" + String(r.roundNumber))
                            .join(", "),
                          section: (t as Translate)("case.retainer_rounds", { fallback: "Retainer Fee Rounds" }),
                          fallback: "No formula for {list}: the amounts not in the file stay 0 until a formula is set in « {section} ».",
                        })}
                      </span>
                    </>
                  )}
                </>
              )}
              {/* 164 §A.5(b) — every same-name key that paired by order. */}
              {(s2.orderMatched || []).length > 0 && (
                <>
                  <br />
                  {t("cc.import.summary_matched_by_order", {
                    list: (s2.orderMatched || [])
                      .map((m2) => m2.name + " ×" + m2.n)
                      .join(", "),
                    fallback:
                      "Matched by order (same name more than once): " +
                      (s2.orderMatched || [])
                        .map((m2) => m2.name + " ×" + m2.n)
                        .join(", "),
                  })}
                </>
              )}
              {/* 164 §A.1 — the summary says which: columns present but
                  ignored (box off), or applied with what applying did. */}
              {s2.retainer && !s2.retainer.applied && (
                <>
                  <br />
                  <span style={{ color: "var(--am)" }}>
                    {t("cc.import.summary_retainer_ignored", {
                      fallback:
                        "Retainer columns were present in the file but ignored (the apply box was off).",
                    })}
                  </span>
                </>
              )}
              {s2.retainer && s2.retainer.applied && (
                <>
                  <br />
                  {/* 209 §7 [RULED 16] — one sentence per number, in the sentence's frame */}
                  {tt("cc.import.retainer_applied_frame", {
                    parts: [
                      tt("cc.import.retainer_frozen", { count: s2.retainer.frozen, fallback: "{count} dues frozen" }),
                      tt("cc.import.retainer_released", { count: s2.retainer.released, fallback: "{count} released" }),
                      tt("cc.import.retainer_payments", { count: s2.retainer.payments, fallback: "{count} payments written" }),
                    ].join(" · "),
                    fallback: "Retainer columns applied: {parts}.",
                  })}
                </>
              )}
              {s2.mode === "replace" && (
                <>
                  <br />
                  {t("cc.import.summary_preserved", withCount(s2.preservedPayments, "n", { fallback:
                      "Preserved payment history for " +
                      s2.preservedPayments +
                      " matched client" +
                      (s2.preservedPayments !== 1 ? "s" : "") +
                      ".",
                  }))}{" "}
                  {t("cc.import.summary_dropped", withCount(s2.droppedLinks, "n", { fallback:
                      "Dropped " +
                      s2.droppedLinks +
                      " unmatched link" +
                      (s2.droppedLinks !== 1 ? "s" : "") +
                      ".",
                  }))}
                  {/* 164 §A.6(b) — orphan outcomes get their own line and
                      never touch the imported count. */}
                  {((s2.orphansArchived || 0) > 0 ||
                    (s2.orphanFailures || []).length > 0) &&
                    " " +
                      // 208 §20 [RULED 16] — one sentence per number, each in its language's plural, joined here
                      [
                        t("cc.import.orphans_archived_n", { count: s2.orphansArchived ?? 0, fallback: `${String(s2.orphansArchived ?? 0)} archived` }),
                        (s2.orphanFailures || []).length > 0
                          ? t("cc.import.orphans_not_archived_n", { count: (s2.orphanFailures || []).length, fallback: `${String((s2.orphanFailures || []).length)} could not be archived` })
                          : null,
                      ].filter(Boolean).join(" · ")}
                </>
              )}
            </div>
            {/* 207B §2 — the clients « Linked existing » counts, row by row: each
                was already a client of the firm and is now on this case. Taking a
                namesake off is the members list's own act, behind this summary. */}
            {showLinked && (s2.linked ?? []).length > 0 && (
              <div
                className="table-wrap"
                data-cc-linked-list=""
                style={{ maxHeight: 200, overflowY: "auto", marginBottom: 10 }}
              >
                <Table style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={{ fontSize: FS.s9 }}>{tt("cc.import.col_row", { fallback: "Row" })}</th>
                      <th style={{ fontSize: FS.s9 }}>{tt("common.name", { fallback: "Name" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(s2.linked ?? []).map((l) => (
                      <tr key={l.rn}>
                        <td className="mono" style={{ fontSize: FS.s10 }}>{l.rn}</td>
                        <td style={{ fontSize: FS.s10 }}>{l.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
            {refusedN > 0 && (
              <>
                <div
                  style={{
                    fontSize: FS.s10,
                    fontWeight: 600,
                    color: "var(--am)",
                    marginBottom: 4,
                    textTransform: "uppercase",
                    letterSpacing: ".4px",
                  }}
                >
                  {t("cc.import.skipped_rows", {
                    n: refusedN,
                    fallback:
                      "Skipped rows (" + refusedN + ")",
                  })}
                </div>
                <div
                  className="table-wrap"
                  style={{ maxHeight: 240, overflowY: "auto" }}
                >
                  <Table style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ fontSize: FS.s9 }}>
                          {t("cc.import.col_row", {
                            fallback: "Row",
                          })}
                        </th>
                        <th style={{ fontSize: FS.s9 }}>
                          {t("common.name", { fallback: "Name" })}
                        </th>
                        <th style={{ fontSize: FS.s9 }}>
                          {t("cc.import.col_reason", {
                            fallback: "Reason",
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {s2.skipped.filter((sk: { rn: number | string }) => typeof sk.rn === "number").map((sk: any, i: any) => (
                        <tr key={i}>
                          <td
                            className="mono"
                            style={{ fontSize: FS.s10 }}
                          >
                            {sk.rn}
                          </td>
                          <td style={{ fontSize: FS.s10 }}>
                            {sk.name}
                          </td>
                          <td
                            style={{
                              fontSize: FS.s10,
                              color: "var(--am)",
                            }}
                          >
                            {sk.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
            {/* 207 §4 [RULED 2026-09-23] — the rows imported with a cell left
                EMPTY because it could not be read: imported, not refused, each
                with its row, its column and the file's own text. */}
            {(s2.cellsToCheck ?? []).length > 0 && (
              <>
                <div
                  style={{
                    fontSize: FS.s10,
                    fontWeight: 600,
                    color: "var(--am)",
                    margin: "8px 0 4px",
                    textTransform: "uppercase",
                    letterSpacing: ".4px",
                  }}
                >
                  {tt("cc.import.cells_to_check", {
                    n: (s2.cellsToCheck ?? []).length,
                    fallback: "Imported with a cell to check ({n})",
                  })}
                </div>
                <div style={{ fontSize: FS.s10, color: "var(--tm)", marginBottom: 4 }}>
                  {tt("cc.import.cells_to_check_note", {
                    fallback:
                      "Each was imported empty; the list shows a ? in that cell until it is checked.",
                  })}
                </div>
                <div className="table-wrap" style={{ maxHeight: 200, overflowY: "auto" }}>
                  <Table style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ fontSize: FS.s9 }}>{tt("cc.import.col_row", { fallback: "Row" })}</th>
                        <th style={{ fontSize: FS.s9 }}>{tt("common.name", { fallback: "Name" })}</th>
                        <th style={{ fontSize: FS.s9 }}>{tt("cc.import.col_column", { fallback: "Column" })}</th>
                        <th style={{ fontSize: FS.s9 }}>{tt("cc.import.col_in_file", { fallback: "In the file" })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(s2.cellsToCheck ?? []).map((c) => (
                        <tr key={String(c.rn) + "·" + c.column}>
                          <td className="mono" style={{ fontSize: FS.s10 }}>{c.rn}</td>
                          <td style={{ fontSize: FS.s10 }}>{c.name}</td>
                          <td style={{ fontSize: FS.s10 }}>{c.column}</td>
                          <td className="mono" style={{ fontSize: FS.s10, color: "var(--am)" }}>{c.raw}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
            {/* 207 §3 — the lines that are NOT rows of the file (the vocabulary
                note, a roster refusal) under their own heading, so the skipped
                table counts rows and nothing else. */}
            {s2.skipped.some((sk: { rn: number | string }) => typeof sk.rn !== "number") && (
              <div style={{ fontSize: FS.s10, color: "var(--am)", margin: "8px 0", lineHeight: 1.6 }}>
                <div style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: ".4px", marginBottom: 2 }}>
                  {tt("common.notes", { fallback: "Notes" })}
                </div>
                {s2.skipped
                  .filter((sk: { rn: number | string }) => typeof sk.rn !== "number")
                  .map((sk: { name: string; reason: string }, i: number) => (
                    <div key={i}>
                      {sk.name} — {sk.reason}
                    </div>
                  ))}
              </div>
            )}
            {/* 164 §A.6(b) — archive refusals under their OWN heading,
                never inside "skipped rows": these are not ignored rows of
                the file, they are members the archive step could not
                archive. */}
            {(s2.orphanFailures || []).length > 0 && (
              <>
                <div
                  style={{
                    fontSize: FS.s10,
                    fontWeight: 600,
                    color: "var(--am)",
                    margin: "10px 0 4px",
                    textTransform: "uppercase",
                    letterSpacing: ".4px",
                  }}
                >
                  {t("cc.import.orphan_failures", {
                    n: (s2.orphanFailures || []).length,
                    fallback:
                      "Could not archive (" +
                      (s2.orphanFailures || []).length +
                      ")",
                  })}
                </div>
                <div
                  className="table-wrap"
                  style={{ maxHeight: 200, overflowY: "auto" }}
                >
                  <Table style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ fontSize: FS.s9 }}>
                          {t("common.name", { fallback: "Name" })}
                        </th>
                        <th style={{ fontSize: FS.s9 }}>
                          {t("cc.import.col_reason", {
                            fallback: "Reason",
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(s2.orphanFailures || []).map((sk, i) => (
                        <tr key={i}>
                          <td style={{ fontSize: FS.s10 }}>
                            {sk.name}
                          </td>
                          <td
                            style={{
                              fontSize: FS.s10,
                              color: "var(--am)",
                            }}
                          >
                            {sk.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
            {/* Phase 43 — import COI/dedup at scale: reviewable list
                of imported rows that match an existing client. */}
            {s2.matches && s2.matches.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: FS.s10,
                    fontWeight: 600,
                    color: "var(--am)",
                    margin: "10px 0 4px",
                    textTransform: "uppercase",
                    letterSpacing: ".4px",
                  }}
                >
                  {/* 207B §2 — what the report lists since the default links: the
                      rows NOT put on an existing client (skipped, or created). */}
                  {tt("cc.import.matches_not_linked", {
                    n: s2.matches.length,
                    fallback:
                      "Rows not put on an existing client, though one has the same name and e-mail ({n})",
                  })}
                </div>
                <div
                  className="table-wrap"
                  style={{ maxHeight: 200, overflowY: "auto" }}
                >
                  <Table style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ fontSize: FS.s9 }}>
                          {tt("cc.import.col_in_file", { fallback: "In the file" })}
                        </th>
                        <th style={{ fontSize: FS.s9 }}>
                          {t("cc.import.col_existing", {
                            fallback: "Existing client",
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {s2.matches.map((m: any, i: any) => (
                        <tr key={i}>
                          <td style={{ fontSize: FS.s10 }}>
                            {m.candidate.companyName ||
                              [
                                m.candidate.firstName,
                                m.candidate.lastName,
                              ]
                                .filter(Boolean)
                                .join(" ") ||
                              "—"}
                          </td>
                          <td style={{ fontSize: FS.s10 }}>
                            {/* One name per distinct client, with a count.
                                Five identical rows read as five different
                                people otherwise — the opposite of what a
                                duplicate report is for. */}
                            {countedNames(m.matches)
                              .join(", ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
      </ModalLayout>
    );
  }
  return (
    <ModalLayout
      title={t("modal.import.clients_title", {
        fallback: "Import Clients",
      })}
      onClose={
        importing ? undefined : () => setClImportFile(null)
      }
      showX={false}
      closeOnOverlay={!importing}
      maxWidth={600}
      footer={
        <div className="modal-footer">
          {/* LEGAL-LANE (fix 1) — Cancel is gone the moment the
              import starts. The s() engine dispatches every row at
              once, so there is no "current row" to stop after and
              nothing honest for a Cancel to do; leaving it would
              only abandon the UI while the server kept writing. */}
          {!importing && (
            <button
              type="button"
              className="modal-cancel"
              onClick={() => setClImportFile(null)}
            >
              {t("common.cancel", { fallback: "Cancel" })}
            </button>
          )}
          <button
            type="button"
            className="modal-save"
            onClick={doImport}
            disabled={importing}
            aria-busy={importing || undefined}
          >
            {importing
              ? t("cc.import.progress", {
                  done: prog.done,
                  total: prog.total,
                  fallback:
                    "Importing… " + prog.done + "/" + prog.total,
                })
              : t("modal.import.confirm_n_clients", {
                  count: rows.length,
                  fallback:
                    "✓ Import " +
                    rows.length +
                    " client" +
                    (rows.length !== 1 ? "s" : ""),
                })}
          </button>
        </div>
      }
    >
          <p style={{ fontSize: FS.s11, marginBottom: 10 }}>
            {t("cc.import.found_rows", {
              n: rows.length,
              count: rows.length, // 208 §20
              fallback:
                "Found " +
                rows.length +
                " row" +
                (rows.length !== 1 ? "s" : "") +
                " in the file.",
            })}
          </p>
          {/* LEGAL-LANE (fix 3) — the retainer plan, stated BEFORE
              the import runs: which rounds were recognised, which
              will be created, and anything the header parser could
              not account for. Nothing about money is silent. */}
          {hdrRounds.length > 0 && (
            <div
              style={{
                fontSize: FS.s10_5,
                border: "1px solid var(--br)",
                borderRadius: RAD.r6,
                padding: "7px 9px",
                marginBottom: 12,
                lineHeight: 1.7,
              }}
            >
              <div
                style={{
                  fontSize: FS.s10,
                  fontWeight: 600,
                  color: "var(--tm)",
                  textTransform: "uppercase",
                  letterSpacing: ".4px",
                  marginBottom: 3,
                }}
              >
                {t("cc.import.retainer_title", {
                  fallback: "Retainer rounds in this file",
                })}
              </div>
              <div>
                {hdrRounds
                  .map(
                    (r: any) =>
                      "R" +
                      r.roundNumber +
                      (r.label ? " (" + r.label + ")" : ""),
                  )
                  .join(" · ")}
              </div>
              {/* 164 §A.1 — these two lines describe what applying WILL do;
                  box unticked they would be lies, so they render only when
                  it is ticked (the checkbox note says what "off" means). */}
              {applyRetainer && (
                <div
                  style={{
                    color:
                      createdRounds.length > 0
                        ? "var(--gn)"
                        : "var(--tm)",
                    fontWeight: createdRounds.length > 0 ? 600 : 400,
                  }}
                >
                  {createdRounds.length > 0
                    ? t("cc.import.rounds_will_create", {
                        n: createdRounds.length,
                        count: createdRounds.length, // 208 §20
                        // 209 §8 — every form names the rounds it will create, as this fallback always did
                        rounds: createdRounds.map((r: { roundNumber?: unknown; label?: unknown }) => "R" + String(r.roundNumber) + " " + String(r.label)).join(", "),
                        fallback:
                          createdRounds.length +
                          " new retainer round" +
                          (createdRounds.length !== 1 ? "s" : "") +
                          " will be created: " +
                          createdRounds
                            .map(
                              (r: any) => "R" + r.roundNumber + " " + r.label,
                            )
                            .join(", "),
                      })
                    : t("cc.import.rounds_all_exist", {
                        fallback:
                          "All of these rounds already exist on this case — none will be created or changed.",
                      })}
                </div>
              )}
              {applyRetainer && (
                <div style={{ color: "var(--tm)" }}>
                  {t("cc.import.retainer_writes", {
                    fallback:
                      "Per member: due amount, paid amount and the Compta reference (kept as the payment's reference).",
                  })}
                </div>
              )}
              {hdrWarnings.length > 0 && (
                <ul
                  style={{
                    margin: "5px 0 0",
                    paddingLeft: 16,
                    color: "var(--am)",
                  }}
                >
                  {hdrWarnings.map((w: any, wi: any) => (
                    <li key={wi}>{w}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: FS.s10,
                fontWeight: 600,
                color: "var(--tm)",
                marginBottom: 6,
              }}
            >
              {t("cc.import.mode_label", {
                fallback: "Import mode",
              })}
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: FS.s11,
                cursor: "pointer",
                marginBottom: 4,
              }}
            >
              <input
                type="radio"
                checked={mode === "add"}
                onChange={() => setMode("add")}
              />
              {t("cc.import.add_to_existing", {
                fallback: "Add to existing clients",
              })}
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: FS.s11,
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                checked={mode === "replace"}
                onChange={() => setMode("replace")}
              />
              <span>
                {t("cc.import.replace_all", {
                  fallback: "Replace all clients for this case",
                })}{" "}
                <span style={{ color: "var(--rd)", fontSize: FS.s10 }}>
                  {/* 164 §A.1 — the old note claimed "preserves payment
                      history"; the truthful note states what Replace does
                      to members not in the file (§A.6: archived). */}
                  {t("cc.import.replace_note_archive", {
                    fallback:
                      "(members not in the file are archived)",
                  })}
                </span>
              </span>
            </label>
          </div>
          {/* 164 §A.1 — the checkbox that decides whether the file's retainer
              columns are applied at all. Off by default; rendered only when
              the header carries rounds. */}
          {hdrRounds.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: FS.s11,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={applyRetainer}
                  onChange={(e) =>
                    setApplyRetainer(e.target.checked)
                  }
                />
                {t("cc.import.apply_retainer", {
                  n: hdrRounds.length,
                  count: hdrRounds.length, // 208 §20
                  fallback:
                    "Apply the retainer columns (R1…Rn due/paid) to the members — " +
                    hdrRounds.length +
                    " round" +
                    (hdrRounds.length !== 1 ? "s" : "") +
                    " in this file",
                })}
              </label>
              <div
                style={{
                  fontSize: FS.s10,
                  color: "var(--tm)",
                  marginLeft: 22,
                  marginTop: 2,
                }}
              >
                {t("cc.import.apply_retainer_note", {
                  fallback:
                    "Off: the file's retainer columns are ignored — no dues, payments or retainer rounds are written.",
                })}
              </div>
            </div>
          )}
          {/* 207B §1 — the choice shows in BOTH modes (in Replace it decides a
              row that matches no member of this case but a client of the
              firm), four options, the default first, each saying what happens
              to the CASE. */}
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: FS.s10,
                fontWeight: 600,
                color: "var(--tm)",
                marginBottom: 6,
              }}
            >
              {tt("cc.import.dup_label", {
                fallback:
                  "When a client with the same name already exists",
              })}
            </div>
            {[
              {
                v: "link",
                words: tt("cc.import.dup_opt_link", {
                  fallback: "Put the existing client on this case — their record unchanged",
                }),
              },
              {
                v: "overwrite",
                words: tt("cc.import.dup_opt_update", {
                  fallback: "Update the existing client's record from the file and put them on this case",
                }),
              },
              {
                v: "new",
                words: tt("cc.import.dup_opt_create", {
                  fallback: "Create a new client anyway and put the new client on this case",
                }),
              },
              {
                v: "skip",
                words: tt("cc.import.dup_opt_skip", {
                  fallback: "Skip the row — no client created, nobody put on this case (listed in the summary)",
                }),
              },
            ].map((o) => (
              <label
                key={o.v}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: FS.s11,
                  cursor: "pointer",
                  marginBottom: 4,
                }}
              >
                <input
                  type="radio"
                  name="cc-import-dup"
                  value={o.v}
                  checked={dupMode === o.v}
                  onChange={() => setDupMode(o.v)}
                />
                {o.words}
              </label>
            ))}
          </div>
          <div
            style={{
              fontSize: FS.s10,
              fontWeight: 600,
              color: "var(--tm)",
              marginBottom: 4,
            }}
          >
            {t("cc.import.preview", withCount(Math.min(5, rows.length), "n", { fallback:
                "Preview (first " +
                Math.min(5, rows.length) +
                " rows)",
            }))}
          </div>
          <div
            className="table-wrap"
            style={{ maxHeight: 200, overflowY: "auto" }}
          >
            <Table style={{ width: "100%" }}>
              <thead>
                <tr>
                  {hdrs.slice(0, 7).map((h2) => (
                    <th
                      key={h2}
                      style={{
                        fontSize: FS.s8,
                        padding: "3px 4px",
                        color: "var(--tm)",
                      }}
                    >
                      {h2}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 5).map((row, i) => (
                  <tr key={i}>
                    {hdrs.slice(0, 7).map((h2) => (
                      <td
                        key={h2}
                        style={{
                          fontSize: FS.s9,
                          padding: "2px 4px",
                          maxWidth: 100,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {String(row[h2] || "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
    </ModalLayout>
  );
}

// BUG-A SWEEP — hoisted out of CollectiveCaseView's render body. It was
// declared there and mounted as <CcMS …/> at eight call sites, so every CCV
// render gave React a new component type for each party multi-select. That is
// not cosmetic here: MultiSelect owns open/closed and search state, and a
// remount slammed every open party picker shut and cleared what had been typed
// into it — while the user was typing, on any render the case happened to do.
// The eight names it closed over arrive in one `ctx` bag rather than as eight
// props: these call sites sit eight levels deep in JSX and threading eight
// identifiers through each would bury the two that actually vary.
/* 190 round 9 [AT] — the collective view's collapse defaults: the shared
   object (C80, brief 84) plus its three own sections, declared ONCE so the
   mount and the per-matter reset cannot disagree. */
const CC_SEC_COL_DEFAULTS = { ...SEC_COL_DEFAULTS, ccClients: true, ccRetainer: true, docTracking: true } as const;
const CcMS = ({ field, label2, list2, jt, ctx }: any) => {
  const { MultiSelect } = ctx;
  const objMode = jt !== undefined;
  const sel = objMode
    ? ctx.ccIds[field] || []
    : ctx.cs[field] || [];
  const rmap = objMode ? ctx.ccRoles[field] || {} : undefined;
  /* 214 §2–§4 — the counsel chips' parties and state and a client's own counsel's tag come from the ctx's
     builders; the chips' order puts the suspended ones last. Read through ONE typed alias (this file's ESLint
     count may not rise). And the WRITER the census found (docs/214-deploy-note.md §7.2 (b)): an add or a role
     change rebuilt every element as `{ id, roles }` and would have DROPPED the link's parties and state — the
     stored element is carried (`ccElementOf`) and only `id` and `roles` are set over it. */
  const ctxT = ctx as { cs?: Record<string, unknown>; ccLinks: LinkMap<CounselLink>; counselExtra?: IdToNode; counselMuted?: IdToBool; contactExtra?: IdToNode };
  const fieldKey = String(field);
  const stored = ctxT.cs?.[fieldKey];
  const counselSlot = fieldKey === "ocuIds" ? ctxT.counselExtra : undefined;
  const contactSlot = fieldKey === "cpIds" ? ctxT.contactExtra : undefined;
  const chipExtra = counselSlot ?? contactSlot;
  const chipMuted = fieldKey === "ocuIds" ? ctxT.counselMuted : undefined;
  // 216 §4 [RULED 71] — the chips in the one order, resolved against the group's pool (`list2`); the acting counsel
  // still first (ruling 52), each half in that order. The STORED order is not touched: the writer below carries it.
  const byName = orderIdsByParty(sel as readonly unknown[], list2 as readonly unknown[]);
  const shown: readonly unknown[] = fieldKey === "ocuIds" ? actingFirst(byName as readonly number[], ctxT.ccLinks) : byName;
  const onCh = objMode
    ? (ids: any, nm: any) =>
        ctx.updCs({
          [field]: ids.map((id: any) => ({
            ...(ccElementOf(stored, Number(id)) ?? {}),
            id,
            roles: (nm && nm[id]) || rmap[id] || [],
          })),
        })
    : (v: any) => ctx.updCs({ [field]: v });
  /* BRIEF 108 §2b-3 — every chip in the pane removes the same way: no
     confirm, effective at once, 5-second undo (label: null skips the hook's
     dialog; the hook's single-slot rule finalizes a first delete when a
     second lands inside the window). The snapshot is the STORED column value
     verbatim — objMode's [{id, roles}] included — so undo restores the roles
     with the id, and the restore writes through updCs, the same server path
     the removal took. */
  const onRemove = (id: any, name: any) => {
    const stored = ctx.cs[field] || [];
    const next = objMode
      ? stored.filter(
          (e: any) => (e && typeof e === "object" ? e.id : e) !== id,
        )
      : stored.filter((x: any) => x !== id);
    ctx.confirmAndUndo({
      label: null,
      toastName: ctx.t("parties.removed_toast", {
        name,
        fallback: `Removed ${name}`,
      }),
      onConfirm: () => ctx.updCs({ [field]: next }),
      undo: {
        snapshot: stored,
        restore: (snap: any) => ctx.updCs({ [field]: snap }),
      },
    });
  };
  return (
    <MultiSelect
      colKey={field}
      l={label2}
      opts={list2}
      sel={shown}
      chipExtra={chipExtra}
      chipMuted={chipMuted}
      onChange={onCh}
      onRemove={onRemove}
      locked={ctx.partiesLocked}
      isOpen={ctx.poGet(field, sel.length > 0)}
      onToggle={() => ctx.poSet(field)}
      onViewItem={ctx.viewPartyCc}
      onNew={() => {
        ctx.sQm({
          type: field.replace(/Ids$/, ""),
          retKey: field,
          ccId: ctx.cs.id,
        });
        ctx.sQf({});
      }}
      titleField={
        field === "cpIds" || field === "ocIds"
          ? "function"
          : undefined
      }
      {...(objMode
        ? {
            roleEntries: ctx.partyRoleEntries,
            roleMap: rmap,
            junctionType: jt,
          }
        : {})}
    />
  );
};

// BUG-A SWEEP — the collective-case LIST importer, hoisted to module scope.
// It was `const CcII = () => {…}` inside CollectiveCaseView's render body,
// mounted as <CcII />: a new component type on every CCV render, so React
// unmounted and remounted it and its parse/preview state — rows, headers,
// mode, error — was destroyed by anything that re-rendered the view.
// This is the same defect, in the same file, as the roster importer fixed in
// PR #209 (ClientImportModal). Four importers carried it; this is one of them.
// AUTHORITY (155 §4): sibling of CaseImportModal (CaseView.tsx — the family
// note lives there); a fix to either lands in both.
function CollectiveCaseImportModal({ ccImportFile, setCcImportFile, cc, sCc, s, SK, t, toast, pools }: any) {
// 05n §4 — see src/lib/importVocabulary.mjs.
const _vCaseType = useTaxonomy("case_type").entries;
const _vCaseStatus = useTaxonomy("case_status").entries;
// 209 §19 — the languages the dictionary carries: the collective case form's choices
const { available } = useTranslation() as { available: string[] };
const [rows, setRows] = useState<any[]>([]);
const [hdrs, setHdrs] = useState<any[]>([]);
const [parsed, setParsed] = useState(false);
const [error, setError] = useState<any>(null);
const [mode, setMode] = useState("add");
// 214 addendum 4 §25 [RULED 63] — the window's plan and the run it waits for (« Tout importer » / « Importer sans vider »)
const [lossAsk, setLossAsk] = useState<{ plan: LossPlan; run: Dispatch<boolean> } | null>(null);
useState(() => {
  const reader = new FileReader();
  reader.onload = async (ev) => {
    try {
      const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
      const wb = XLSX.read((ev.target as any).result, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!json.length) {
        setError(
          t("cc.import.error_empty_file", {
            fallback: "Empty file",
          }),
        );
        return;
      }
      setHdrs(Object.keys(json[0] as any));
      setRows(json);
      setParsed(true);
    } catch (err: any) {
      setError(
        t("cc.import.error_failed", {
          msg: err.message,
          fallback: "Failed: " + err.message,
        }),
      );
    }
  };
  reader.readAsArrayBuffer(ccImportFile);
});
const doImport = () => {
  const h = hdrs;
  const refIdx = findC(h, "Ref", "ref");
  const idIdx = findC(h, "Id", "id"); // 214 addendum 5 §29 [RULED 65] — the collective case's Id, the export's first column
  const ttIdx = findC(h, "Title", "title");
  const stIdx = findC(h, "Status", "status");
  const tpIdx = findC(h, "Type", "type", "ctype");
  const odIdx = findC(h, "OpenDate", "openDate");
  const deIdx = findC(h, "Description", "desc");
  const juIdx = findC(h, "JurisdictionId", "juId");
  // 214 addendum 3 §21 [ruling 62 (b)] — every partner: the plural header the export writes now, the singular one
  // an older sheet carries (one id), parsed as the other id cells are.
  const rpIdx = findC(
    h,
    "ResponsiblePartnerIds",
    "ResponsiblePartnerId",
    "responsiblePartners",
    "responsiblePartner",
  );
  const rhIdx = findC(
    h,
    "ReferenceHourlyRate",
    "referenceHourlyRate",
  );
  const crIdx = findC(h, "CostsRatio", "costsRatio");
  const ciIdx = findC(h, "ClaimAmount", "claimAmount");
  const sfIdx = findC(h, "SuccessFeeRate", "successFeeRate");
  const clinksIdx = findC(h, "ClientLinks", "clientLinks");
  const cliIdx = findC(h, "ClientIds", "clIds"); // 214 addendum 3 §21 — the export's members cell, never read before
  const opiIdx = findC(h, "OpponentIds", "opIds");
  const ocuiIdx = findC(h, "OpponentLawyerIds", "ocuIds");
  const exiIdx = findC(h, "ExpertIds", "exIds");
  const cpiIdx = findC(h, "ClientContactIds", "cpIds");
  const ociIdx = findC(h, "OpponentContactIds", "ocIds");
  const meiIdx = findC(h, "MediatorIds", "meIds");
  const ariIdx = findC(h, "ArbitratorIds", "arIds");
  const op2iIdx = findC(h, "Op2Ids", "op2Ids");
  const cmtIdx = findC(h, "CommitteeIds", "committeeIds");
  const colIdx = findC(h, "CaseCollaborators", "caseCollaborators");
  const stgIdx = findC(h, "Stages", "stages");
  const docIdx = findC(h, "Docs", "docs");
  // 209 §19 [ADVISOR] — the collective case's language, under its header in any of the
  // three languages; a code or the form's own name for it (caseLanguage.ts). A row
  // without one creates it « à choisir », and never clears a stored one on an update.
  const lgIdx = findC(h, "Language", "Langue", "Taal");
  const pn = (v: any) => {
    if (v === "" || v == null) return null;
    const n = parseFloat(String(v).replace(",", "."));
    return isNaN(n) ? null : n;
  };
  const pIds = (v: any) =>
    String(v || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => +x)
      .filter((n) => !isNaN(n));
  const pJson = (v: any) => {
    if (!v) return [];
    try {
      return JSON.parse(v);
    } catch {
      return [];
    }
  };
  let imported = 0,
    updated = 0,
    skipped = 0;
  // 214 addendum 5 §29 [ADVISOR — the clients sheet's rule] — the rows refused (no Ref, an Id naming no collective case
  // of the list): counted with the skipped ones, each with its reason in the summary
  const refused: RefusedRow[] = [];
  // 05n §4 — one resolver, one report, per import run. `cell()` ALWAYS returns
  // what should be written, so no call site has to remember which states mean
  // "write it anyway"; the report is what tells somebody it needs a human.
  const vocabReport = makeImportVocabularyReport();
  // A10 (brief 65) — src/lib/importCell.
  const cell = makeImportCell(vocabReport);
  let working = mode === "replace" ? [] : [...cc];
  /* 214 addendum 4 §25 [RULED 63 — Laurent, 04/10] — WHAT THIS IMPORT WOULD LOSE, before anything is written: the
     window's plan (a value emptied, a party taken out of a list, a collective case archived) and, beside `working`,
     the rows « Importer sans vider » would write. « Replace all » archives every stored collective case and creates
     the sheet's rows anew: each is listed. The members are NEVER listed: the cell adds and never removes (§26). */
  const losses = newLossPlan();
  const workingKeep: unknown[] = mode === "replace" ? [] : (cc as unknown[]).slice();
  if (mode === "replace") addRowsTaken(losses, cc, "archived", matterLabel, matterTitle);
  const names = poolNamer(pools, pnameUtil);
  const hdr = (i: number, name: string): string => (i >= 0 ? String(h[i]) : name);
  const LIST_FIELDS: DiffField[] = [
    ["title", hdr(ttIdx, "Title")], ["ctype", hdr(tpIdx, "Type")], ["openDate", hdr(odIdx, "OpenDate")], ["desc", hdr(deIdx, "Description")],
    ["juId", hdr(juIdx, "JurisdictionId")], ["responsiblePartners", hdr(rpIdx, "ResponsiblePartnerIds")], ["referenceHourlyRate", hdr(rhIdx, "ReferenceHourlyRate")],
    ["costsRatio", hdr(crIdx, "CostsRatio")], ["claimAmount", hdr(ciIdx, "ClaimAmount")], ["successFeeRate", hdr(sfIdx, "SuccessFeeRate")],
    ["opIds", hdr(opiIdx, "OpponentIds")], ["ocuIds", hdr(ocuiIdx, "OpponentLawyerIds")], ["exIds", hdr(exiIdx, "ExpertIds")],
    ["cpIds", hdr(cpiIdx, "ClientContactIds")], ["ocIds", hdr(ociIdx, "OpponentContactIds")], ["meIds", hdr(meiIdx, "MediatorIds")], ["arIds", hdr(ariIdx, "ArbitratorIds")],
    ["op2Ids", hdr(op2iIdx, "Op2Ids")], ["committeeIds", hdr(cmtIdx, "CommitteeIds")], ["caseCollaborators", hdr(colIdx, "CaseCollaborators")], ["stages", hdr(stgIdx, "Stages")], ["docs", hdr(docIdx, "Docs")],
  ];
  const languageOf = (cellValue: unknown): { language?: string } => {
    const language = importedCaseLanguage(cellValue, available);
    return language ? { language } : {};
  };
  let rn = 1;
  for (const row of rows) {
    rn++;
    const vals = h.map((k) => row[k]);
    const ref = String(vals[refIdx] || "").trim();
    const title = String(vals[ttIdx] || "").trim();
    /* 214 addendum 5 §29 [RULED 65 — Laurent, 05/10] — WHERE THE ROW LANDS, asked ONCE and read by the merges
       (`prevCc`), the window and the write alike: by its Ref (its Id not read); else by its Id; else a new collective
       case. « Replace all » and a sheet without the Id column read no Id. A row without a Ref whose Id names no
       collective case of the list is refused. Until this the merges and the write each asked by Ref alone. */
    const match = sheetRowMatch(working, ref, mode === "replace" || idIdx < 0 ? undefined : vals[idIdx]);
    if (match.by === "refused") {
      refused.push({ rn, id: match.id });
      skipped++;
      continue;
    }
    if (match.by === "new" && !ref && !title) {
      skipped++;
      continue;
    }
    /* 214 addendum 2 §18 [ADVISOR — 213B's merge rule] — THE SHEET NAMES COUNSEL IDS; THE ELEMENTS ARE THE CASE'S.
       A collective case's `ocuIds` are elements ({ id, roles, partyIds, status, suspendedDate }); the sheet's cell
       holds ids. A named id keeps its stored element verbatim, a dropped one leaves, a new one enters acting with
       no party (`mergeCcElements`). Until this the ids REPLACED the elements: an exported cell (« [object Object] »,
       the export joined the elements) parsed to no id and the PUT EMPTIED the list; typed ids the server refused
       (400 partyRole.badShape) and the row rolled back. */
    const prevCc = match.by === "new" ? undefined : (working[match.at] as { ocuIds?: unknown; opIds?: unknown; op2Ids?: unknown; clientLinks?: unknown }); // 214 addendum 5 §29 — the one answer
    /* 214 addendum 3 §21 [ruling 62 (b) — a write that EMPTIED] — THE MEMBERS. The export writes the members' ids
       (`ClientIds`) and has written no `ClientLinks` JSON column since LEGAL-LANE fix 2; this import read ONLY that
       column, so every re-imported export wrote `clientLinks: []` and the PUT deleted every member with his
       payments, stated dues, notes and dates (measured on the shadow — docs/214-deploy-note.md §7.21). Now: the
       legacy JSON column where a sheet still carries one; else the `ClientIds` cell MERGED into the stored elements
       (a named member verbatim, a dropped one leaves, a new one as the add window makes him — `mergeCcMembers`);
       else — a sheet naming neither — the roster as stored (164 §A.1's preserve-on-silence: a file with no member
       column must never blank a claimant's money) [ADVISOR]. */
    const membersOf = (): { clientLinks?: unknown[] } => {
      /* 214 addendum 4 §26 [RULED 64 — Laurent, 04/10] — THE MEMBERS CELL AND THE LEGACY COLUMN ADD THE MEMBERS THEY
         NAME THAT THE CASE LACKS, AND NEVER REMOVE ONE: a stored case keeps every member verbatim (`ccMembersAdded`;
         a member leaves a collective case only from the case itself); a new row takes the cell's members fresh and
         the legacy column's elements as they are. Until this (§21's merge) a member the cell did not name left —
         an emptied cell took all of them: staging's 305 on 04/10. */
      if (clinksIdx >= 0) {
        const elems = pJson(vals[clinksIdx]) as unknown[];
        return { clientLinks: prevCc ? ccMembersAdded(prevCc.clientLinks, elems.map(entryId).filter((n): n is number => n !== null)) : elems };
      }
      if (cliIdx >= 0) return { clientLinks: ccMembersAdded(prevCc?.clientLinks, pIds(vals[cliIdx])) };
      return {};
    };
    const full = {
      ref,
      title,
      status: cell("case_status", _vCaseStatus, vals[stIdx], rn) || "active",
      ctype: cell("case_type", _vCaseType, vals[tpIdx], rn),
      openDate: String(vals[odIdx] || "").trim(),
      desc: String(vals[deIdx] || "").trim(),
      juId: vals[juIdx] ? +vals[juIdx] || null : null,
      responsiblePartners: pIds(vals[rpIdx]), // 214 addendum 3 §21 — every partner the cell names
      referenceHourlyRate: pn(vals[rhIdx]),
      costsRatio: pn(vals[crIdx]),
      claimAmount: pn(vals[ciIdx]),
      successFeeRate: pn(vals[sfIdx]),
      ...membersOf(),
      // 214 addendum 3 §21 — the three element-shaped party columns through ONE merge (213B's rule, §18's helper): a
      // named party keeps its element verbatim (roles; a counsel's parties and state), a dropped one leaves, a new
      // one enters with no role (`freshPartyElement`) — a new counsel acting with no party (the helper's default).
      opIds: mergeCcElements(prevCc?.opIds, pIds(vals[opiIdx]), freshPartyElement),
      ocuIds: mergeCcElements(prevCc?.ocuIds, pIds(vals[ocuiIdx])), // 214 addendum 2 §18
      exIds: pIds(vals[exiIdx]),
      cpIds: pIds(vals[cpiIdx]),
      ocIds: pIds(vals[ociIdx]),
      meIds: pIds(vals[meiIdx]),
      arIds: pIds(vals[ariIdx]),
      op2Ids: mergeCcElements(prevCc?.op2Ids, pIds(vals[op2iIdx]), freshPartyElement),
      committeeIds: pIds(vals[cmtIdx]),
      caseCollaborators: pJson(vals[colIdx]),
      stages: pJson(vals[stgIdx]),
      docs: pJson(vals[docIdx]),
      ...languageOf(vals[lgIdx]),
    };
    const eIdx = match.by === "new" ? -1 : match.at; // 214 addendum 5 §29 — the one answer (was: by Ref alone, a second time)
    /* 214 addendum 3 §24 [RULING 62 (b) — Laurent, 04/10] — A COLUMN THE SHEET DOES NOT HAVE LEAVES THE STORED VALUE
       AS IT IS; an empty cell in a column the sheet has still clears it (the clients sheet's rule, 164 §A.1 — the
       members above and the language already followed it). Until this every column the sheet lacked was written as
       its default over the stored row (docs/214-deploy-note.md §7.24). A new row keeps the defaults. */
    // 214 addendum 4 §25 [ADVISOR] — an EMPTY Status cell leaves an existing collective case's status (a status is
    // never emptied; « active » stays a new row's default): for a stored row the column counts as absent.
    const statusStated = String(vals[stIdx] ?? "").trim() !== "";
    const data = eIdx >= 0
      ? withoutAbsentColumns(full, [
          // 214 addendum 5 §29 — a row found by its Id never writes its empty Ref over the stored one: the Ref is the
          // key, the Id stood in for it (an empty cell would write "" over a NULL ref — docs/214-deploy-note.md §7.29)
          ["ref", match.by === "id" ? -1 : refIdx],
          ["title", ttIdx], ["status", statusStated ? stIdx : -1], ["ctype", tpIdx], ["openDate", odIdx], ["desc", deIdx], ["juId", juIdx],
          ["responsiblePartners", rpIdx], ["referenceHourlyRate", rhIdx], ["costsRatio", crIdx], ["claimAmount", ciIdx],
          ["successFeeRate", sfIdx], ["opIds", opiIdx], ["ocuIds", ocuiIdx], ["exIds", exiIdx], ["cpIds", cpiIdx],
          ["ocIds", ociIdx], ["meIds", meiIdx], ["arIds", ariIdx], ["op2Ids", op2iIdx], ["committeeIds", cmtIdx],
          ["caseCollaborators", colIdx], ["stages", stgIdx], ["docs", docIdx],
        ])
      : full;
    if (eIdx >= 0) {
      const stored: unknown = working[eIdx];
      // 214 addendum 4 §25 — the row's losses under its ref, and the row « sans vider » would write (addendum 5 §29: a
      // row found by its Id, under its collective case's ref, else its title)
      const gKey = match.by === "id" ? `#${String(match.id)}` : ref;
      const gLabel = match.by === "id" ? matterLabel(stored) : ref;
      for (const line of rowLosses(stored, data, LIST_FIELDS, names)) addLoss(losses, gKey, gLabel, line);
      working[eIdx] = { ...working[eIdx], ...data };
      workingKeep[eIdx] = { ...(stored as object), ...withoutEmptying(data, stored) };
      updated++;
    } else {
      const row = { id: tempId(), ...data };
      working.push(row);
      workingKeep.push(row);
      imported++;
    }
  }
  // 214 addendum 5 §29 — each refused row's reason, in the summary's own toast (one toast at a time — 212 [METHOD] (a))
  const refusedText = refusedReasons(t as Translate, refused);
  if (!imported && !updated) {
    toast(
      t("toast.no_valid_rows", { fallback: "⚠ No valid rows" }) +
        " (" +
        t("cc.import.n_skipped", withCount(skipped, "n", { fallback: skipped + " skipped",
        })) +
        ")" +
        refusedText,
    );
    setCcImportFile(null);
    return;
  }
  /* 214 addendum 4 §25 [RULED 63] — THE WINDOW when something would be lost, the import at once when nothing would. */
  const commitPlan = (rows: unknown[]) => {
  s(SK.cc, rows, sCc);
  const _vocabMsg = vocabReport.summary(t);
  if (_vocabMsg) toast("\u26a0 " + _vocabMsg);
  toast(
    t("cc.import.toast_done", {
      imported: imported,
      updated: updated,
      skip:
        skipped > 0
          ? ", " +
            t("cc.import.n_skipped", withCount(skipped, "n", { fallback: skipped + " skipped",
            }))
          : "",
      fallback: `Imported ${imported}, updated ${updated}${skipped > 0 ? ", skipped " + skipped : ""}`,
    }) + refusedText,
    refused.length > 0 ? "warning" : undefined, // 214 addendum 5 §29 [ADVISOR] — a refused row: the warning colour
  );
  setCcImportFile(null);
  };
  if (hasLoss(losses)) {
    setLossAsk({ plan: losses, run: (keep) => { setLossAsk(null); commitPlan(keep ? workingKeep : working); } });
    return;
  }
  commitPlan(working);
};
if (error)
  return (
    <ImportErrorModal error={error} setCaImportFile={setCcImportFile} t={t} />
  );
if (!parsed)
  return (
    <ImportLoadingModal t={t} />
  );
if (lossAsk)
  return (
    <ImportLossDialog
      plan={lossAsk.plan}
      t={t as Translate}
      groupsSentence={(t as Translate)("import.loss.matters", withCount(lossAsk.plan.groups.length, "count", { fallback: `${String(lossAsk.plan.groups.length)} matters` }))}
      onAll={() => { lossAsk.run(false); }}
      onKeep={hasEmptied(lossAsk.plan) ? () => { lossAsk.run(true); } : undefined}
      onCancel={() => { setLossAsk(null); }}
    />
  );
return (
  <ModalLayout
    title={t("cc.import.cc_title", {
      fallback: "Import Collective Cases",
    })}
    onClose={() => setCcImportFile(null)}
    onSave={doImport}
    saveLabel={t("cc.import.cc_import_n", withCount(rows.length, "n", { fallback: "Import " + rows.length + " collective cases",
    }))}
    showX={false}
    closeOnOverlay
    maxWidth={600}
  >
        <p style={{ fontSize: FS.s11, marginBottom: 10 }}>
          {t("cc.import.cc_found_rows", {
            n: rows.length,
            count: rows.length, // 208 §20
            fallback: "Found " + rows.length + " rows.",
          })}
        </p>
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: FS.s10,
              fontWeight: 600,
              color: "var(--tm)",
              marginBottom: 6,
            }}
          >
            {t("cc.import.mode_label", { fallback: "Import mode" })}
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: FS.s11,
              cursor: "pointer",
              marginBottom: 4,
            }}
          >
            <input
              type="radio"
              checked={mode === "add"}
              onChange={() => setMode("add")}
            />
            {t("cc.import.cc_add_update", {
              fallback: "Add new, update existing (match by Ref)",
            })}
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: FS.s11,
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              checked={mode === "replace"}
              onChange={() => setMode("replace")}
            />
            <span>
              {t("cc.import.cc_replace_all", {
                fallback: "Replace all collective cases",
              })}{" "}
              <span style={{ color: "var(--rd)", fontSize: FS.s10 }}>
                {t("cc.import.cc_replace_destructive", {
                  fallback: "(destructive)",
                })}
              </span>
            </span>
          </label>
        </div>
        <div
          style={{
            fontSize: FS.s10,
            fontWeight: 600,
            color: "var(--tm)",
            marginBottom: 4,
          }}
        >
          {t("cc.import.preview", withCount(Math.min(5, rows.length), "n", { fallback:
              "Preview (first " +
              Math.min(5, rows.length) +
              " rows)",
          }))}
        </div>
        <div
          className="table-wrap"
          style={{ maxHeight: 200, overflowY: "auto" }}
        >
          <Table style={{ width: "100%" }}>
            <thead>
              <tr>
                {hdrs.slice(0, 7).map((h2) => (
                  <th
                    key={h2}
                    style={{
                      fontSize: FS.s8,
                      padding: "3px 4px",
                      color: "var(--tm)",
                    }}
                  >
                    {h2}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 5).map((row, i) => (
                <tr key={i}>
                  {hdrs.slice(0, 7).map((h2) => (
                    <td
                      key={h2}
                      style={{
                        fontSize: FS.s9,
                        padding: "2px 4px",
                        maxWidth: 100,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {String(row[h2] || "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
  </ModalLayout>
);
}

// 140 §3.3 — the slice of a shaped collective-case row the convert flow reads
// and writes back into `cc`/`f`. The rows carry dozens more fields; the merge
// spreads the whole response row into the LIST and only these into the form.
type ConvertedCcRow = {
  id?: number;
  ref?: string | null;
  status?: string | null;
  isProspect?: boolean;
  formerRef?: string | null;
  pendingDate?: string | null;
  closedDate?: string | null;
  archivedDate?: string | null;
} & Record<string, unknown>;

const CollectiveCaseView = ({
  cc,
  sCc,
  ccMode,
  setCcMode,
  cl,
  sCl,
  ca,
  // 174 §5 — the email section's matter-choice modal's recents.
  getRecentCases,
  // 190 round 6 [T] — App's quadrant resolver, for the case Bento's card edges.
  userMatrixQuadrant,
  did,
  sDid,
  op,
  ocu,
  ex,
  cp,
  oc,
  me,
  ar,
  op2,
  co,
  ju,
  te,
  sTe,
  ag,
  sAg,
  tk,
  sTk,
  OPEN_ST,
  tkTypes,
  evtTypes,
  schedAxis,
  holidays, // 132 §6
  calDisp, // 133 §4
  agTypeFilt,
  setAgTypeFilt,
  currentUser,
  s,
  nav,
  tryNavCase,
  toast,
  pname,
  coIni,
  SK,
  I,
  nid,
  sv,
  chatMsgs,
  setChatMsgs,
  chatRead,
  setChatRead,
  setMailDlg,
  setDoodleDlg,
  mtgDefLoc,
  linkTypesList,
  caseTypesList,
  caseStatusesList,
  docStatusesList,
  stageTypesList,
  taskStatusesList,
  itemLevelList,
  reminderOptsList,
  setPartyDetailDlg,
  activeTimers,
  startTimer,
  inv,
  sInv,
  firmSettings,
  globalOm,
  MultiSelect,
  sQm,
  sQf,
  partiesLocked: partiesLockedProp,
  setPartiesLocked: setPartiesLockedProp,
  partiesOpen: partiesOpenProp,
  setPartiesOpen: setPartiesOpenProp,
  TkStatusTag,
  partyRoleEntries,
  // 176 §3.2 — forwarded to CaseDocumentsSection untouched (App.tsx:9588).
  onProvisionFolders,
  // 212 §27 — the matters' subfolder check, handed on to the list's toolbar (absent: no button)
  onFolderCheck,
  svTkData,
  // brief 86 Phase 2 step 3 — click-to-call, widened from CaseView.
  onCallColleague,
  callActive,
  // Phase 29 C61 — subtask done-tick hands (task, subtask) to App's
  // complete-style prompt (time-log offer).
  onSubtaskDone,
  etTag,
  evtLongestCode, // 139 §1 — travels with etTag, to CaseAgendaSection
  coAvs,
  locIcon,
}: any) => {
  const { t } = useTranslation();
  const { lang: readerLang, available, locale: __locale } = useTranslation() as { lang: string; available: string[]; locale: string }; // 214 addendum 1 §11 — `locale`, for the margin's percentage (`pct1`) // 209 §5 — the dictionary's languages, the reader's
  /* Phase 30 Stage-3 (C-1) — matrix tasks can ride member cases outside the
     viewer's scoped lists; the bare nav() dead-jumped onto a blank CaseView.
     C85 guard instead. */
  const _navCase = tryNavCase || ((kind: any, id: any) => (nav(kind, id), true));
  // Phase M+3.7 Stage 3 round 3 — raw entries to localise type/status badges.
  const _caseTypeEntries = useTaxonomy("case_type").entries;
  // 05n — this view held TWO selects that hardcoded a vocabulary instead of
  // reading one: a person_title list (:8617 before this commit) and a country
  // list (:8840), both with the English label as the <option value>. They are
  // writers into `Client.title` and `Client.country`, so after the conversion
  // each save through them re-forked the column they had just been cleaned of.
  const _titles = useTaxonomyList("person_title");
  const _titleEntries = useTaxonomy("person_title").entries;
  const _countries = useTaxonomyList("country");
  const _countryEntries = useTaxonomy("country").entries;
  const _caseStageEntries = useTaxonomy("case_stage").entries;
  const _caseStatusEntries = useTaxonomy("case_status").entries;
  /* 189 §B — the definitions that say "show in lists", as the cc list's
     trailing columns. */
  const _customFieldTax = useTaxonomy("custom_field") as { entries: unknown; lang: string };
  const customFieldCols = customFieldColumns(_customFieldTax.entries, "cc", _customFieldTax.lang);
  /* 123 §3 — the firm's own first meeting type, for the parties-pane meeting
     button. `null` when the firm has flagged none, and the seed then leaves the
     type empty rather than naming a code the taxonomy may not have. */
  const _meetingType = firstMeetingTypeCode(useTaxonomy("event_type").entries);
  /* 124 §1 — and the mode that type declares. Archived-inclusive like App's own
     fold (#191); `_meetingType` itself excludes archived rows, so this only ever
     resolves a live one, but the fold's population must not disagree between the
     two files. */
  const _meetingDefaultMode =
    meetingDefaultModes(
      useTaxonomy("event_type", { includeArchived: true }).entries,
    )[String(_meetingType ?? "")] || null;
  const _itemLevelEntries = useTaxonomy("item_level").entries;
  // 05n — the CASE_TYPES fallback is gone (unreachable, and wrong-shaped once
  // the list carries codes); the local stays because :2811 reads it.
  const _caseTypes = caseTypesList || [];
  const _caseStatuses =
    caseStatusesList && caseStatusesList.length
      ? caseStatusesList
      : [
          ["active", t("status.active", { fallback: "Active" })],
          ["pending", t("case.status.pending", { fallback: "Suspended" })],
          ["closed", t("costs.status.closed", { fallback: "Closed" })],
          ["archived", t("case.status.archived", { fallback: "Archived" })],
        ];
  const _docStatuses =
    docStatusesList && docStatusesList.length ? docStatusesList : DOC_STATUSES;
  /* 151 §2 step 3 — document_category for the one modal's Type select; local
     like the sibling useTaxonomyList calls above (App threads none here). */
  const _docTypes = useTaxonomyList("document_category");
  // 05n — `_stageTypes` was DEAD here: declared, never read (its one reader
  // went with an earlier extraction). Removed rather than reshaped, the same
  // way 18 found `casePriList` and `rolesList` dead in SettingsView.
  const _taskStatuses =
    taskStatusesList && taskStatusesList.length
      ? taskStatusesList
      : TASK_STATUSES;
  // Brief 57 §1 — the RAW rows beside the tuple list above, because
  // `meta.terminal` is what decides whether a status is open and the tuple
  // shape carries no meta.
  const _taskStatusEntries = useTaxonomy("task_status").entries;
  const _reminderOpts =
    reminderOptsList && reminderOptsList.length
      ? reminderOptsList
      : REMINDER_OPTS;
  const _ITEM_LEVEL = itemLevelMap(itemLevelList);
  const [mod, sMod] = useState<any>(null);
  const [f, sF] = useState<any>({});
  // Phase 23 (D2-corr) — inline event-subtask expansion (CCV agenda pane).
  const [evExpanded, setEvExpanded] = useState(() => new Set());
  const [tkExpanded, setTkExpanded] = useState(() => new Set());
  const toggleEvExpanded = (id: any) =>
    setEvExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const { confirmAndUndo, confirmDlg, cancelConfirm, undoToast, runUndo } =
    useConfirmAndUndo();
  // Phase M+2b: usePermissions hook + per-CC RP fetch.
  const { can: _permCan, viewer: _permViewer, finVisible: _permFin, billingDelegate: _permBillingDelegate } = usePermissions();
  /* 190 §D — the list Bento's hook, at the top with the other hooks (rules
     of hooks: the list block below sits after the detail screen's early
     return). It fetches only while the board is showing. */
  const listBento = useBento("list", "cc", ccMode === "board");
  /* 190 round 6 [S](i) — this host holds the other kind's rows too (`ca`); the same hook over cases when the template spans both. */
  const listBentoOther = useBento("list", "case", ccMode === "board" && listBento.allMatters);
  const [_ccRPMap, _setCcRPMap] = useState<any>({});
  useEffect(() => {
    if (!did) return;
    // Server enforces visibility — FE guard removed in hotfix #2.
    let cancelled = false;
    api
      .get("/collective-cases/" + did + "/responsible-partners")
      .then((rps) => {
        if (cancelled) return;
        const ids = new Set(
          (Array.isArray(rps) ? rps : []).map((r) => r.collaboratorId),
        );
        _setCcRPMap((prev: any) => ({ ...prev, [did]: ids }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [did]);
  // Phase 25 — load the CC's bulk mailings so the Timeline can show one summary
  // line per mailing. Gated by the view permission to avoid 403 noise.
  /* 211 §10 — asked the 210 §1 way (src/hooks/usePageList.ts): it answered
     `[]` when it failed, and the timeline lost its mailings' lines without a
     word. A mailing sent asks it again (`loadBulkMailings`, the mailer's
     `onSent`). */
  const [_mailingsSent, _setMailingsSent] = useState(0);
  const _mailings = usePageList<any>(
    PAGE_LISTS.mailings,
    "the collective case's bulk mailings",
    did && _permCan("collectiveCase.bulkMailer.view", { ccId: did }) ? "/collective-cases/" + did + "/bulk-mailings" : null,
    readList,
    String(_mailingsSent),
  );
  const bulkMailings = _mailings.rows;
  const loadBulkMailings = useCallback(() => {
    _setMailingsSent((n) => n + 1);
  }, []);
  const _isRPForCC = (ccId: any) =>
    !!(_ccRPMap[ccId] && _permViewer && _ccRPMap[ccId].has(_permViewer.id));
  const ccRef = useRefLatest(cc);
  const tkRef = useRefLatest(tk);
  // BRIEF 90 §5 (86 Phase 2, step 1) — the undo snapshot's restore target,
  // the pair of CaseView.tsx:875. Deleting a time entry here used to be
  // immediate and irreversible; see the section for why that is the half of
  // this pair that was fixed rather than merged.
  const teRef = useRefLatest(te);
  const agRef = useRefLatest(ag);
  const [filter, setFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  // 58 §2 row 11 — "Mes dossiers" on this list too. Same state shape as the
  // case list's `caMy`; the pill, its count and its label all come from the
  // shared builder, so there is one declaration and not two.
  const [ccMy, setCcMy] = useState(false);
  /* 139 §2.7 — the cc list lights up at no cc-specific cost: this state + one
     prop below are the same two lines the case list adds, and the badge, the
     toggle and the filter arm all live in the shared builder.

     140 §3.1 — AND THE SECOND CREATE BUTTON ARRIVES: unit 8 is this brief, so
     `om` is no longer called with `null` by every caller. The prospect arm it
     has carried since unit 2 (grep `genRef(true)`) is what the button reaches,
     and `onAddProspect` is passed at the builder call site below. */
  const [ccProspect, setCcProspect] = useState<ProspectFilterValue>("both");
  const [sort, setSort] = useState({ k: "nextev", d: 1 });
  const [clFilter, setClFilter] = useState("all");
  const [clSort, setClSort] = useState<MemberSort>(MEMBER_SORT_DEFAULT); // 208 §8 — the members table's `{ k, d }`; §6's order by default
  const [clQ, setClQ] = useState(""); // 208 §7 — the members table's search
  const sortClBy = (k: string) => {
    setClSort((cur) => nextMemberSort(cur, k));
  };
  const [payDlg, setPayDlg] = useState<any>(null);
  const [payForm, setPayForm] = useState({
    amount: "",
    date: "",
    reference: "",
  });
  // 213B §22 [RULED 50] — the payment window's « Montant dû » section: the adapted amount typed and why; reset when
  // the window opens (the cell's onClick), filled with the adapted due where the cell is stated.
  const [dueForm, setDueForm] = useState({ amount: "", note: "" });
  const setDueAmount = (n: number | null) => { setDueForm((p) => ({ ...p, amount: n == null ? "" : String(n) })); };
  const setDueNote = (v: string) => { setDueForm((p) => ({ ...p, note: v })); };
  /** The formula's amount, landed (or not) from the engine's second answer, into the open window's state. */
  const setFormulaDue = (v: number | null) => {
    setPayDlg((p: unknown) => (p && typeof p === "object" ? { ...p, formulaDue: v } : p));
  };
  const [newClDlg, setNewClDlg] = useState(false);
  const [newClF, setNewClF] = useState<any>({});
  // 208 §4 [RULED 13] — « + Add » opens the window at once (the Phase 23 panel is gone). A client of the
  // firm picked in its name field: that record's id, which the window then saves to; null for a new client.
  const [newClPickId, setNewClPickId] = useState<number | null>(null);
  const [contactBuilderOpen, setContactBuilderOpen] = useState(false);
  // Phase 25 — collective bulk-mailer surface + its mailing log (for the
  // Timeline summary lines).
  /* 138A §A1 — the clients section now has TWO doors into ONE modal, so this
     holds WHICH one rather than merely whether one is open. `null` is closed;
     the two truthy values are the component's own `mode`, so there is no second
     vocabulary to keep in step. */
  const [bulkMailerOpen, setBulkMailerOpen] = useState<BulkMailerMode | null>(null);
  const setBulkMailerMode = (m: BulkMailerMode | null) => setBulkMailerOpen(m);
  const csvRef = useRef<any>(null);
  // C.2 — the retainer-round money engine moved server-side
  // (POST /api/retainer/compute; same arithmetic, verbatim,
  // recording-verified: recordings/c2-rounds/). The view posts the rounds and
  // member rows it holds and displays the returned money — it computes none.
  // Latest-wins: a stale response never overwrites a newer one. The previous
  // result stays on screen until the next response lands ("—" placeholders
  // before the first); results carry the CC id so a case switch never shows
  // another case's money.
  const [roundsCalc, setRoundsCalc] = useState<any>(null);
  const _rcCs = did ? cc.find((c: any) => c.id === did) : null;
  const _rcRounds = _rcCs?.rounds || [];
  const _rcLinks = _rcCs?.clientLinks || [];
  // The Clients section's allCls join (link order, missing clients dropped),
  // reduced to the compute inputs.
  const _rcMembers = _rcLinks
    .map((lk: any) => {
      const c = cl.find((x: any) => x.id === lk.clId);
      return c
        ? {
            clId: lk.clId,
            // 211 Part Eight [RULED 24] — the member's: its own, else the client's
            // (a member with none posts exactly what it posted)
            quantum: memberQuantum(lk as { quantum?: unknown }, c as { quantum?: unknown }),
            payments: lk.payments || [],
            /* LEGAL-LANE (fix 3) — the per-member per-round due override the
               retainer import writes. Sent only when the member actually has
               one, so the compute key (and therefore the request) is unchanged
               for every member that does not — the engine's `dueFor` falls back
               to formula × quantum exactly as before. */
            ...(lk.dueByRound && Object.keys(lk.dueByRound).length
              ? { dueByRound: lk.dueByRound }
              : {}),
            /* 141 §3.1 — the termination gate's one input, on the same
               conditional-spread shape and for the same reason: a round due
               after this date generates no due for this member, unless a
               stated dueByRound amount names the round (found 17 ruling: the
               override wins). Only sent when set, so absent posts identical —
               and _rcKey changing on termination recomputes the money cards. */
            ...(lk.terminatedDate
              ? { terminatedDate: lk.terminatedDate }
              : {}),
          }
        : null;
    })
    .filter(Boolean);
  // The round editor's sample quantum (first member's non-zero quantum, else
  // 10000) — the derivation stays client-side (it only SELECTS the quantum);
  // the evaluation happens server-side.
  const _rcFirstCl =
    (_rcLinks[0] && cl.find((x: any) => x.id === _rcLinks[0].clId)) || null;
  const _rcSampleQ = _rcFirstCl ? Number(memberQuantum(_rcLinks[0] as { quantum?: unknown }, _rcFirstCl as { quantum?: unknown })) || 10000 : 10000; // 211 Part Eight — the first member's
  const _rcKey = _rcCs
    ? JSON.stringify({
        ccId: _rcCs.id,
        rounds: _rcRounds,
        members: _rcMembers,
        previewQuantum: _rcSampleQ,
      })
    : null;
  useEffect(() => {
    if (!_rcKey) {
      setRoundsCalc(null);
      return;
    }
    let live = true;
    computeRetainerRounds({
      rounds: _rcRounds,
      members: _rcMembers,
      previewQuantum: _rcSampleQ,
    })
      .then((r) => {
        if (live) setRoundsCalc({ ccId: _rcCs.id, data: r });
      })
      .catch(() => {
        if (live) setRoundsCalc(null);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_rcKey]);
  // C.2 — the CC financial summaries + success fee moved server-side
  // (POST /api/financials/compute; same arithmetic, verbatim,
  // recording-verified: recordings/c2-financials/). The view posts the rows
  // it holds and displays the returned money — it computes none.
  // Latest-wins; "—" placeholders before the first response; results are
  // keyed by cc id so a case switch never shows another case's money.
  const { rows: _roleRows } = useRoleLabels();
  const [ccFinCalc, setCcFinCalc] = useState<any>(null);
  // 161 §8/§9 — the open matter's OVERHEADS share (R9), served; see CaseView.
  const [ohShare, setOhShare] = useState<Record<number, number>>({});
  useEffect(() => {
    if (!did) return;
    let live = true;
    getOverheadsShare(null, did)
      .then((r: any) => {
        if (live && r && Number.isFinite(+r.share)) setOhShare((p) => ({ ...p, [did]: +r.share }));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [did]);
  const _cfSeq = useRef(0);
  useEffect(() => {
    const seq = ++_cfSeq.current;
    computeCaseFinancials({
      collectiveCases: (cc || []).map(redMatterFin),
      entries: (te || []).map(redEntryFin),
      collaborators: (co || []).map(redCoFin),
      roleRows: redRoleRows(_roleRows),
      // Brief 87 §3b — see CaseView: per-entry VAT from the invoice snapshot.
      invoices: (inv || []).map(redFinInv),
      // 161 §8 — the recap's DOCUMENT basis (R7) + the overheads input (R9).
      matterInvoices: (inv || []).map(redRecapInv),
      overheadsByCc: ohShare,
    })
      .then((r: any) => {
        if (_cfSeq.current !== seq) return;
        const byId: any = {};
        for (const row of r?.perCc || []) byId[row.id] = row;
        setCcFinCalc(byId);
      })
      .catch(() => {
        if (_cfSeq.current === seq) setCcFinCalc(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cc, te, co, _roleRows, inv, ohShare]);
  // C.2 — the CC-modal Bruto/Netto success-fee estimate is server-handed too
  // (the browser rendered bruto and the literal bruto − 0; both now arrive
  // from the same endpoint). Keyed on the live form fields, latest-wins.
  const [ccModalCalc, setCcModalCalc] = useState<any>(null);
  const _cmSeq = useRef(0);
  useEffect(() => {
    if (mod !== "cc") {
      setCcModalCalc(null);
      return;
    }
    const seq = ++_cmSeq.current;
    computeCaseFinancials({
      ccModal: {
        // B7 (2026-07-13 fix pass) — carry the CC id so the server can blank
        // the estimate for viewers outside isFinancialVisible (the case
        // modal already posted its id).
        id: f.id === undefined ? null : f.id,
        // P17.1a (F2) — the served rate card (exVAT/vatIncl) needs the rate inputs.
        referenceHourlyRate:
          f.referenceHourlyRate === undefined ? null : f.referenceHourlyRate,
        costsRatio: f.costsRatio === undefined ? null : f.costsRatio,
        claimAmount: f.claimAmount === undefined ? null : f.claimAmount,
        successFeeRate: f.successFeeRate === undefined ? null : f.successFeeRate,
      },
    })
      .then((r: any) => {
        if (_cmSeq.current === seq) setCcModalCalc(r?.ccModal ?? null);
      })
      .catch(() => {
        if (_cmSeq.current === seq) setCcModalCalc(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mod, f.referenceHourlyRate, f.costsRatio, f.claimAmount, f.successFeeRate]);
  const [showDesc, setShowDesc] = useState(false);
  const [partiesLockedLocal, setPartiesLockedLocal] = useState(true);
  /* 214 §3 [RULED 52] — the suspend window for a counsel's link, and the role's word for a contact with the role
     Avocat (the contact_role vocabulary; the hook's cache is module-level). */
  const [suspendDlg, setSuspendDlg] = useState<{ id: number; name: string } | null>(null);
  const contactRoleEntries = useTaxonomy("contact_role").entries as TaxList;
  const partiesLocked =
    partiesLockedProp !== undefined ? partiesLockedProp : partiesLockedLocal;
  const setPartiesLocked = setPartiesLockedProp || setPartiesLockedLocal;
  const [partiesOpenLocal, setPartiesOpenLocal] = useState<any>({});
  const partiesOpen =
    partiesOpenProp !== undefined ? partiesOpenProp : partiesOpenLocal;
  const setPartiesOpen = setPartiesOpenProp || setPartiesOpenLocal;
  const [collStg, setCollStg] = useState<any>({});
  const [stgExp, setStgExp] = useState(() => new Set());
  const [tkStatusFilt, setTkStatusFilt] = useState("");
  const [tkSort, setTkSort] = useState<any>({});
  const [tkMy, setTkMy] = useState(false);
  const [tkMatrix, setTkMatrix] = useState(false);
  /* 190 glance round 2 [A] — the case Bento's open state lives beside the
     matrix's: the Tasks section holds the toggle and the pane, the Agenda
     section hides its body under the pane. */
  const [caseBento, setCaseBento] = useState(false);
  const [tkPage, setTkPage] = useState(0);
  useEffect(() => {
    setTkPage(0);
  }, [tkMy, tkStatusFilt, did]);
  // Phase 13 Item 4 — ephemeral per-section collapse map. Defaults match the
  // brief: Description / Agenda / Tasks / Stages / Emails / Time-entries open;
  // everything else collapsed. CcView has additional sections (ccClients,
  // ccRetainer, docTracking). Phase 20 unified the Timeline into a shared
  // component placed inside csStagesCol (under Stages); the historical
  // csSec-emails/csSec-emailBody class swap is gone — actual emails now live
  // in csSec-emailBody and the Timeline lives in csStagesCol. No persistence
  // — re-collapses on reload.
  const [secCol, setSecCol] = useState<any>({ ...CC_SEC_COL_DEFAULTS });
  const sCol = (k: any) => !!secCol[k];
  const tog = (k: any) => setSecCol((p: any) => ({ ...p, [k]: !p[k] }));
  const [eisenPickerOpen, setEisenPickerOpen] = useState(false);
  const [pfMod, setPfMod] = useState<any>(null);
  const [ccListLocked, setCcListLocked] = useState(true);
  const [ccImportFile, setCcImportFile] = useState<any>(null);
  const ccFileRef = useRef<any>(null);
  const [addCoOpen, setAddCoOpen] = useState(false);
  // 86 Phase 2 step 7 — the assigned-collaborator table's page. CaseView paged
  // at 10 (`CaseView.tsx:740`); this side rendered every row, so a matter with
  // thirty collaborators scrolled the card off the screen.
  const [crPage, setCrPage] = useState(0);
  const [clImportFile, setClImportFile] = useState<any>(null);
  const [agMy, setAgMy] = useState(false);
  const [agWeek, setAgWeek] = useState("this");
  const [agPage, setAgPage] = useState(0);
  // Phase 28 (item B) — append-only chronology rows for this CC's timeline.
  /* Round 19 (C104) — same gate as CaseView: fetch only when this cc is in
     the viewer's server-scoped list; the membership boolean is a dep so the
     entitled fetch fires once the boot lists arrive (deferred, never
     skipped), and an out-of-scope `did` never issues the request. */
  const _chronVisible = did != null && cc.some((c: any) => c.id === did);
  /* 211 §10 — as CaseView's (src/hooks/usePageList.ts): the refresh is what
     the effect re-read on. The timeline layers it and the mailings over App's
     marks. */
  const _chron = usePageList<any>(
    PAGE_LISTS.chronicle,
    "the collective case's chronicle",
    _chronVisible ? "/collective-cases/" + String(did) + "/chronicle" : null,
    readList,
    [cc.find((c: any) => c.id === did)?.updatedAt, te.length, ag.length, tk.length].map(String).join("|"),
  );
  const chronicle = _chron.rows;
  const _tlLists = usePageListContext(_chron.entry, _mailings.entry);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatType, setChatType] = useState("general");
  const [chatSearch, setChatSearch] = useState("");
  const [chatMention, setChatMention] = useState(false);
  const [chatMentionQ, setChatMentionQ] = useState("");
  const chatRef = useRef<any>(null);
  const [collabLocked, setCollabLocked] = useState(true);
  const [teamOpen, setTeamOpen] = useState(false);
  // Phase 28 (E) — lock affordance for the Access-rights matrix in the
  // consolidated "Case Team" section. Default locked, like collabLocked.
  // Phase 37 S3 correction 1 — viewing + editing panes merged into one matrix,
  // so a single lock now governs the whole rights area.
  const [teamViewLocked, setTeamViewLocked] = useState(true);
  const [stagesOpen, setStagesOpen] = useState(false);
  const [agLocked, setAgLocked] = useState(true);
  const [tkLocked, setTkLocked] = useState(true);
  const [docLocked, setDocLocked] = useState(true);
  const [stgLocked, setStgLocked] = useState(true);
  // 186 (e) [RULED 186 (16)] — the Stages search field's query; the list is
  // filtered here by CaseStageTimeline's own predicate (this view pages nothing).
  const [stgQ, setStgQ] = useState("");
  const [clLocked, setClLocked] = useState(true);
  const [roundsLocked, setRoundsLocked] = useState(true);
  /* BRIEF 108 §5 — the CaseView mirror: locks re-lock when the viewed matter
     changes (unmount already covers leaving the section). Includes the list
     lock (drilling into a matter is leaving the list page) and the
     partiesLockedLocal fallback — the live path's parties lock is App-held
     and App's own §5 effect resets it. */
  useEffect(() => {
    setCcListLocked(true);
    setCollabLocked(true);
    setTeamViewLocked(true);
    setAgLocked(true);
    setTkLocked(true);
    setDocLocked(true);
    setStgLocked(true);
    setClLocked(true);
    setRoundsLocked(true);
    setPartiesLockedLocal(true);
    /* 110 §6a — the CaseView mirror: the stage drawers' open-state (a Set of
       bare per-matter stage ids, colliding across matters) clears on the
       same key; the timeline's notesOpen clears via its mount key. */
    setStgExp(new Set());
    /* 190 round 9 [AT] — the CaseView mirror: the collapse map back to its
       defaults on the same key (one map per component since Phase 13, never
       keyed on the matter; this component does not unmount between matters). */
    setSecCol({ ...CC_SEC_COL_DEFAULTS });
  }, [did]);
  // 55 rows 12+13 — the Gate-4 C6 D11 β two-axis CC list filter is removed:
  // both state hooks, both chip rows, both `.filter()` passes. Stale from the
  // former invoicing system, and the case list never had them. The Latest
  // invoice COLUMN is untouched.
  /* Phase 7 round 5 Item B — agMod state removed; CCV event add/edit routes through the canonical App.jsx case "ag" modal via globalOm. */
  useEffect(() => {
    console.log(
      "CC useEffect did=",
      did,
      "lx:scrollToEvent=",
      localStorage.getItem("lx:scrollToEvent"),
    );
    if (did) {
      // Phase 19 r9 — last-mile fix for "open linked event" from Places.
      // The CCV agenda is open by default but defaults to the current-week
      // filter (agWeek="this"), so a linked event in another week isn't in
      // the DOM when scrollToEv fires. If a scroll is queued for an event on
      // THIS collective case, clear the week + "My" filters first so
      // #ev-<id> renders. (CCV agenda is not paginated, so no page jump.)
      const pendingEv = peekScrollToEv();
      if (pendingEv != null) {
        const evRow = (ag || []).find((a: any) => a.id === Number(pendingEv) || a.id === pendingEv);
        if (evRow && evRow.ccId === did) {
          setAgWeek("all");
          setAgMy(false);
        }
      }
      checkScrollToEv();
      checkScrollToTk();
      setTkSort((p: any) => {
        if (!p[did]) return p;
        const n = { ...p };
        delete n[did];
        return n;
      });
    }
  }, [did]);

  const uf = (k: any, v: any) => sF((p: any) => ({ ...p, [k]: v }));
  const cm = () => {
    sMod(null);
    sF({});
  };

  /* RULED 2026-07-25 — the collective case has its OWN ref pattern
     (FirmSettings.ccRefPattern, default "CC-{YYYY}/{NNN}"), which is exactly
     what this used to build by hand. It gets one because it never really
     shared the case format: this modal offered "CC-<year>/<nnn>" while the
     server generator built "<year>/<nnn>", and since the create route reads
     `ref || generateRefTx(…)` the prefilled form is what reached the database.
     Both ends now read the same column, through the same pure grammar. */
  /* BRIEF 04 UNIT 2 — the prospect half of the same rule (ruling 9: the
     collective case gets its own zero series). Which column is the tag's
     answer, exactly as on the case side, so the pre-filled ref matches what the
     create route will allocate. The argument is a SEED, not the form: `om(null)`
     opens an ordinary collective case and is every caller today. Unit 8 (PR 3)
     is what adds the button that passes `true`. */
  const genRef = (prospect = false) =>
    nextRefFrom(
      prospect ? firmSettings?.prospectCcRefPattern : firmSettings?.ccRefPattern,
      cc,
    );

  const om = (e: any) => {
    if (e && !(e.isProspect && !e.id)) {
      sF(normFormDates(e));
    } else if (e) {
      sF({ ref: genRef(true), status: "active", isProspect: true });
    } else {
      sF({ ref: genRef(), status: "active" });
    }
    sMod("cc");
  };
  /* 209 §5 [RULED 18] — THE FORM, ONE CLICK AWAY. A language refusal met on
     this collective case (its e-mail billing, a drop, its memo, a pro forma)
     carries an action asking for its form (src/lib/caseLanguage.ts): on the
     case's own page this view opens it; met elsewhere, the app navigates here
     and the view takes the waiting request on arrival. */
  // The handler reads the view as it is NOW: kept current after every render,
  // subscribed once (the effect below), so no effect re-subscribes per render.
  const ccFormCtx = useRef({ cc: cc as unknown, did: did as unknown, om });
  useEffect(() => {
    ccFormCtx.current = { cc: cc as unknown, did: did as unknown, om };
  });
  const openCcForm = (ccId: number) => {
    const row = (ccFormCtx.current.cc as { id: number }[]).find((x) => x.id === ccId);
    if (row) ccFormCtx.current.om(row);
  };
  useEffect(() => {
    const h = (ev: Event) => {
      const m = (ev as CustomEvent<MatterRef>).detail;
      if (m.ccId && Number(ccFormCtx.current.did) === m.ccId) openCcForm(m.ccId);
    };
    globalThis.addEventListener(OPEN_MATTER_FORM, h);
    return () => { globalThis.removeEventListener(OPEN_MATTER_FORM, h); };
  }, []);
  useEffect(() => {
    // a request that waited while the app navigated here: taken once, after this render
    if (did != null && takePendingMatterForm(Number(did))) {
      const ccId = Number(did);
      globalThis.setTimeout(() => { openCcForm(ccId); }, 0);
    }
  }, [did]);
  /* 209 §5 — a NEW collective case's language is OFFERED from its first client
     (a client of the firm with a language the dictionary carries): shown in the
     field and saved with it unless changed — never imposed on an existing one. */
  const fv = f as { id?: number | null; language?: string | null; clientLinks?: { clId?: number; clientId?: number }[] };
  const firstCcClientLang = (() => {
    const first = Array.isArray(fv.clientLinks) ? fv.clientLinks[0] : undefined;
    const c = first ? (cl as { id: number }[]).find((x) => x.id === (first.clId ?? first.clientId)) : undefined;
    const code = c ? normalizeLang(storedClientLanguage(c)) : null;
    return code && available.includes(code) ? code : null;
  })();
  const ccLang = storedCaseLanguage(fv) ?? (fv.id ? null : firstCcClientLang);

  /* 140 §3.3 (brief 04 unit 8) — THE SWITCH, COLLECTIVE HALF. Choosing a real
     status on a tagged ROW is NOT a status edit: it fires the convert route.
     Getting this wrong is the whole risk of the unit on both sides — the
     control looks like a PUT and is not one. §2.4's ordering is the safety
     mechanism: convert flips the tag and allocates the ref immediately (no
     Graph call); the folder step waits in `onExpire` until the window closes;
     an undo inside the window therefore costs nothing.

     THE ONE DIFFERENCE from the case flow is on the server and is written out
     there: a collective conversion does not untag its members, so there are no
     client ids to carry back — the undo body is the status snapshot alone. */
  const convertProspectCc = (picked: string) => {
    if (!f.id) return;
    const ccId: number = f.id;
    // Captured before the mutation: what the row was, to hand back on undo.
    // The form already holds these as "YYYY-MM-DD" (normFormDates ran when the
    // modal opened); the server's normalizeDates accepts that shape.
    const snapshot = {
      status: (f.status as string) || "active",
      pendingDate: (f.pendingDate as string) || null,
      closedDate: (f.closedDate as string) || null,
      archivedDate: (f.archivedDate as string) || null,
      // 209 §23 — the moment the status was set: an undone conversion gives it back with the status
      statusDate: (f as { statusDate?: string | null }).statusDate ?? null,
    };
    // Shared by the three closures below (same invocation scope, no hook):
    // `done` is the convert round-trip, and both undo and finalize must wait
    // for it so a fast Undo click cannot outrun the convert response.
    const ctx: { done: Promise<ConvertedCcRow | null> | null } = { done: null };
    const mergeRow = (row: ConvertedCcRow) => {
      // The whole row into the LIST; only the conversion's own fields into the
      // form — a wholesale overwrite would clobber in-flight edits of others.
      sCc((prev: unknown) =>
        Array.isArray(prev)
          ? prev.map((c: ConvertedCcRow) => (c.id === ccId ? { ...c, ...row } : c))
          : prev,
      );
      sF((p: ConvertedCcRow) => ({
        ...p,
        ...normFormDates({
          ref: row.ref ?? p.ref,
          status: row.status ?? p.status,
          isProspect: !!row.isProspect,
          formerRef: row.formerRef ?? null,
          pendingDate: row.pendingDate ?? null,
          closedDate: row.closedDate ?? null,
          archivedDate: row.archivedDate ?? null,
        }),
      }));
    };
    confirmAndUndo({
      title: [f.ref, f.title].filter(Boolean).join(" — "),
      // The RULED sentence, shared verbatim with the case side: one key, one
      // set of translations, one thing Laurent reads at the glance.
      message: t("case.convert.confirm", {
        fallback:
          "This will transform the Prospect into a real case with a casefile. Continue?",
      }),
      confirmLabel: t("common.continue", { fallback: "Continue" }),
      onConfirm: () => {
        ctx.done = (convertCollectiveCase(ccId, { status: picked }) as Promise<{
          collectiveCase?: ConvertedCcRow;
        } | null>)
          .then((out: { collectiveCase?: ConvertedCcRow } | null) => {
            const row = out?.collectiveCase;
            if (!row) return null;
            mergeRow(row);
            return row;
          })
          .catch((e: Error) => {
            toast(e.message, "error");
            return null;
          });
      },
      undo: {
        snapshot,
        restore: (snap: unknown) => {
          const s2 = snap as typeof snapshot;
          Promise.resolve(ctx.done).then((converted) => {
            if (!converted) return; // the convert itself failed — nothing to invert
            (convertCollectiveCaseUndo(ccId, s2) as Promise<{
              collectiveCase?: ConvertedCcRow;
            } | null>)
              .then((out: { collectiveCase?: ConvertedCcRow } | null) => {
                if (out?.collectiveCase) mergeRow(out.collectiveCase);
              })
              .catch((e: Error) => toast(e.message, "error"));
          });
        },
      },
      toastName: t("case.convert.toast_undo", {
        ref: f.ref,
        fallback: `Converting ${f.ref} into a real case`,
      }),
      // §2.3 — 5s as written, 20 at this call site too: the duration is
      // comfort, the ordering is the safety.
      windowMs: 20000,
      onExpire: () => {
        Promise.resolve(ctx.done).then((converted) => {
          if (!converted) return;
          (convertCollectiveCaseFinalize(ccId) as Promise<{
            ref?: string;
            folder?: { did?: string };
          } | null>)
            .then((r: { ref?: string; folder?: { did?: string } } | null) => {
              const did = r?.folder?.did;
              const folderTxt =
                did === "renamed"
                  ? t("case.convert.folder_renamed", { fallback: "casefile folder renamed" })
                  : did === "created"
                    ? t("case.convert.folder_created", { fallback: "casefile folder created" })
                    : did === "already_named"
                      ? t("case.convert.folder_kept", { fallback: "casefile folder already up to date" })
                      : t("case.convert.folder_skipped", { fallback: "casefile folder not updated" });
              toast(
                `${t("case.convert.done", { ref: r?.ref || "", fallback: `Conversion final — now case ${r?.ref || ""}` })} — ${folderTxt}`,
                "success",
              );
            })
            .catch((e: Error) => toast(e.message, "error"));
        });
      },
    });
  };

  const save = () => {
    if (!f.title) {
      toast(t("toast.title_required", { fallback: "⚠ Title is required" }));
      return;
    }
    if (!f.ref) {
      toast(
        t("toast.reference_required", { fallback: "⚠ Reference is required" }),
      );
      return;
    }
    // 209 §5 [RULED 18] — a new collective case is not created without its language.
    if (!fv.id && !ccLang) {
      (toast as ToastFn)(String(tImperative("toast.case_language_required", { fallback: "⚠ The case's language is required" })));
      return;
    }
    const cd = {
      ...f,
      clIds: f.clIds || [],
      opIds: f.opIds || [],
      ocuIds: f.ocuIds || [],
      exIds: f.exIds || [],
      juId: f.juId ? +f.juId : null,
      status: f.status || "active",
      caseCollaborators: f.caseCollaborators || [],
      language: ccLang, // 209 §5 — the language shown is the language saved (an offered one included)
    };
    if (f.id) {
      s(
        SK.cc,
        cc.map((c: any) => (c.id === f.id ? { ...c, ...cd } : c)),
        sCc,
      );
      toast(t("toast.updated", { fallback: "Updated" }));
    } else {
      s(SK.cc, [...cc, { ...cd, id: tempId() }], sCc);
      toast(t("toast.created", { fallback: "Created" }));
    }
    cm();
  };

  const del = (id: any) => {
    s(
      SK.cc,
      cc.filter((c: any) => c.id !== id),
      sCc,
    );
    toast(t("common.deleted"));
    sDid(null);
  };

  // 55 rows 12+13 — the two invoice filter axes are gone with their chips.
  // Removing the chips alone would have left two `.filter()` passes reading
  // state nothing can change: harmless while it says "all", and unreachable
  // dead weight for the next reader.

  // A20 (brief 65) — src/lib/localToday.
  const todayStrCc = localTodayStr();
  const canFin = !!_permViewer?.isAdmin || _permCan("caseView.financials.view");
  const canTerms = !!_permViewer?.isAdmin || _permCan("caseView.terms.view");
  const canSeeFin = canFin;
  const isPartnerStrictCc = canTerms;
  // 55 §3 row 21 — the date that belongs to the CURRENT status, for the list
  // column beside the pill. `active` returns null on purpose: its date is the
  // Opening date, which already has its own column; the modal and the info
  // pane are where all four live together.
  const statusDateCc = (c: any) =>
    c.status === "pending"
      ? c.pendingDate || null
      : c.status === "closed"
        ? c.closedDate || null
        : c.status === "archived"
          ? c.archivedDate || null
          : null;
  // 55 row 22 — `marginPctClrCc` went with the Margin % column, its only
  // call site.
  // A4 (brief 65) — the reader lives in src/lib/matterMetrics; the five names
  // that differed between this copy and CaseView's are the arguments.
  const ccMetrics = (c: any) =>
    readMatterMetrics(c, {
      fin: ccFinCalc,
      tasks: tk,
      events: ag,
      taskOwnerKey: "ccId",
      eventOwnerKey: "ccId",
      rosterField: "clientLinks",
      todayStr: todayStrCc,
      taskStatusEntries: _taskStatusEntries,
    });
  // 55 row 7 — `a.ccId`, not `a.caId`. This filter is inside the COLLECTIVE
  // view and `c` is a collective case, so `a.caId === c.id` matched the CASE
  // whose id happens to be the same integer. Its sibling upcomingEvts above,
  // and every other agenda filter in this file, already read ccId. `nextev` is
  // the default sort, so this was how the screen arrived.
  // A16 (brief 65) — src/lib/nextEventDate; the owner key and the today string
  // are the two things that differed between the views.
  const nextEvDate = (c: any) =>
    nextEventDate(ag, "ccId", c.id, todayStrCc);
  const modal = mod === "cc" && (
    <ModalLayout
      title={
        f.id
          ? t("cc.modal.edit_title", { fallback: "Edit Collective Case" })
          : t("cc.modal.new_title", { fallback: "New Collective Case" })
      }
      onClose={cm}
      closeOnOverlay
      stopShellClicks
      rawBody
      footer={
        <div className="modal-footer">
          <button type="button" className="modal-cancel" onClick={cm}>
            {t("common.cancel", { fallback: "Cancel" })}
          </button>
          <button type="button" className="modal-save" onClick={save}>
            ✓ {t("common.save", { fallback: "Save" })}
          </button>
        </div>
      }
    >
        <div className="modal-body">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
            }}
          >
            <div className="field">
              {/* 58 §3 — the required marker was hand-rolled markup beside a
                  second key for the same word. `field.ref_required` is the
                  modal vocabulary's existing "Ref *", the same key every other
                  required modal label uses. */}
              <label>{t("field.ref_required", { fallback: "Ref *" })}</label>
              <input
                className="field-input"
                value={f.ref || ""}
                onChange={(e) => uf("ref", e.target.value)}
                /* 110 §5 — the placeholder that LIED: "CC-2024/001" reads
                   exactly like a stored reference, and the glance read the
                   empty fixture's box as filled — the "Reference is required"
                   ghost. A NEW key, per the 105 rule: the seed only inserts,
                   so editing modal.cc.ref_ph in place would correct nothing
                   on any seeded box; the old rows stay seeded and unread. */
                placeholder={t("modal.cc.ref_ph_hint", {
                  fallback: "e.g. CC-2026/001",
                })}
                autoFocus
              />
            </div>
            <div className="field">
              <label>{t("field.status", { fallback: "Status" })}</label>
              {/* 140 §3.3 [RULED, Laurent, 2026-08-27; brief 04 unit 8] —
                  while the ROW is tagged, a synthetic Prospect option sits
                  selected on top of the real statuses (which stay below it,
                  unchanged), and choosing a real status fires the convert flow
                  above instead of a status edit. Ruling 6 stands: the switch
                  lives in the edit modal, and this dropdown IS the affordance.

                  A tagged DRAFT (the + prospect prefill, no id yet) is not a
                  row and cannot convert — it keeps today's select exactly:
                  plain statuses, tag riding along orthogonally until first
                  save. Same gate as the case side (`f.id && f.isProspect`). */}
              <MenuSelect
                className="field-input"
                value={
                  f.id && f.isProspect ? "__prospect__" : f.status || "active"
                }
                onChange={(v) => {
                  // 55 §3 — the same auto-set the case modal has had since
                  // Phase 30. Until this slice the collective case had no
                  // column to auto-set into, so the modal only stored the
                  // status; now the companion date is filled on the flip and
                  // stays editable in the field below.
                  const td = new Date().toISOString().slice(0, 10);
                  if (f.id && f.isProspect) {
                    if (v === "__prospect__") return;
                    convertProspectCc(v);
                    return;
                  }
                  sF((p: any) => ({
                    ...p,
                    status: v,
                    pendingDate:
                      v === "pending" ? p.pendingDate || td : p.pendingDate,
                    closedDate:
                      v === "closed"
                        ? p.closedDate || td
                        : v !== "closed" && v !== "archived"
                          ? undefined
                          : p.closedDate,
                    archivedDate:
                      v === "archived" ? p.archivedDate || td : p.archivedDate,
                  }));
                }}
                options={[
                  ...(f.id && f.isProspect
                    ? [
                        [
                          "__prospect__",
                          t("matters.prospect.badge", { fallback: "Prospect" }),
                        ] as const,
                      ]
                    : []),
                  /* 108 §6b — the divider is the menu's own row now */
                  ..._caseStatuses.map(
                    ([v, l]: any) => [String(v), String(l ?? "")] as const,
                  ),
                ]}
              />
            </div>
            <FieldSelect
              label={t("common.type", { fallback: "Type" })}
              value={f.ctype}
              onChange={(v) => uf("ctype", v)}
              options={[["", "—"], ..._caseTypes]}
              entries={_caseTypeEntries}
              t={t}
            />
          </div>
          <div className="field">
            <label>{t("field.title_required", { fallback: "Title *" })}</label>
            <input
              className="field-input"
              value={f.title || ""}
              onChange={(e) => uf("title", e.target.value)}
            />
          </div>
          {/* 209 §5 [RULED 18] — the collective case's language: required for a
              new one, offered from its first client when it has a language the
              dictionary carries; an existing one without reads « to choose »,
              amber (the backfill never guesses a collective case). */}
          <div className="field-row" style={{ gridTemplateColumns: "1fr 2fr" }}>
            <FieldSelect
              label={`${String(tImperative("case.language.label", { fallback: "Case language" }))}${fv.id ? "" : " *"}`}
              value={ccLang ?? ""}
              onChange={(v) => { uf("language", v || null); }}
              options={[["", "—"], ...languageOptions(available, readerLang)]}
            />
            {!ccLang && fv.id && (
              <div style={{ alignSelf: "end", paddingBottom: 8, color: "var(--am)", fontSize: FS.s10_5, fontWeight: 600 }}>
                {String(tImperative("case.language.to_choose", { fallback: "Case language to choose" }))}
              </div>
            )}
          </div>
          {(() => {
            const _ccCtxModal = f.id ? { ccId: f.id } : {};
            const _canTermsModal =
              !!_permViewer?.isAdmin || _permCan("caseView.terms.view", _ccCtxModal);
            const _canFinModal =
              !!_permViewer?.isAdmin ||
              _permCan("caseView.financials.view", _ccCtxModal);
            const costsR = f.costsRatio ?? 19;
            const refR = f.referenceHourlyRate ?? 0;
            // P17.1a (F2) — rate card is server-authoritative (served with the
            // firm vatMul via ccModalCalc; was hardcoded *1.21). "—" until landed.
            const exVAT = ccModalCalc ? ccModalCalc.exVAT : null;
            const vatIncl = ccModalCalc ? ccModalCalc.vatIncl : null;
            return (
              <>
                <div className="field">
                  <label>
                    {t("modal.case.claim_amount", { fallback: "Claim Amount" })}
                  </label>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    {/* 208 §10 — the one amount field (src/components/AmountField.tsx): « 12 500,00 », typed with a decimal comma */}
                    <AmountField
                      value={f.claimAmount}
                      empty=""
                      inputMode="decimal"
                      onAmount={(n) => uf("claimAmount", n)}
                      style={{ flex: 1, padding: "4px 6px", fontSize: FS.s11_5 }}
                      placeholder="0"
                    />
                    <span
                      style={{
                        fontSize: FS.s10,
                        color: "var(--tm)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("common.currency_eur", { fallback: "EUR" })}
                    </span>
                  </div>
                </div>
                {_canTermsModal && (
                  <div
                    style={{
                      border: "1px solid var(--br)",
                      borderRadius: RAD.r8,
                      padding: "10px 12px",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: FS.s9_5,
                        textTransform: "uppercase",
                        letterSpacing: ".4px",
                        color: "var(--tm)",
                        fontWeight: 600,
                        marginBottom: 8,
                      }}
                    >
                      {t("modal.case.terms", { fallback: "Terms" })}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px 12px",
                        marginBottom: 6,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: FS.s10,
                            color: "var(--ts)",
                            marginBottom: 3,
                          }}
                        >
                          {t("modal.case.reference_hourly_rate", {
                            fallback: "Reference Hourly Rate",
                          })}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          {/* 208 §10 — the one amount field */}
                          <AmountField
                            value={refR || null}
                            empty=""
                            inputMode="decimal"
                            onAmount={(n) => uf("referenceHourlyRate", n)}
                            style={{
                              flex: 1,
                              padding: "4px 6px",
                              fontSize: FS.s11_5,
                            }}
                            placeholder="0"
                          />
                          <span
                            style={{
                              fontSize: FS.s10,
                              color: "var(--tm)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t("common.eur_per_hour", { fallback: "EUR/h" })}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: FS.s10,
                            color: "var(--ts)",
                            marginBottom: 3,
                          }}
                        >
                          {t("modal.case.costs_ratio_pct", {
                            fallback: "Costs Ratio %",
                          })}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <input
                            type="number"
                            step="1"
                            min="0"
                            max="100"
                            className="field-input"
                            value={costsR}
                            onChange={(e) => uf("costsRatio", +e.target.value)}
                            style={{
                              flex: 1,
                              padding: "4px 6px",
                              fontSize: FS.s11_5,
                            }}
                          />
                          <span
                            style={{
                              fontSize: FS.s10,
                              color: "var(--tm)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            %
                          </span>
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: FS.s10,
                            color: "var(--ts)",
                            marginBottom: 3,
                          }}
                        >
                          {t("cc.terms.total_exvat", {
                            fallback: "Total ExVAT",
                          })}
                        </div>
                        <div
                          className="field-input"
                          style={{
                            padding: "4px 6px",
                            fontSize: FS.s11_5,
                            color: "var(--ts)",
                            cursor: "default",
                          }}
                        >
                          {fq(exVAT)} {t("common.eur_per_hour", { fallback: "EUR/h" })}
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: FS.s10,
                            color: "var(--ts)",
                            marginBottom: 3,
                          }}
                        >
                          {t("cc.terms.total_vatincl", {
                            fallback: "Total VAT Incl.",
                          })}
                        </div>
                        <div
                          className="field-input"
                          style={{
                            padding: "4px 6px",
                            fontSize: FS.s11_5,
                            color: "var(--ts)",
                            cursor: "default",
                          }}
                        >
                          {fq(vatIncl)} {t("common.eur_per_hour", { fallback: "EUR/h" })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {_canFinModal && (
                  <div
                    style={{
                      border: "1px solid var(--br)",
                      borderRadius: RAD.r8,
                      padding: "10px 12px",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: FS.s9_5,
                        textTransform: "uppercase",
                        letterSpacing: ".4px",
                        color: "var(--tm)",
                        fontWeight: 600,
                        marginBottom: 8,
                      }}
                    >
                      {t("case.claim.success_fee", { fallback: "Success Fee" })}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px 12px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: FS.s10,
                            color: "var(--ts)",
                            marginBottom: 3,
                          }}
                        >
                          {t("modal.case.success_fee_rate_pct", {
                            fallback: "Success Fee Rate %",
                          })}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            className="field-input"
                            value={successFeeRateOf(f)}
                            onChange={(e) =>
                              uf("successFeeRate", +e.target.value)
                            }
                            style={{
                              flex: 1,
                              padding: "4px 6px",
                              fontSize: FS.s11_5,
                            }}
                          />
                          <span
                            style={{
                              fontSize: FS.s10,
                              color: "var(--tm)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            %
                          </span>
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: FS.s10,
                            color: "var(--ts)",
                            marginBottom: 3,
                          }}
                        >
                          {t("cc.terms.bruto_success_fee", {
                            fallback: "Bruto Success Fee",
                          })}
                        </div>
                        <div
                          className="field-input"
                          style={{
                            padding: "4px 6px",
                            fontSize: FS.s11_5,
                            color: "var(--ts)",
                            cursor: "default",
                          }}
                        >
{fq(ccModalCalc ? ccModalCalc.bruto : null)}{" "}
                          €
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: FS.s10,
                            color: "var(--ts)",
                            marginBottom: 3,
                          }}
                        >
                          {t("cc.terms.netto_success_fee", {
                            fallback: "Netto Success Fee",
                          })}
                        </div>
                        <div
                          className="field-input"
                          style={{
                            padding: "4px 6px",
                            fontSize: FS.s11_5,
                            color: "var(--ts)",
                            cursor: "default",
                          }}
                        >
{fq(ccModalCalc ? ccModalCalc.netto : null)}{" "}
                          €
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
          >
            <div className="field">
              <label>{t("field.open_date", { fallback: "Open Date" })}</label>
              <input
                className="field-input"
                type="date"
                value={(f.openDate || "").slice(0, 10)}
                onChange={(e) => uf("openDate", e.target.value)}
              />
            </div>
            <div className="field">
              <label>
                {t("field.jurisdiction", { fallback: "Jurisdiction" })}
              </label>
              <Combobox
                value={f.juId ? String(f.juId) : ""}
                onChange={(v) => uf("juId", v ? +v : "")}
                options={ju.map((j: any) => ({
                  value: String(j.id),
                  label: j.name || "(unnamed)",
                  sub: j.city || "",
                }))}
                placeholder={t("common.select_dots", { fallback: "Select..." })}
              />
            </div>
            <div className="field">
              <label>
                {t("case.field.introductionDate", {
                  fallback: "Introduction date",
                })}
              </label>
              <input
                className="field-input"
                type="date"
                value={(f.introductionDate || "").slice(0, 10)}
                onChange={(e) => uf("introductionDate", e.target.value)}
              />
            </div>
            {/* 55 §3 row 21 — the three status-companion dates, conditional on
                status exactly as the case modal does. Until this slice the
                archive button wrote archivedDate and closedDate into a form
                the route then dropped on the floor; there are columns now. */}
            {f.status === "pending" && (
              <div className="field">
                <label>{t("field.pending_since")}</label>
                <input
                  className="field-input"
                  type="date"
                  value={(f.pendingDate || "").slice(0, 10)}
                  onChange={(e) => uf("pendingDate", e.target.value)}
                />
              </div>
            )}
            {(f.status === "closed" || f.status === "archived") && (
              <div className="field">
                <label>{t("field.closed_on")}</label>
                <input
                  className="field-input"
                  type="date"
                  value={(f.closedDate || "").slice(0, 10)}
                  onChange={(e) => uf("closedDate", e.target.value)}
                />
              </div>
            )}
            {f.status === "archived" && (
              <div className="field">
                <label>{t("field.archived_on")}</label>
                <input
                  className="field-input"
                  type="date"
                  value={(f.archivedDate || "").slice(0, 10)}
                  onChange={(e) => uf("archivedDate", e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="field">
            {/* AUTHORITY (155 §4): twin of CaseModal.tsx's folder block —
                differs deliberately by scope ctx (ccId vs caseId); the
                link/permission wiring must change in BOTH. */}
            <label>
              {t("field.sharepoint_folder", {
                fallback: "SharePoint / OneDrive Folder",
              })}
            </label>
            {(() => {
              const _ccCtx = f.id ? { ccId: f.id } : {};
              const _canEditFolder = _permCan(
                "caseView.documents.link",
                _ccCtx,
              );
              const _canLinkFolder = _permCan(
                "caseView.documents.link",
                _ccCtx,
              );
              if (f.caseRootFolder) {
                const _nm =
                  f.caseRootFolder.split("/").filter(Boolean).pop() ||
                  f.caseRootFolder;
                return (
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                      flexWrap: "wrap",
                      padding: "5px 8px",
                      background: "var(--bi)",
                      border: "1px solid var(--br)",
                      borderRadius: RAD.r6,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: FS.s11,
                        color: "var(--tp)",
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={f.caseRootFolder}
                    >
                      <Folder
                        size={11}
                        style={{ color: "var(--ac)", flexShrink: 0 }}
                      />
                      {_nm}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{
                        padding: "2px 7px",
                        fontSize: FS.s10,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                      title={t("modal.case.open_in_sharepoint", {
                        fallback: "Open folder in SharePoint",
                      })}
                      onClick={async () => {
                        try {
                          // 174 §1.2 — through api(): BOTH credentials (the
                          // lx:token Bearer and the lx_session cookie), not
                          // the cookie alone. api() supplies the /api prefix.
                          const j = (await api.get(
                            "/auth/microsoft/browse?path=" +
                              encodeURIComponent(f.caseRootFolder) +
                              "&ccId=" +
                              f.id +
                              "&mode=resolve",
                          )) as { currentFolder?: { webUrl?: string } };
                          if (j.currentFolder?.webUrl) {
                            window.open(j.currentFolder.webUrl, "_blank");
                            return;
                          }
                          toast(
                            t("toast.cannot_resolve_folder", {
                              fallback: "⚠ Couldn't resolve folder link",
                            }),
                          );
                        } catch (e2: any) {
                          toast(
                            "⚠ " +
                              (e2.message ||
                                t("toast.open_failed", {
                                  fallback: "Open failed",
                                })),
                          );
                        }
                      }}
                    >
                      <ExtLink2 size={10} />
                      {t("common.open", { fallback: "Open" })}
                    </button>
                    {_canLinkFolder && (
                      <>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{
                            padding: "2px 6px",
                            fontSize: FS.s10,
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                          title={t("modal.case.relink_folder", {
                            fallback: "Re-link to a different folder",
                          })}
                          onClick={async () => {
                            try {
                              const picked = await openOneDrivePicker({
                                mode: "link",
                                ccId: f.id || null,
                              });
                              if (picked?.webUrl && picked.isFolder) {
                                const parentPath = picked.raw?.parentReference
                                  ?.path
                                  ? picked.raw.parentReference.path.replace(
                                      /^.*?root:/,
                                      "",
                                    )
                                  : "";
                                uf(
                                  "caseRootFolder",
                                  (parentPath ? parentPath + "/" : "/") +
                                    picked.name,
                                );
                              }
                            } catch (e2: any) {
                              toast(
                                "⚠ " +
                                  (e2.message ||
                                    t("toast.picker_failed", {
                                      fallback: "Picker failed",
                                    })),
                              );
                            }
                          }}
                        >
                          <Pencil size={10} />
                        </button>
                        <button
                          type="button"
                          className="btn"
                          style={{
                            padding: "2px 6px",
                            fontSize: FS.s10,
                            display: "inline-flex",
                            alignItems: "center",
                            background: "rgba(248,113,113,.1)",
                            color: "var(--rd)",
                            border: "1px solid rgba(248,113,113,.3)",
                          }}
                          title={t("modal.case.unlink_folder", {
                            fallback: "Unlink folder from this case",
                          })}
                          onClick={() => uf("caseRootFolder", null)}
                        >
                          <X size={10} />
                        </button>
                      </>
                    )}
                    {!_canLinkFolder && (
                      <span
                        style={{
                          fontSize: FS.s9_5,
                          color: "var(--tm)",
                          fontStyle: "italic",
                        }}
                      >
                        {t("common.read_only", { fallback: "Read-only" })}
                      </span>
                    )}
                  </div>
                );
              }
              if (!_canEditFolder && !_canLinkFolder)
                return (
                  <span
                    style={{
                      fontSize: FS.s10,
                      color: "var(--tm)",
                      fontStyle: "italic",
                    }}
                  >
                    {t("modal.case.no_folder_no_permission", {
                      fallback:
                        "No folder linked. You don't have permission to create or link a folder.",
                    })}
                  </span>
                );
              return (
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {_canEditFolder && (
                    <AddButton
                      label={
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Folder size={10} />
                          {t("modal.case.create_case_folder", {
                            fallback: "Create Case Folder",
                          })}
                        </span>
                      }
                      onClick={async () => {
                        try {
                          // 174 §1.2 — through api(): BOTH credentials, plus
                          // the X-LexCRM a cookie-auth mutation needs. api()
                          // supplies the /api prefix.
                          const j = (await api.post(
                            "/auth/microsoft/create-case-folder",
                            { ref: f.ref, title: f.title },
                          )) as {
                            path?: string;
                            existed?: boolean;
                            name?: string;
                          };
                          uf("caseRootFolder", j.path);
                          toast(
                            j.existed
                              ? t("toast.folder.linked_to_existing", {
                                  name: j.name || "",
                                  fallback:
                                    "Linked to existing folder: " +
                                    (j.name || ""),
                                })
                              : t("toast.folder.created", {
                                  name: j.name || "",
                                  fallback:
                                    "Created new folder: " + (j.name || ""),
                                }),
                          );
                        } catch (e2: any) {
                          toast(
                            "⚠ " +
                              (e2.message ||
                                t("toast.folder_create_failed", {
                                  fallback: "Folder create failed",
                                })),
                          );
                        }
                      }}
                      style={
                        !f.ref && !f.title
                          ? { opacity: 0.5, pointerEvents: "none" }
                          : undefined
                      }
                    />
                  )}
                  {_canLinkFolder && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{
                        padding: "2px 7px",
                        fontSize: FS.s10,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                      onClick={async () => {
                        try {
                          const picked = await openOneDrivePicker({
                            mode: "link",
                            ccId: f.id || null,
                          });
                          if (picked?.webUrl && picked.isFolder) {
                            const parentPath = picked.raw?.parentReference?.path
                              ? picked.raw.parentReference.path.replace(
                                  /^.*?root:/,
                                  "",
                                )
                              : "";
                            uf(
                              "caseRootFolder",
                              (parentPath ? parentPath + "/" : "/") +
                                picked.name,
                            );
                            toast(
                              t("toast.linked", { fallback: "Linked" }) +
                                ": " +
                                picked.name,
                            );
                          } else if (picked && !picked.isFolder) {
                            toast(
                              t("toast.select_folder_not_file", {
                                fallback: "⚠ Select a folder, not a file",
                              }),
                            );
                          }
                        } catch (e2: any) {
                          toast(
                            "⚠ " +
                              (e2.message ||
                                t("toast.picker_failed", {
                                  fallback: "Picker failed",
                                })),
                          );
                        }
                      }}
                    >
                      <Link2 size={10} />
                      {t("modal.case.link_existing_folder", {
                        fallback: "Link existing folder",
                      })}
                    </button>
                  )}
                  <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                    {t("cc.no_folder_yet")}
                  </span>
                </div>
              );
            })()}
          </div>
          <MultiSelect
            l={t("case.responsible_partner", {
              fallback: "Responsible Partner",
            })}
            opts={choosableCo(co, f.responsiblePartners).filter(
              (c2: any) => (c2.role || "").toLowerCase() === "partner",
            )}
            sel={f.responsiblePartners || []}
            onChange={(v: any) => uf("responsiblePartners", v)}
          />
          <div className="field">
            <label>{t("case.desc", { fallback: "Description" })}</label>
            <textarea
              className="field-input"
              rows={3}
              value={f.desc || ""}
              onChange={(e) => uf("desc", e.target.value)}
              style={{ resize: "vertical" }}
            />
          </div>
          {/* 189 §B — the firm's own fields, after the built-in ones. */}
          <CustomFieldsBlock
            entity="cc"
            values={(f as { customFields?: Record<string, unknown> | null }).customFields}
            onChange={(v) => { uf("customFields", v); }}
          />
          {f.id && (
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 16,
                paddingTop: 14,
                paddingBottom: 2,
                borderTop: "1px solid var(--br)",
                position: "sticky",
                bottom: -12,
                background: "var(--bs)",
                zIndex: Z.stickyCell,
              }}
            >
              <button
                type="button"
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: FS.s11,
                  fontWeight: 600,
                  borderRadius: RAD.r8,
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  background:
                    f.status === "archived"
                      ? "rgba(74,124,255,.15)"
                      : "rgba(139,69,19,.15)",
                  color: f.status === "archived" ? "var(--ac)" : CT.c_b87333,
                }}
                onClick={() => {
                  const td = new Date().toISOString().slice(0, 10);
                  if (f.status === "archived") {
                    sF((p: any) => ({
                      ...p,
                      status: p._prevStatus || "active",
                      archivedDate: undefined,
                    }));
                  } else {
                    sF((p: any) => ({
                      ...p,
                      _prevStatus: p.status,
                      status: "archived",
                      archivedDate: td,
                      closedDate:
                        p.closedDate ||
                        (p.status !== "closed" ? td : p.closedDate),
                    }));
                  }
                }}
              >
                {f.status === "archived"
                  ? t("common.unarchive", { fallback: "Unarchive" })
                  : t("common.archive", { fallback: "Archive" })}
              </button>
              <button
                type="button"
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: FS.s11,
                  fontWeight: 600,
                  borderRadius: RAD.r8,
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  background: "rgba(248,113,113,.15)",
                  color: "var(--rd)",
                }}
                onClick={() => {
                  if (f.status !== "archived") {
                    toast(
                      t("toast.archive_first_to_delete", {
                        fallback:
                          "⚠ Archive the case first. Only archived cases can be deleted.",
                      }),
                    );
                    return;
                  }
                  confirmAndUndo({
                    label: `collective case ${f.ref}`,
                    onConfirm: () => {
                      del(f.id);
                      cm();
                    },
                    undo: null,
                  });
                }}
              >
                {t("common.delete_caps", { fallback: "DELETE" })}
              </button>
            </div>
          )}
        </div>
    </ModalLayout>
  );

  // Detail view
  if (did) {
    const cs = cc.find((c: any) => c.id === did);
    if (!cs) return null;
    const _isRPHere = _isRPForCC(cs.id);
    // Brief 86 §2a-2 — the same two rules as CaseView.tsx:879-897, on the
    // collective side and reading ccId. Financials are the RP's, through the
    // server's own isFinancialVisible() terms; time entries are open to every
    // collaborator who can open the matter, with the money columns still
    // scrubbed server-side.
    const canFin =
      _permFin({ ccId: cs.id }) ||
      _permCan("caseView.financials.view", { ccId: cs.id });
    const canTerms = _permCan("caseView.terms.view", { ccId: cs.id });
    const canTimeEntries = true;
    const canEditOthersTasks = _permCan("caseView.tasks.edit", { ccId: cs.id });
    const canAssignCollabs = _permCan("caseView.identity.edit", { ccId: cs.id });
    const canViewTimeline = _permCan("caseView.timeline.view", { ccId: cs.id });
    // 182 A.3 — the Notes section's bits (caseView.notes.*), the tasks shape;
    // 186 B.2 dropped the delete bit's reader (a note is archived, on the edit bit).
    const canViewNotes = _permCan("caseView.notes.view", { ccId: cs.id });
    const canCreateNotes = _permCan("caseView.notes.create", { ccId: cs.id });
    const canEditNotes = _permCan("caseView.notes.edit", { ccId: cs.id });
    const canModifyCase = _permCan("caseView.identity.edit", { ccId: cs.id });
    /* 150 §5.3 — (b) follows the markBilled KEY's own authority (CV's twin;
       see the note there). The mirror's grant arm reads caseId only — the
       server helper's own shape — so on a collective case this resolves
       admin · RP. */
    const canMarkBilled = _permBillingDelegate({ ccId: cs.id });
    // 150 §4 — InvoiceView's canSeeCards generalised to this scope (CV's twin).
    const canSeeTeCounters = !!_permViewer?.isAdmin || _isRPHere;
    // 150 §5 (d)+(g) — CV's twin.
    const canTeImportExport = _permCan("caseView.timeEntries.importExport", { ccId: cs.id });
    const canTePillColumn = _permCan("caseView.timeEntries.pillColumn", { ccId: cs.id });
    // 144 §4/§5 — the bulk pair, the canMarkBilled shape (see CaseView's twin).
    const canBulkEdit = !!_permViewer?.isAdmin || (_permCan("caseView.timeEntries.bulkEdit", { ccId: cs.id }) && _isRPHere);
    const canBulkDelete = !!_permViewer?.isAdmin || (_permCan("caseView.timeEntries.bulkDelete", { ccId: cs.id }) && _isRPHere);
    const canManageDocs = _permCan("caseView.documents.edit", { ccId: cs.id });
    const canViewDesc = _permCan("caseView.identity.view", { ccId: cs.id });
    const canViewParties = _permCan("caseView.parties.view", { ccId: cs.id });
    const canViewAgenda = _permCan("caseView.agenda.view", { ccId: cs.id });
    const canEditAgenda = _permCan("caseView.agenda.edit", { ccId: cs.id });
    // Phase 24 (fold-in H.1) — gate the CDD block like CaseView does, so it
    // doesn't mount the header/spinner before the internal null-return.
    const canViewCdd = _permCan("caseView.cdd.view", { ccId: cs.id });
    const canViewTasks = _permCan("caseView.tasks.view", { ccId: cs.id });
    const canEditTasks = _permCan("caseView.tasks.edit", { ccId: cs.id });
    const canDeleteTasks = _permCan("caseView.tasks.delete", { ccId: cs.id });
    const _assignedHere =
      !!_permViewer?.isAdmin ||
      _isRPHere ||
      (cs.caseCollaborators || []).some((x: any) => x && x.coId === currentUser);
    const canViewDocs =
      _assignedHere || _permCan("caseView.documents.view", { ccId: cs.id });
    const canDeleteDocs = _permCan("caseView.documents.delete", {
      ccId: cs.id,
    });
    const canLinkDocs = _permCan("caseView.documents.link", { ccId: cs.id });
    const canViewTime = canTimeEntries;
    const canEditTime = _permCan("caseView.timeEntries.edit", { ccId: cs.id });
    const canDeleteTime = _permCan("caseView.timeEntries.delete", {
      ccId: cs.id,
    });
    const canViewCCClients = _permCan("collectiveCase.clientList.view", {
      ccId: cs.id,
    });
    const canViewCCRetainer = _permCan("collectiveCase.retainerRounds.view", {
      ccId: cs.id,
    });
    const canViewCCDocs = _permCan("collectiveCase.clientDocuments.view", {
      ccId: cs.id,
    });
    const canContactBuilder = _permCan("collectiveCase.contactBuilder.view", {
      ccId: cs.id,
    });
    // Phase 25 — bulk-mailer: SEE vs. FIRE are separately gated.
    const canBulkMailerView = _permCan("collectiveCase.bulkMailer.view", {
      ccId: cs.id,
    });
    const canBulkMailerSend = _permCan("collectiveCase.bulkMailer.send", {
      ccId: cs.id,
    });
    const canViewCalculator = _permCan("caseView.calculator.view", {
      ccId: cs.id,
    });
    const canEditCalculator = _permCan("caseView.calculator.edit", {
      ccId: cs.id,
    });
    const jr = ju.find((j: any) => j.id === cs.juId);
    const curStg = (cs.stages || []).slice(-1)[0] || null;
    const canSeeFin = canFin;
    const rounds = cs.rounds || [];
    const links = cs.clientLinks || [];
    // C.2 — the server-computed round money for THIS cc (null while the first
    // compute is in flight or after a compute error; the previous result
    // keeps rendering while a newer compute is in flight).
    const rcd =
      roundsCalc && roundsCalc.ccId === cs.id ? roundsCalc.data : null;
    // Per-client outstanding balance for the contact-list/bulk-mailer
    // segment evaluator ({amountDue} merge value included). clIds are unique
    // across clientLinks (addClient guards; the importer merges by client).
    const rcAmountDueByClId = rcd
      ? Object.fromEntries(
          rcd.perMember.map((pm: any) => [pm.clId, pm.amountDue]),
        )
      : null;
    const updCs = (upd: any) =>
      s(
        SK.cc,
        cc.map((c: any) => (c.id === cs.id ? { ...c, ...upd } : c)),
        sCc,
      );
    /* Phase 10 — the 6 procedural CC party columns store Array<{id,roles[]}>.
   Normalise to id arrays (for legacy read sites) + {id:roles[]} maps (for the
   role pickers). cpIds/ocIds stay number[] (D5); committeeIds is untouched. */
    const _CC_OBJ_COLS = ["opIds", "ocuIds", "op2Ids"];
    const _ccPid = (e: any) => (e && typeof e === "object" ? e.id : e);
    const ccIds: Record<string, any> = {};
    const ccRoles: Record<string, any> = {};
    for (const _k of _CC_OBJ_COLS) {
      const _a = cs[_k] || [];
      ccIds[_k] = _a.map(_ccPid);
      ccRoles[_k] = Object.fromEntries(
        _a
          .filter((e: any) => e && typeof e === "object")
          .map((e: any) => [e.id, Array.isArray(e.roles) ? e.roles : []]),
      );
    }
    /* 214 §2/§3 [RULED 51–52] — in a collective case the link IS the `ocuIds` element: its parties and its
       state are written through `updCs` (the JSON column), never a route; contacts stay ids alone (D5), so a
       client's own counsel is listed with the role's word and no per-matter state here (said in the note).
       Typed aliases read once (this file's ESLint count may not rise); every arrow at most two levels deep. */
    const csL2 = cs as { ocuIds?: unknown };
    const tD2 = t as TFn;
    const pnameT = pname as NameOf;
    const lockedT = Boolean(partiesLocked);
    const ocuPool2 = ocu as readonly unknown[];
    const cpPool2 = cp as readonly unknown[];
    const opPool2 = op as readonly unknown[];
    const hasId2 = (x: unknown, id: number): x is { id: number } => !!x && typeof x === "object" && (x as { id?: unknown }).id === id;
    const ccLinks = ccLinksOf(csL2.ocuIds);
    const ccOcuLinkOf = (id: number) => linkOf(ccLinks, id);
    const ccOcuMuted = (id: number) => isSuspended(ccOcuLinkOf(id));
    const setCcCounselState = (id: number, status: string | null, date?: string) => {
      updCs({ ocuIds: ccElementsWith(csL2.ocuIds, id, status ? { status, suspendedDate: date ?? todayIso() } : { status: null }) });
    };
    const setCcCounselParties = (id: number, ids: number[]) => {
      updCs({ ocuIds: ccElementsWith(csL2.ocuIds, id, { partyIds: ids }) });
    };
    const askCcSuspend = (id: number) => {
      setSuspendDlg({ id, name: pnameT(ocuPool2.find((x) => hasId2(x, id))) });
    };
    const closeCcSuspend = () => {
      setSuspendDlg(null);
    };
    const confirmCcSuspend = (date: string) => {
      const d = suspendDlg;
      setSuspendDlg(null);
      if (d) setCcCounselState(d.id, "Suspended", date);
    };
    const ccOpIds = ccIds.opIds as number[];
    const ccPartyOpts = orderParties(opPool2
      .filter((o): o is { id: number } => !!o && typeof o === "object" && typeof (o as { id?: unknown }).id === "number" && ccOpIds.includes((o as { id: number }).id)))
      .map((o) => ({ id: o.id, name: pnameT(o) })); // 216 §4 [RULED 71] — the picker's options by name
    const lawyerWord2 = String(resolveTaxonomyLabel("lawyer", contactRoleEntries) ?? "");
    const ccOcuChipExtra = (id: number) =>
      counselChipExtra({
        link: ccOcuLinkOf(id),
        parties: ccPartyOpts,
        locked: lockedT,
        t: tD2,
        onParties: (ids) => {
          setCcCounselParties(id, ids);
        },
        onSuspend: () => {
          askCcSuspend(id);
        },
        onReactivate: () => {
          setCcCounselState(id, null);
        },
      });
    const noAction = () => undefined;
    const ccCpChipExtra = (id: number) =>
      isClientCounsel(cpPool2.find((x) => hasId2(x, id)) as { role?: unknown } | undefined)
        ? clientCounselChipExtra({ link: null, locked: true, t: tD2, roleLabel: lawyerWord2, onSuspend: noAction, onReactivate: noAction })
        : null;
    const ccInvoices = []; // TODO Gate 4: fetch invoices by ccId from /api/invoices?ccId=...
    const daysBetween = (from: any, to: any) => {
      if (!from) return 0;
      const a = new Date(from),
        b = new Date(to);
      return Math.floor(((b as any) - (a as any)) / (1000 * 60 * 60 * 24));
    };
    const today = new Date().toISOString().slice(0, 10);
    // invStatusEff, latestInvForClient, invStatusColors all removed in Phase M+3 — see scope §5.6 / §6.1. Latest-invoice status badge in the client list returns to a placeholder until Gate 4 wires the real fetch.
    // nextInvId removed in Phase M+3 — only used by the now-removed Gen PF / genFormal flows.

    return (
      <>
        <button className="btn-back" onClick={() => sDid(null)}>
          {t("cc.back_all")}
        </button>
        {suspendDlg && (
          <LinkSuspendDialog name={suspendDlg.name} t={tD2} onClose={closeCcSuspend} onConfirm={confirmCcSuspend} />
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            margin: "4px 0 12px",
            flexWrap: "wrap",
          }}
        >
          <h3 style={{ fontSize: FS.s15 }}>
            {cs.ref} — {cs.title}
          </h3>
          {cs.ctype && (
            <span
              className="tag"
              style={ctc(cs.ctype, _caseTypeEntries) || undefined}
            >
              {resolveTaxonomyLabel(cs.ctype, _caseTypeEntries)}
            </span>
          )}
          {/* 109 §5 — the identical block CaseView's header carries: status
              pill (the matter list's `.status .status-<key>` classes,
              matterListCfg.tsx:488) + the companion date. Same `case_status`
              kind (verified: `_caseStatusEntries` above is that kind), and the
              schema carries the same three dates Case does (schema.prisma
              CollectiveCase, "named EXACTLY as Case names them"). Active: no
              pill.

              140 §1.3 — and it stays identical: the prospect pill takes the
              slot while the matter is tagged, in place of the status pill, on
              CaseView's own arm (`CollectiveCase.isProspect` is unit 1's
              sibling column and 139 §2 verified it on the wire). Laurent's
              reason applies to both views word for word — once inside the
              matter, nothing else on this screen says it is not a real case
              yet. 109 §5's clean header for an ordinary active case is
              untouched. */}
          {cs.isProspect ? (
            <span className="status status-prospect">
              {t("matters.prospect.badge", { fallback: "Prospect" })}
            </span>
          ) : cs.status && cs.status !== "active" ? (
            <span
              className={`status ${({ pending: "status-pending", closed: "status-closed", archived: "status-archived" } as any)[cs.status] || "status-active"}`}
            >
              {resolveTaxonomyLabel(cs.status, _caseStatusEntries) || cs.status}
            </span>
          ) : null}
          {cs.status === "pending" && cs.pendingDate && (
            <span style={{ fontSize: FS.s10, color: "var(--am)" }}>
              {t("case.status_since", { fallback: "since" })} {fd(cs.pendingDate)}
            </span>
          )}
          {(cs.status === "closed" || cs.status === "archived") &&
            cs.closedDate && (
              <span style={{ fontSize: FS.s10, color: "var(--rd)" }}>
                {t("case.status_closed_on", { fallback: "closed" })}{" "}
                {fd(cs.closedDate)}
              </span>
            )}
          {cs.status === "archived" && cs.archivedDate && (
            <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
              {t("archive.archived_on", { fallback: "archived" })}{" "}
              {fd(cs.archivedDate)}
            </span>
          )}
          {curStg && (
            <span
              style={{
                fontSize: FS.s10,
                background: "var(--as)",
                color: "var(--ac)",
                padding: "2px 8px",
                borderRadius: RAD.r10,
                marginLeft: 2,
              }}
            >
              {t("case.current_stage", { fallback: "Current stage" })}:{" "}
              {resolveTaxonomyLabel(curStg.type, _caseStageEntries)}
              <TaxonomyOrphanMark
                value={curStg.type}
                entries={_caseStageEntries}
                t={t}
              />
            </span>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>

            {(canModifyCase ||
              (cs.responsiblePartners || []).includes(currentUser)) && (
              <button
                className="btn btn-ghost"
                style={{ padding: "3px 7px" }}
                onClick={() => om(cs)}
              >
                <Pencil size={I.s} />
              </button>
            )}
            {cs.status === "archived" ? (
              <button
                className="btn btn-danger"
                style={{ padding: "3px 7px" }}
                onClick={() =>
                  confirmAndUndo({
                    label: `collective case ${cs.ref}`,
                    onConfirm: () => del(cs.id),
                    undo: null,
                  })
                }
              >
                <Trash2 size={I.s} />
              </button>
            ) : null}
          </div>
        </div>
        {/* Two-column header */}
        <div
          className="csGrid"
          style={(() => {
            const wide = [];
            const narrow = [];
            if (canViewDesc || canViewParties)
              wide.push(
                '"' +
                  (canViewDesc ? "info" : ".") +
                  " " +
                  (canViewParties ? "rightStack" : ".") +
                  '"',
              );
            if (canViewAgenda || canViewTasks)
              wide.push(
                '"' +
                  (canViewAgenda ? "agenda" : ".") +
                  " " +
                  (canViewTasks ? "tasks" : ".") +
                  '"',
              );
            if (canViewDocs || canViewTimeline)
              wide.push('"docsAndStages docsAndStages"');
            if (canViewParties) wide.push('"emailBody emailBody"');
            // 108 §4 — the same card CaseView mounts, immediately after Emails.
            if (canViewParties) wide.push('"whatsapp whatsapp"');
            if (canViewCCClients) {
              wide.push('"ccClients ccClients"');
              narrow.push('"ccClients"');
            }
            if (canViewCCRetainer) {
              wide.push('"ccRetainer ccRetainer"');
              narrow.push('"ccRetainer"');
            }
            if (canViewCCDocs) wide.push('"docTracking docTracking"');
            if (isPartnerStrictCc || _isRPHere)
              wide.push('"collabRates collabRates"');
            if (canViewCalculator) wide.push('"calculator calculator"');
            if (canFin) wide.push('"fin fin"');
            if (canViewTime) wide.push('"time time"');
            if (canViewAgenda) narrow.push('"agenda"');
            if (canViewTasks) narrow.push('"tasks"');
            if (canViewDocs || canViewTimeline) narrow.push('"docsAndStages"');
            if (canViewParties) narrow.push('"emailBody"');
            if (canViewParties) narrow.push('"whatsapp"');
            if (canViewCCDocs) narrow.push('"docTracking"');
            if (isPartnerStrictCc || _isRPHere) narrow.push('"collabRates"');
            if (canViewCalculator) narrow.push('"calculator"');
            if (canFin) narrow.push('"fin"');
            if (canViewTime) narrow.push('"time"');
            if (canViewDesc) narrow.push('"info"');
            if (canViewParties) narrow.push('"rightStack"');
            return {
              gridTemplateAreas: wide.join(" "),
              "--narrowAreas": narrow.join(" "),
            };
          })()}
        >
          {canViewDesc && (
            <div
              className="csInfo"
              style={{
                flex: "1 1 400px",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {/* Description */}
              <CaseDescriptionCard
                cs={cs}
                showDesc={showDesc}
                setShowDesc={setShowDesc}
                sCol={sCol}
                tog={tog}
                t={t}
              />
              {/* Info & Terms */}
              {/* BRIEF 86 PHASE 2 STEP 3 — the same component CaseView mounts.
                  `canSeeFin` is passed as `canFin`: this file's own
                  `const canSeeFin = canFin` (the detail scope's, beside its
                  `canFin`) means the two names were never two values. */}
              <CaseInfoSection
                kind="cc"
                cs={cs}
                co={co}
                currentUser={currentUser}
                ag={ag}
                ju={ju}
                canFin={canSeeFin}
                canTerms={canTerms}
                isRp={_isRPHere}
                _caseStatusEntries={_caseStatusEntries}
                onCallColleague={onCallColleague}
                callActive={callActive}
                openAgendaModal={globalOm}
                nav={nav}
                sCol={sCol}
                tog={tog}
                t={t}
                te={te}
                inv={inv}
              />
            </div>
          )}

          {/* Right column: Parties */}
          <div
            className="csRightStack"
            style={{
              gridArea: "rightStack",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              minWidth: 0,
            }}
          >
            {canViewParties && (
              <div
                className="csParties"
                style={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <div className="detail-card" style={{ flex: 1, overflow: "visible" }}>
                  <div
                    className="csSecHdr"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: FS.s10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: ".5px",
                        color: "var(--tm)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        // Phase 25.1 r3#2: left zone grows so the action buttons
                        // sit centered between two equal flex-1 sides.
                        flex: "1 1 0",
                      }}
                      onClick={() => {
                        // Phase 25.1 MF5: opening Parties also auto-expands its
                        // three subcategories (Clients / Opponents / Third
                        // parties).
                        const opening = sCol("parties");
                        tog("parties");
                        if (opening)
                          setPartiesOpen((p: any) => ({
                            ...p,
                            [`${cs.id}:g1`]: true,
                            [`${cs.id}:g2`]: true,
                            [`${cs.id}:g3`]: true,
                          }));
                      }}
                    >
                      {!sCol("parties") ? (
                        <ChevronDown size={11} style={{ color: "var(--tm)" }} />
                      ) : (
                        <ChevronRight
                          size={11}
                          style={{ color: "var(--tm)" }}
                        />
                      )}
                      <span>{t("case.parties", { fallback: "Parties" })}</span>
                    </div>
                    {/* Phase 25.1 r3#2: three action buttons centered in the
                        header (between the flex-1 title and the flex-1 lock
                        slot); fixed regardless of lock visibility. */}
                    <div style={{ display: "flex", gap: 3, flex: "0 0 auto" }}>
                      {(() => {
                        const cc2 = (cs.caseCollaborators || [])
                          .map((x3: any) => co.find((m3: any) => m3.id === x3.coId))
                          .filter(Boolean);
                        /* Round 15 (C90, passing fix) — these party lists
                           were referenced from a scope that never defined
                           them (their only definitions sit in the disabled
                           `false &&` block above and in the committee
                           pane's own closure): every meeting / poll / mail
                           compose click in this header threw a
                           ReferenceError. Derived here exactly like the
                           committee pane does.
                           119 (§1c's pre-step) — the nine map-find-filters move
                           to `src/lib/matterCast.ts`. THE ID SOURCES STAY, and
                           they are not the case pane's: opponents, opposing
                           counsel and additional opponents come off `ccIds`,
                           and the clients come from `clientLinks` rather than a
                           `clIds` list. Same nine families, same pools, same
                           order, same names downstream. */
                        const {
                          cls: lc2,
                          cps: cps2,
                          ops: ops2,
                          ocs: ocs2,
                          ols: ols2,
                          exs: exs2,
                          mes: mes2,
                          ars: ars2,
                          op2s: op2s2,
                        } = matterCast(
                          {
                            clIds: (cs.clientLinks || []).map((lk3: any) => lk3.clId),
                            cpIds: cs.cpIds,
                            opIds: ccIds.opIds,
                            ocIds: cs.ocIds,
                            ocuIds: ccIds.ocuIds,
                            exIds: cs.exIds,
                            meIds: cs.meIds,
                            arIds: cs.arIds,
                            op2Ids: ccIds.op2Ids,
                          },
                          {
                            clients: cl,
                            contactPersons: cp,
                            opponents: op,
                            opponentContacts: oc,
                            opponentLawyers: ocu,
                            experts: ex,
                            mediators: me,
                            arbiters: ar,
                            additionalOpponents: op2,
                          },
                          orderParties, // 216 §4 [RULED 71] — each family by last name; the groups' own order stays (119 §5 / 122 §2)
                        );
                        const bA = () => {
                          const grps = [
                            {
                              label: t("cc.attendees.internal_team", {
                                fallback: "Internal Team",
                              }),
                              persons: cc2,
                              precheck: true,
                              /* 119 §5 — the one group whose `id` IS a
                                 collaborator id. Without this flag every row
                                 here would group as a third party, and with it
                                 on the wrong group a Client id would be read as
                                 a Collaborator id. */
                              isCollaborator: true,
                            },
                            {
                              /* 122 §4 [RULED] — IN A COLLECTIVE CASE, THIS
                                 POPULATION IS THE COMMITTEE, NOT THE CLIENTS.
                                 The full client list is a different and much
                                 larger thing, and inviting IT is explicitly not
                                 this brief (the fence): one Graph event carries
                                 every address in one `required` array
                                 (m365.js:1619-1621), which would disclose the
                                 claimant roster to every claimant. This is a
                                 LABEL on one call site and widens no population.
                                 The CCV parties pane keeps its own wording —
                                 that is a different screen and was not ruled.

                                 123 §4 — THE LITERAL KEY BECOMES THE SHARED
                                 DERIVATION. The word does not change: this
                                 builder is always a collective case, so it
                                 always asks with `true`. What changes is that
                                 it now asks the SAME function App's builder
                                 asks (`clientsFamilyKey`, lib/matterLabel), so
                                 the two routes cannot come to disagree about
                                 one matter again — which is what §4 measured
                                 them doing. */
                              label: t(clientsFamilyKey(true)),
                              persons: [...lc2, ...(cps2 || [])],
                              precheck: false,
                            },
                            {
                              label: t("case.parties.opponents"),
                              persons: [...(ops2 || []), ...(ocs2 || [])],
                              precheck: false,
                            },
                            {
                              label: t("case.parties.opp_counsel"),
                              persons: ols2 || [],
                              precheck: false,
                            },
                            {
                              label: t("case.parties.third_parties", {
                                fallback: "Third Parties",
                              }),
                              persons: [
                                ...(exs2 || []),
                                ...(mes2 || []),
                                ...(ars2 || []),
                                ...(op2s2 || []),
                              ],
                              precheck: false,
                            },
                          ];
                          /* 118 §6 — the same flatten as CaseView's, shared.
                             119 §5 — and the same GROUPING: the five entries
                             above are the cast, and §5's three names are the
                             sections for the firm's own people.
                             122 §1 — for everyone else the section is the
                             FAMILY labelled above, so this pane's committee and
                             its opponents stop arriving as one "Third parties"
                             heap. Opened from this pane there is no event yet,
                             so nothing is pre-invited and the result is the old
                             precheck exactly. */
                          return meetingRoster(
                            grps,
                            pname,
                            teamIdSet((cs.caseCollaborators || []).map((x: any) => x.coId)),
                            new Set<string>(),
                            (k) => t(PEOPLE_GROUP_KEY[k]),
                          );
                        };
                        return (
                          <>
                            <button
                              className="btn"
                              style={{
                                padding: "3px 7px",
                                background: "rgba(74,124,255,.15)",
                                color: "var(--ac)",
                                border: "1px solid rgba(74,124,255,.3)",
                              }}
                              title={t("cc.tip.schedule_meeting", {
                                fallback: "Schedule meeting",
                              })}
                              onClick={(e3) => {
                                e3.stopPropagation();
                                // A6 (brief 65) — src/lib/meetingSeed. The dead
                                // twin under the `false &&` above is NOT touched.
                                /* BRIEF 120 §3 — the collective half of the same
                                   move. `ccId`, NOT `caId`: 121's Table 2 says
                                   `caId: cs.id` "resolved against
                                   collectiveCases", but `openModal`'s new-event
                                   arm resolves `caId` against `cases` ONLY
                                   (App.tsx:6893), so a collective id arriving
                                   there finds the wrong Case row or none. The
                                   tree's own canonical cc opener is
                                   `CaseAgendaSection.tsx:274`, and this follows
                                   it. Recorded as D3-2.
                                   `bA()` still builds the roster, and it must:
                                   this pane reads clients off `clientLinks` and
                                   three families off `ccIds`, which App cannot
                                   re-derive. */
                                globalOm("ag", {
                                  ...meetingSeed(
                                    {
                                      ccId: cs.id,
                                      _caseFixed: true,
                                      _attendees: bA(),
                                      /* `openModal`'s matter arm seeds
                                         `assignedCollaborators` for a CASE only
                                         — "the cc's callers already pass their
                                         own list" (App.tsx:6880) — so this does,
                                         exactly as CaseAgendaSection.tsx:276
                                         does. Without it a collective meeting
                                         would open with "Should attend" empty
                                         where the case pane shows the team. */
                                      assignedCollaborators: (
                                        cc2 as { id?: number }[]
                                      ).map((m3) => m3.id),
                                      title: cs.title + " — " + cs.ref,
                                    },
                                    mtgDefLoc,
                                    /* 123 §3 — the firm's own first meeting
                                       type, not the literal "REU". */
                                    _meetingType,
                                    /* 124 §1 — and the mode it declares. */
                                    _meetingDefaultMode,
                                  ),
                                });
                              }}
                            >
                              <Users size={16} color="var(--tp)" />
                            </button>
                            <button
                              className="btn"
                              style={{
                                padding: "3px 7px",
                                background: "rgba(167,139,250,.15)",
                                color: "var(--pp)",
                                border: "1px solid rgba(167,139,250,.3)",
                              }}
                              title={t("cc.tip.schedule_poll", {
                                fallback: "Schedule poll",
                              })}
                              onClick={(e3) => {
                                e3.stopPropagation();
                                setDoodleDlg({
                                  caseObj: {
                                    ...cs,
                                    ref: cs.ref,
                                    title: cs.title,
                                  },
                                  attendees: bA(),
                                  title: cs.title + " — " + cs.ref,
                                });
                              }}
                            >
                              <CalendarClock size={16} color="var(--tp)" />
                            </button>
                            <button
                              className="btn"
                              style={{
                                padding: "3px 7px",
                                background: "rgba(74,222,128,.15)",
                                color: "var(--gn)",
                                border: "1px solid rgba(74,222,128,.3)",
                              }}
                              title={t("caseEmails.send_email_to_all_parties", {
                                fallback: "Send email to all parties",
                              })}
                              onClick={(e3) => {
                                e3.stopPropagation();
                                const committee = orderParties((cs.committeeIds || [])
                                  .map((id: any) => cl.find((x: any) => x.id === id))
                                  .filter(Boolean)); // 216 §4 [RULED 71]
                                /* 119 (§1c's pre-step) — THE FOURTH COPY. Same
                                   eight families and the same id sources as the
                                   committee pane's; `committee` above stays
                                   local because it resolves `committeeIds`,
                                   which is this button's own client concept and
                                   not one of the nine. */
                                const {
                                  cps: cpsX,
                                  ops: opsX,
                                  ocs: ocsX,
                                  ols: olsX,
                                  exs: exsX,
                                  mes: mesX,
                                  ars: arsX,
                                  op2s: op2sX,
                                } = matterCast(
                                  {
                                    cpIds: cs.cpIds,
                                    opIds: ccIds.opIds,
                                    ocIds: cs.ocIds,
                                    ocuIds: ccIds.ocuIds,
                                    exIds: cs.exIds,
                                    meIds: cs.meIds,
                                    arIds: cs.arIds,
                                    op2Ids: ccIds.op2Ids,
                                  },
                                  {
                                    clients: cl,
                                    contactPersons: cp,
                                    opponents: op,
                                    opponentContacts: oc,
                                    opponentLawyers: ocu,
                                    experts: ex,
                                    mediators: me,
                                    arbiters: ar,
                                    additionalOpponents: op2,
                                  },
                                  orderParties, // 216 §4 [RULED 71]
                                );
                                const pool: any[] = [];
                                const push = (p: any) => {
                                  if (p && p.email)
                                    pool.push({
                                      id: p.id,
                                      name: pname(p),
                                      email: p.email,
                                    });
                                };
                                committee.forEach(push);
                                cpsX.forEach(push);
                                opsX.forEach(push);
                                ocsX.forEach(push);
                                olsX.forEach(push);
                                exsX.forEach(push);
                                mesX.forEach(push);
                                arsX.forEach(push);
                                op2sX.forEach(push);
                                if (!pool.length) {
                                  toast?.(
                                    t("caseEmails.no_party_email", {
                                      fallback:
                                        "No party with an email on this case",
                                    }),
                                  );
                                  return;
                                }
                                const ccCols2 = (cs.caseCollaborators || [])
                                  .map((x: any) => co.find((m: any) => m.id === x.coId))
                                  .filter(
                                    (c: any) => c && c.id !== currentUser && c.email,
                                  );
                                setMailDlg({
                                  label: t("caseEmails.case_parties", {
                                    fallback: "Case parties",
                                  }),
                                  persons: pool.map((p) => ({
                                    id: p.id,
                                    name: p.name,
                                    email: p.email,
                                    checked: false,
                                  })),
                                  caseObj: cs,
                                  ccCols: ccCols2,
                                });
                              }}
                            >
                              <Mail size={16} color="var(--tp)" />
                            </button>
                          </>
                        );
                      })()}
                    </div>
                    {/* Phase 25.1 r3#2: lock pinned far-right (same place as every
                        other section's lock). Slot reserved via visibility so the
                        centered buttons never shift when the lock hides/shows. */}
                    <div
                      style={{
                        flex: "1 1 0",
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          visibility: sCol("parties") ? "hidden" : "visible",
                        }}
                      >
                        <LockButton {...({} as any)}
                          locked={partiesLocked}
                          onToggle={() => setPartiesLocked((v: any) => !v)}
                        />
                      </span>
                    </div>
                  </div>
                  {(() => {
                    const poKey = (k: any) => `${cs.id}:${k}`;
                    const poGet = (k: any, dflt: any) => {
                      const v = partiesOpen[poKey(k)];
                      return v === undefined ? dflt : v;
                    };
                    const poSet = (k: any) =>
                      setPartiesOpen((p: any) => ({
                        ...p,
                        [poKey(k)]: !poGet(k, true),
                      }));
                    const viewPartyCc = (it: any, colKey: any) => {
                      if (!setPartyDetailDlg) return;
                      setPartyDetailDlg({
                        type: colKey ? colKey.replace(/Ids$/, "") : null,
                        item: it,
                      });
                    };
                    // A8 (brief 65) — src/lib/mailDialogSeed. The guard stays
                    // HERE: `!withEmail.length` is `!seed.persons.length`, the
                    // same count, because persons IS withEmail mapped.
                    const openMailDlg2 = (label2: any, persons2: any) => {
                      const seed = mailDialogSeed(
                        label2,
                        persons2,
                        { ...cs, ref: cs.ref, title: cs.title },
                        { pname, collaborators: co, currentUser },
                      );
                      if (!seed.persons.length) return;
                      setMailDlg(seed);
                    };

                    // 216 §4 [RULED 71] — the committee's chips and its « + Add » menu in the one order (the chips read
                    // the stored committee order until now, the menu the directory's)
                    const committeeClients = orderParties((cs.committeeIds || [])
                      .map((id: any) => cl.find((x: any) => x.id === id))
                      .filter(Boolean));
                    const linkedClIds = (cs.clientLinks || []).map(
                      (x: any) => x.clId,
                    );
                    const committeePool = orderParties(cl.filter(
                      (x: any) =>
                        linkedClIds.includes(x.id) &&
                        !(cs.committeeIds || []).includes(x.id),
                    ));
                    /* 119 (§1c's pre-step) — THE THIRD COPY, and the one the
                       header's own comment points at ("Derived here exactly like
                       the committee pane does"). Eight families, not nine: this
                       pane has its own client concept (`committeeClients` /
                       `committeePool`) and never resolved `clIds`, so it passes
                       none and `cls` is simply not destructured.
                       MEASURED, because the two spellings were not identical:
                       this pane wrote `ccIds.opIds` with NO `|| []` guard where
                       the header wrote one. It is INERT — `ccIds[k]` is built as
                       `(cs[k] || []).map(_ccPid)` at :3806-3810, so all three are
                       always arrays — so taking the guarded shape changes
                       nothing observable and removes a crash that could never
                       fire. Named rather than presented as an improvement. */
                    const {
                      cps: cps2,
                      ops: ops2,
                      ocs: ocs2,
                      ols: ols2,
                      exs: exs2,
                      mes: mes2,
                      ars: ars2,
                      op2s: op2s2,
                    } = matterCast(
                      {
                        cpIds: cs.cpIds,
                        opIds: ccIds.opIds,
                        ocIds: cs.ocIds,
                        ocuIds: ccIds.ocuIds,
                        exIds: cs.exIds,
                        meIds: cs.meIds,
                        arIds: cs.arIds,
                        op2Ids: ccIds.op2Ids,
                      },
                      {
                        clients: cl,
                        contactPersons: cp,
                        opponents: op,
                        opponentContacts: oc,
                        opponentLawyers: ocu,
                        experts: ex,
                        mediators: me,
                        arbiters: ar,
                        additionalOpponents: op2,
                      },
                      orderParties, // 216 §4 [RULED 71] — the chips' sublists, the group mail and the compact column read these
                    );

                    const cnt1 = committeeClients.length + cps2.length;
                    const cnt2 = ops2.length + ols2.length + ocs2.length;
                    const cnt3 =
                      exs2.length + mes2.length + ars2.length + op2s2.length;

                    const GH2 = (
                      gkey: any,
                      dflt: any,
                      label2: any,
                      total2: any,
                      hasBorder: any,
                      mailPersons: any,
                    ) => (
                      <div
                        onClick={() => poSet(gkey)}
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "4px 0",
                          userSelect: "none",
                          ...(hasBorder
                            ? {
                                borderTop: "1px solid var(--br)",
                                marginTop: 8,
                                paddingTop: 8,
                              }
                            : {}),
                        }}
                      >
                        <span
                          style={{
                            fontSize: FS.s8,
                            color: "var(--tm)",
                            width: 10,
                            textAlign: "center",
                            flexShrink: 0,
                          }}
                        >
                          {poGet(gkey, dflt) ? "\u25BC" : "\u25B6"}
                        </span>
                        <span
                          style={{
                            fontSize: FS.s10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: ".5px",
                            color: "var(--tp)",
                            flex: 1,
                          }}
                        >
                          {label2}
                        </span>
                        {mailPersons &&
                          (() => {
                            const hasEmails = mailPersons.some(
                              (p2: any) => p2 && p2.email,
                            );
                            return (
                              <button
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: hasEmails ? "pointer" : "default",
                                  padding: "0 2px",
                                  opacity: hasEmails ? 1 : 0.25,
                                  color: "var(--tp)",
                                  flexShrink: 0,
                                }}
                                title={
                                  hasEmails
                                    ? t("cc.tip.send_email_to", {
                                        label: label2,
                                        fallback: "Send email to " + label2,
                                      })
                                    : t("cc.tip.no_email_addresses", {
                                        fallback: "No email addresses",
                                      })
                                }
                                onClick={(e2) => {
                                  e2.stopPropagation();
                                  if (hasEmails)
                                    openMailDlg2(label2, mailPersons);
                                }}
                              >
                                <Mail size={21} color="var(--tp)" />
                              </button>
                            );
                          })()}
                      </div>
                    );

                    /* Phase 10 — CcMS. When `jt` (junction type) is given, the column stores
   Array<{id,roles[]}>: MultiSelect gets an id array + role map, and onChange rebuilds
   the object array. Without `jt` (cpIds/ocIds) it stays a plain number[]. */

                    // BUG-A SWEEP — the render-scope slice CcMS (now module
                    // scope) needs. Rebuilt each render on purpose: these are
                    // the live values, and CcMS is a plain prop consumer.
                    const ccMsCtx = {
                      MultiSelect,
                      ccIds,
                      ccRoles,
                      cs,
                      updCs,
                      /* BRIEF 108 §2b-3 — the pane's removal rides the same
                         5s undo the committee chip uses. */
                      confirmAndUndo,
                      t,
                      partiesLocked,
                      poGet,
                      poSet,
                      viewPartyCc,
                      sQm,
                      sQf,
                      partyRoleEntries,
                      // 214 §2–§4 — the counsel chips' builders and the elements' link map (CcMS reads them typed)
                      ccLinks,
                      counselExtra: ccOcuChipExtra,
                      counselMuted: ccOcuMuted,
                      contactExtra: ccCpChipExtra,
                    };

                    const compactGH = (label2: any, hasBorder: any, mailPersons: any) => {
                      const hasEmails = mailPersons.some(
                        (p2: any) => p2 && p2.email,
                      );
                      return (
                        <div
                          key={label2}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "4px 0",
                            userSelect: "none",
                            ...(hasBorder
                              ? {
                                  borderTop: "1px solid var(--br)",
                                  marginTop: 8,
                                  paddingTop: 8,
                                }
                              : {}),
                          }}
                        >
                          <span style={{ width: 10, flexShrink: 0 }} />
                          <span
                            style={{
                              fontSize: FS.s10,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: ".5px",
                              color: "var(--tp)",
                              flex: 1,
                            }}
                          >
                            {label2}
                          </span>
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              cursor: hasEmails ? "pointer" : "default",
                              padding: "0 2px",
                              opacity: hasEmails ? 1 : 0.25,
                              color: "var(--tp)",
                              flexShrink: 0,
                            }}
                            title={
                              hasEmails
                                ? t("cc.tip.send_email_to", {
                                    label: label2,
                                    fallback: "Send email to " + label2,
                                  })
                                : t("cc.tip.no_email_addresses", {
                                    fallback: "No email addresses",
                                  })
                            }
                            onClick={(e2) => {
                              e2.stopPropagation();
                              if (hasEmails) openMailDlg2(label2, mailPersons);
                            }}
                          >
                            <Mail size={21} color="var(--tp)" />
                          </button>
                        </div>
                      );
                    };
                    if (sCol("parties"))
                      return (
                        <>
                          {compactGH(t("case.parties.clients"), false, [
                            ...committeeClients,
                            ...cps2,
                          ])}
                          {compactGH(t("case.parties.opponents"), true, [
                            ...ops2,
                            ...ocs2,
                            ...ols2,
                          ])}
                          {compactGH(
                            t("case.parties.third_parties", {
                              fallback: "Third Parties",
                            }),
                            true,
                            [...exs2, ...mes2, ...ars2, ...op2s2],
                          )}
                        </>
                      );

                    return (
                      <>
                        {/* Group 1: Clients */}
                        {GH2(
                          "g1",
                          cnt1 > 0,
                          t("case.parties.clients"),
                          cnt1,
                          false,
                          [...committeeClients, ...cps2],
                        )}
                        {poGet("g1", cnt1 > 0) && (
                          <>
                            {/* Committee subsection */}
                            <div style={{ marginBottom: 4 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  marginBottom: 2,
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: FS.s9_5,
                                    color: "var(--tm)",
                                    fontWeight: 500,
                                  }}
                                >
                                  {t("cc.committee.label", {
                                    fallback: "Committee",
                                  })}
                                </span>
                                {!partiesLocked &&
                                  (committeePool.length > 0 ? (
                                    /* 154B §2 — an ACTION select: its value is
                                       permanently "", the first row is the
                                       label, and picking a person adds one.
                                       `caret={false}` because "+ Add" already
                                       reads as the affordance and the box is
                                       16px tall. */
                                    <MenuSelect
                                      style={{
                                        padding: "0 6px",
                                        fontSize: FS.s9,
                                        height: 16,
                                        minHeight: 16,
                                        boxSizing: "border-box",
                                        lineHeight: "14px",
                                        width: "auto",
                                        background: "rgba(74,124,255,.15)",
                                        color: "var(--ac)",
                                        border: "1px solid rgba(74,124,255,.3)",
                                        borderRadius: RAD.r5,
                                        fontFamily: "inherit",
                                        cursor: "pointer",
                                      }}
                                      caret={false}
                                      value=""
                                      onChange={(v2) => {
                                        if (!v2) return;
                                        updCs({
                                          committeeIds: [
                                            ...(cs.committeeIds || []),
                                            +v2,
                                          ],
                                        });
                                      }}
                                      options={[
                                        [
                                          "",
                                          t("cc.committee.add_option", {
                                            fallback: "+ Add",
                                          }),
                                        ] as const,
                                        ...committeePool.map(
                                          (c2: any) =>
                                            [String(c2.id), pname(c2)] as const,
                                        ),
                                      ]}
                                    />
                                  ) : linkedClIds.length === 0 ? (
                                    <span
                                      style={{
                                        fontSize: FS.s9,
                                        color: "var(--tm)",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {t("cc.committee.link_clients_first", {
                                        fallback: "Link clients first",
                                      })}
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        fontSize: FS.s9,
                                        color: "var(--tm)",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {t("cc.committee.all_on_committee", {
                                        fallback: "All clients on committee",
                                      })}
                                    </span>
                                  ))}
                              </div>
                              {committeeClients.length > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 3,
                                    marginBottom: 3,
                                  }}
                                >
                                  {committeeClients.map((c2: any) => (
                                    <span
                                      key={c2.id}
                                      className="lib-pill"
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <span
                                        onClick={() =>
                                          setPartyDetailDlg &&
                                          setPartyDetailDlg({
                                            type: "cl",
                                            item: c2,
                                          })
                                        }
                                        style={{
                                          cursor: setPartyDetailDlg
                                            ? "pointer"
                                            : "default",
                                          padding: "0 3px",
                                        }}
                                        title={t("cc.tip.click_to_view", {
                                          fallback: "Click to view details",
                                        })}
                                      >
                                        {pname(c2)}
                                      </span>
                                      {!partiesLocked && (
                                        <button
                                          type="button"
                                          style={{
                                            marginLeft: 8,
                                            padding: 0,
                                            width: 16,
                                            height: 16,
                                            background: "rgba(248,113,113,.15)",
                                            border:
                                              "1px solid rgba(248,113,113,.4)",
                                            color: "var(--rd)",
                                            borderRadius: RAD.r4,
                                            cursor: "pointer",
                                            fontSize: FS.s11,
                                            lineHeight: "12px",
                                            fontFamily: "inherit",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                          }}
                                          title={t("cc.tip.remove_committee", {
                                            fallback: "Remove from committee",
                                          })}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            confirmAndUndo({
                                              /* BRIEF 108 §2b-2 — null skips
                                                 the confirm dialog (the
                                                 hook's own contract); the 5s
                                                 undo toast is the whole
                                                 protection. Double
                                                 protection was ruled wrong.
                                                 The label was an untranslated
                                                 literal — nothing retires. */
                                              label: null,
                                              toastName: `Removed ${pname(c2)}`,
                                              onConfirm: () =>
                                                updCs({
                                                  committeeIds: (
                                                    cs.committeeIds || []
                                                  ).filter((x: any) => x !== c2.id),
                                                }),
                                              undo: {
                                                snapshot: c2.id,
                                                restore: (snap) =>
                                                  s(
                                                    SK.cc,
                                                    ccRef.current.map((c: any) =>
                                                      c.id === cs.id
                                                        ? {
                                                            ...c,
                                                            committeeIds: [
                                                              ...(c.committeeIds ||
                                                                []),
                                                              snap,
                                                            ],
                                                          }
                                                        : c,
                                                    ),
                                                    sCc,
                                                  ),
                                              },
                                            });
                                          }}
                                        >
                                          <X size={9} />
                                        </button>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <CcMS
                              ctx={ccMsCtx}
                              field="cpIds"
                              label2={t("case.parties.client_contacts")}
                              list2={cp}
                            />
                            {committeeClients.length > 0 &&
                              cps2.length > 0 &&
                              (<ClientContactsSublist cls={committeeClients} cps={cps2} t={t} pname={pname} roleLabel={lawyerWord2} />)}
                          </>
                        )}
                        {/* Group 2: Opponents */}
                        {GH2(
                          "g2",
                          cnt2 > 0,
                          t("case.parties.opponents"),
                          cnt2,
                          true,
                          [...ops2, ...ocs2, ...ols2],
                        )}
                        {poGet("g2", cnt2 > 0) && (
                          <>
                            <CcMS
                              ctx={ccMsCtx}
                              field="opIds"
                              label2={t("case.parties.opponents")}
                              list2={op}
                              jt="opponent"
                            />
                            <CcMS
                              ctx={ccMsCtx}
                              field="ocuIds"
                              label2={t("case.parties.opp_counsel")}
                              list2={ocu}
                              jt="opponentLawyer"
                            />
                            {ops2.length > 0 &&
                              ols2.length > 0 &&
                              (<OpponentCounselSublist ops={ops2 as readonly { id: number }[]} ols={ols2 as readonly { id: number }[]} links={ccLinks} t={tD2} pname={pnameT} />)}
                            <CcMS
                              ctx={ccMsCtx}
                              field="ocIds"
                              label2={t("case.parties.opp_contacts")}
                              list2={oc}
                            />
                            {ops2.length > 0 &&
                              ocs2.length > 0 &&
                              (<OpponentContactsSublist ops={ops2} ocs={ocs2} t={t} pname={pname} />)}
                          </>
                        )}
                        {/* Group 3: Third Parties */}
                        {GH2(
                          "g3",
                          cnt3 > 0,
                          t("case.parties.third_parties"),
                          cnt3,
                          true,
                          [...exs2, ...mes2, ...ars2, ...op2s2],
                        )}
                        {poGet("g3", cnt3 > 0) && (
                          <>
                            <CcMS
                              ctx={ccMsCtx}
                              field="exIds"
                              label2={t("nav.experts")}
                              list2={ex}
                            />
                            <CcMS
                              ctx={ccMsCtx}
                              field="meIds"
                              label2={t("case.parties.mediators")}
                              list2={me}
                            />
                            <CcMS
                              ctx={ccMsCtx}
                              field="arIds"
                              label2={t("case.parties.arbiters")}
                              list2={ar}
                            />
                            <CcMS
                              ctx={ccMsCtx}
                              field="op2Ids"
                              label2={t("case.parties.other_persons")}
                              list2={op2}
                              jt="additionalOpponent"
                            />
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
            {canViewCdd && (
            <div
              className="csSec-cdd"
              style={{ minWidth: 0 }}
              data-secollapsed={sCol("cdd") ? "1" : "0"}
            >
              <SectionHeader {...({} as any)}
                title={t("case.section.cdd", {
                  fallback: "Client and case due diligence",
                })}
                collapsible
                open={!sCol("cdd")}
                onToggle={() => tog("cdd")}
              />
              <CddCaseSection
                caseRow={cs}
                co={co}
                currentUserId={currentUser}
                kind="cc"
                nav={nav}
              />
            </div>
            )}
            {/* Phase 36 (A/E) — per-case CONFID folder eliminated; folder link
                now lives per linked client in the CDD section. */}
          </div>
          {canViewAgenda && (
            <CaseAgendaSection
              kind="cc"
              covered={caseBento}
              cs={cs}
              caseStageEntries={_caseStageEntries}
              cag={(ag || [])
                .filter((a2: any) => a2.ccId === cs.id)
                .sort(
                  (a2: any, b2: any) =>
                    (new Date(a2.date) as any) - (new Date(b2.date) as any),
                )}
              co={co}
              currentUser={currentUser}
              ag={ag}
              sAg={sAg}
              s={s}
              SK={SK}
              agRef={agRef}
              coAvs={coAvs}
              evtTypes={evtTypes}
              etTag={etTag}
              evtLongestCode={evtLongestCode}
              locIcon={locIcon}
              MultiSelect={MultiSelect}
              _ITEM_LEVEL={_ITEM_LEVEL}
              itemLevelList={itemLevelList}
              evExpanded={evExpanded}
              setEvExpanded={setEvExpanded} // 110 §1h
              toggleEvExpanded={toggleEvExpanded}
              agMy={agMy}
              setAgMy={setAgMy}
              agWeek={agWeek}
              setAgWeek={setAgWeek}
              agPage={agPage}
              setAgPage={setAgPage}
              agLocked={agLocked}
              setAgLocked={setAgLocked}
              canEditAgenda={canEditAgenda}
              canEditOthersTasks={canEditOthersTasks}
              /* brief 91 §3a — the event drawer's timer column. */
              activeTimers={activeTimers}
              startTimer={startTimer}
              confirmAndUndo={confirmAndUndo}
              openModal={globalOm}
              sCol={sCol}
              tog={tog}
              t={t}
            />
          )}
          {canViewTasks && (
            /* BRIEF 86 PHASE 2 STEP 4 — the same component CaseView mounts.
               A BLOCK comment, not a JSX one: this sits inside {cond && (…)},
               which takes exactly one child. */
            <CaseTasksSection
              userMatrixQuadrant={userMatrixQuadrant}
              kind="cc"
              cs={cs} co={co} tk={tk} sTk={sTk} s={s} SK={SK}
              ca={[]} cc={cc} currentUser={currentUser} tkTypes={tkTypes}
              itemLevelList={itemLevelList} itemLevelBase={itemLevelBase}
              itemLevelOrder={itemLevelOrder} _ITEM_LEVEL={_ITEM_LEVEL}
              tkExpanded={tkExpanded} setTkExpanded={setTkExpanded}
              tkLocked={tkLocked} setTkLocked={setTkLocked}
              tkMatrix={tkMatrix} setTkMatrix={setTkMatrix}
              bentoOpen={caseBento} setBentoOpen={setCaseBento}
              tkMy={tkMy} setTkMy={setTkMy}
              tkPage={tkPage} setTkPage={setTkPage}
              tkSort={tkSort} setTkSort={setTkSort}
              tkStatusFilt={tkStatusFilt} setTkStatusFilt={setTkStatusFilt}
              tkRef={tkRef}
              canEditTasks={canEditTasks} canEditOthersTasks={canEditOthersTasks}
              _isRPHere={_isRPHere} _permViewer={_permViewer}
              activeTimers={activeTimers} startTimer={startTimer}
              confirmAndUndo={confirmAndUndo} svTkData={svTkData}
              _navCase={_navCase} nav={nav}
              onSubtaskDone={onSubtaskDone}
              openTaskModal={globalOm}
              sCol={sCol} tog={tog} t={t} toast={toast}
              TkStatusTag={TkStatusTag}
              headerStyle={{ margin: "10px 0 6px" }}
            />
          )}
          {/* AUTHORITY (155 §4): twin of CaseView's docs+stages composition —
              same shared sections, kind="cc"; a layout change lands in BOTH. */}
          {(canViewDocs || canViewTimeline) && (
            <div className="csSec-docsAndStages">
              {canViewDocs && (
                <div
                  className="csSec-gendocs"
                  style={{ flex: "1 1 400px", minWidth: 0 }}
                >
                  <CaseDocumentsSection
                    key={cs.id}
                    kind="cc"
                    collapsible
                    sectionOpen={!sCol("gendocs")}
                    onSectionToggle={() => tog("gendocs")}
                    cs={cs}
                    cl={cl}
                    op={op}
                    co={co}
                    currentUser={currentUser}
                    linkTypesList={linkTypesList}
                    stageTypesList={stageTypesList}
                    toast={toast}
                    docLocked={docLocked}
                    setDocLocked={setDocLocked}
                    canViewDocs={canViewDocs}
                    canManageDocs={canManageDocs}
                    canLinkDocs={canLinkDocs}
                    isAdmin={!!_permViewer?.isAdmin}
                    isRPHere={_isRPHere}
                    assigned={_assignedHere}
                    titleKey="cc.section.general_documents"
                    paginate={false}
                    // 140 THE RECONCILE — the CCV twin: the cc conversion has
                    // the identical gap, and the offer is one shared component
                    // that picks its finalize route by `kind`.
                    firmSettings={firmSettings}
                    onProvisionFolders={onProvisionFolders}
                    onUpdateCase={(patch: any) => updCs(patch)}
                    onDeleteLink={(lk: any) => {
                      updCs({
                        links: (cs.links || []).filter((x: any) => x.id !== lk.id),
                      });
                      toast(t("common.deleted"));
                    }}
                  />
                </div>
              )}
              {canViewTimeline && (
                <div className="csStagesCol">
                  <div
                    className="csSec-stages"
                    style={{ minWidth: 0 }}
                    data-secollapsed={sCol("stages") ? "1" : "0"}
                  >
                    {/* Procedural Stages & Documents */}
                    {(() => {
                      // A18 (brief 65) — src/lib/stageOrder.
                      const stgs = sortStagesByStart(cs.stages);
                      // 186 (e) — read once into a typed row list (the eslint ratchet).
                      const stgRows = stgs as StageRow[];
                      const allDocs = cs.docs || [];
                      /* 151 §4 — THE ALL-DOCUMENTS PANE IS RETIRED
                         [RULED, point (vii)] — the CV twin's note has the
                         full story. On this view the pane's pencil was the
                         false-comment's own opener (the inline doc modal);
                         pane, modal, saveDoc, docRow and the docMod state
                         all died together. The §3 stage-subdrawer row is
                         the one classification surface, and it finally
                         gives a collective case what the pane here never
                         did: status and stage on a document. */
                      return (
                        <>
                          <div
                            id="cc-stages"
                            className="csSecHdr"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              margin: "10px 0 6px",
                              flexWrap: "wrap",
                            }}
                          >
                            <button
                              type="button"
                              aria-expanded={!sCol("stages")}
                              title={
                                !sCol("stages")
                                  ? t("common.collapse", {
                                      fallback: "Collapse",
                                    })
                                  : t("common.expand", { fallback: "Expand" })
                              }
                              onClick={() => tog("stages")}
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                margin: 0,
                                cursor: "pointer",
                                color: "var(--tm)",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 14,
                                height: 14,
                                flexShrink: 0,
                              }}
                            >
                              {!sCol("stages") ? (
                                <ChevronDown size={11} />
                              ) : (
                                <ChevronRight size={11} />
                              )}
                            </button>
                            <h3
                              style={{
                                fontSize: FS.s12,
                                margin: 0,
                                cursor: "pointer",
                              }}
                              onClick={() => tog("stages")}
                            >
                              {t("cc.section.procedural_stages")}
                            </h3>
                            {!sCol("stages") && (
                              <div
                                style={{
                                  marginLeft: "auto",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                }}
                              >
                                {!stgLocked && (
                                  <AddButton {...({} as any)}
                                    label={t("common.add_button.add", {
                                      fallback: "Add",
                                    })}
                                    onClick={() =>
                                      globalOm("stg", {
                                        ccId: cs.id,
                                        registerNumber: getLatestRegNum(cs),
                                      })
                                    }
                                  />
                                )}
                                <LockButton {...({} as any)}
                                  locked={stgLocked}
                                  onToggle={() => setStgLocked((v) => !v)}
                                />
                              </div>
                            )}
                          </div>
                          {/* 186 (e) [RULED 186 (16)] — the shared search field,
                              and nothing else; `allStages` stays the full list.
                              Under the header like the list — the spine's CSS
                              hides both while the section is closed. */}
                          <SectionSearch value={stgQ} onChange={setStgQ} />
                          {stgRows.length === 0 && (
                            <p
                              style={{
                                fontSize: FS.s11,
                                color: "var(--tm)",
                                marginBottom: 8,
                              }}
                            >
                              {t("cc.empty.no_stages")}
                            </p>
                          )}
                          {stgRows.length > 0 && (
                            <CaseStageTimeline
                              /* 110 §6a — remount per matter: clears the
                                 timeline's own notesOpen (see the stgExp
                                 clear in the §5 effect). */
                              key={"stgtl-" + cs.id}
                              matterRow={cs} // 111 §5c
                              stages={
                                stgQ.trim()
                                  ? stgRows.filter((st) => stageMatchesQuery(st, stgQ, { caseStageEntries: _caseStageEntries, partyRoleEntries, ju }))
                                  : stgRows
                              }
                              docs={allDocs}
                              ju={ju}
                              _caseStageEntries={_caseStageEntries}
                              partyRoleEntries={partyRoleEntries}
                              events={(ag || [])
                                .filter((a2: any) => a2.ccId === cs.id)}
                              stagesOpen={stagesOpen}
                              stgExp={stgExp}
                              setStgExp={setStgExp}
                              stgLocked={stgLocked}
                              /* 151 §2 step 3 — this view pages nothing, so
                                 the full list and the rendered list coincide. */
                              allStages={stgs}
                              docTypesList={_docTypes}
                              docStatusesList={_docStatuses}
                              /* 109 §7b — the drawer's add writes through THIS
                                 view's own updCs docs path, stageId stamped.
                                 151 §4 deleted the inline doc modal and the
                                 all-documents pane; this drawer is the one
                                 entry point. */
                              onCreateStageDoc={(stageId, doc) =>
                                updCs({
                                  docs: [
                                    ...allDocs,
                                    {
                                      id: nid(
                                        allDocs.length ? allDocs : [{ id: 0 }],
                                      ),
                                      name: doc.name || doc.link,
                                      link: doc.link,
                                      /* 151 §2 (iii) — the modal infers the
                                         date (casefile: last-modified;
                                         weblink: recorded-on) and this write
                                         stores what it was handed. An empty
                                         date stays empty — never a false
                                         today. */
                                      date: doc.date || "",
                                      fileType: doc.fileType,
                                      /* 151 §2 step 3 — (iv)'s completed
                                         fields; legacy defaults where empty,
                                         byte-equal to saveDoc's own. */
                                      type: doc.type || "other",
                                      status: doc.status || "",
                                      note: doc.note || "",
                                      stageId,
                                    },
                                  ],
                                })
                              }
                              /* 151 §3 (x) — the pencil's write: the same
                                 updCs docs path, in place; stageId is the
                                 draft's. */
                              onUpdateStageDoc={(docId, doc) =>
                                updCs({
                                  docs: allDocs.map((x2: any) =>
                                    x2.id === docId
                                      ? {
                                          ...x2,
                                          name: doc.name || doc.link,
                                          link: doc.link,
                                          date: doc.date || "",
                                          fileType: doc.fileType,
                                          type: doc.type || "other",
                                          status: doc.status || "",
                                          note: doc.note || "",
                                          stageId:
                                            doc.stageId != null
                                              ? Number(doc.stageId)
                                              : null,
                                        }
                                      : x2,
                                  ),
                                })
                              }
                              /* 151 §3 (x) — the bin, in THIS view's own
                                 delete shape (delete + toast, the doomed
                                 pane's own). */
                              onDeleteStageDoc={(d2: any) => {
                                updCs({
                                  docs: allDocs.filter(
                                    (x2: any) => x2.id !== d2.id,
                                  ),
                                });
                                toast(t("common.deleted"));
                              }}
                              onSaveStageNotes={(stageId, notes) =>
                                updCs({
                                  stages: stgs.map((x2: any) =>
                                    x2.id === stageId
                                      ? { ...x2, notes: notes || "" }
                                      : x2,
                                  ),
                                })
                              }
                              oneDrive={{ ccId: cs.id }}
                              onEditStage={(st) =>
                                globalOm("stg", {
                                  ccId: cs.id,
                                  _si: st.id,
                                  ...st,
                                  juId: st.juId || "",
                                })
                              }
                              onDeleteStage={(st) =>
                                confirmAndUndo({
                                  label: "this stage",
                                  toastName: `Removed "${st.type || "stage"}"`,
                                  onConfirm: () =>
                                    updCs({
                                      stages: stgs.filter(
                                        (x2: any) => x2.id !== st.id,
                                      ),
                                    }),
                                  undo: {
                                    snapshot: st,
                                    restore: (snap) =>
                                      s(
                                        SK.cc,
                                        ccRef.current.map((c2: any) =>
                                          c2.id === cs.id
                                            ? {
                                                ...c2,
                                                stages: [
                                                  ...(c2.stages || []),
                                                  snap,
                                                ],
                                              }
                                            : c2,
                                        ),
                                        sCc,
                                      ),
                                  },
                                })
                              }
                              t={t}
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <ListLoadContext.Provider value={_tlLists}>
                    <CaseTimelineSection {...({} as any)}
                      kind="cc"
                      cs={cs}
                      ag={ag}
                      te={te}
                      co={co}
                      chatMsgs={chatMsgs}
                      mailings={bulkMailings}
                      chronicle={chronicle}
                      currentUser={currentUser}
                      canViewTimeline={canViewTimeline}
                      sectionOpen={!sCol("timeline")}
                      onSectionToggle={() => tog("timeline")}
                    />
                  </ListLoadContext.Provider>
                  {/* 182 A.3 — the contact trail beside the audit trail: the Notes
                      section (Interaction) directly after the timeline, rendered
                      only for a holder of caseView.notes.view. */}
                  {canViewNotes && (
                    <InteractionSection
                      anchorId="cc-notes"
                      ccId={cs.id}
                      viewerId={currentUser?.id}
                      isAdmin={_permViewer?.isAdmin === true}
                      canCreate={canCreateNotes}
                      canEdit={canEditNotes}
                      sectionOpen={!sCol("notes")}
                      onSectionToggle={() => tog("notes")}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {canViewParties && (
            /* BRIEF 86 PHASE 2 STEP 2 — the same component CaseView mounts.
               194 B.1b: the party list is served (86a's clientLinks mapping
               and `ccIds`' normalisation no longer feed this mount); 194 B.3:
               the matter-choice lists left with the section's own list. */
            <CaseEmailsPane
              kind="cc"
              cs={cs}
              currentUser={currentUser}
              co={co}
              toast={toast}
              firmSettings={firmSettings} /* 198 §C — the period pickers' axis input (D.3) */
              /* 184 C.2 [RULED (iii)] — the line CaseView.tsx has: the drawer's
                 "+ Add time entry on selected" opens the time modal on this
                 collective case, prefilled with the e-mail (`ccId`). */
              openLogTime={(prefill: Record<string, unknown>) => {
                globalOm("te", prefill);
              }}
              sCol={sCol}
              tog={tog}
              t={t}
              className="csSec-emailBody"
              style={{ flex: "1 1 100%", minWidth: 0 }}
            />
          )}
          {/* 194 B.0 — the WhatsApp card (108 §4) is mounted inside
              CaseEmailsPane now, the second pane of "Communication". */}
          {(isPartnerStrictCc || _isRPHere) && (
          <CaseTeamSection
            kind="cc"
            cs={cs}
            co={co}
            tk={tk}
            te={te}
            OPEN_ST={OPEN_ST}
            canTerms={canTerms}
            canAssignCollabs={canAssignCollabs}
            isPartnerStrict={isPartnerStrictCc}
            _isRPHere={_isRPHere}
            _permViewer={_permViewer}
            finCalc={ccFinCalc}
            teamOpen={teamOpen}
            setTeamOpen={setTeamOpen}
            teamViewLocked={teamViewLocked}
            setTeamViewLocked={setTeamViewLocked}
            collabLocked={collabLocked}
            setCollabLocked={setCollabLocked}
            addCoOpen={addCoOpen}
            setAddCoOpen={setAddCoOpen}
            crPage={crPage}
            setCrPage={setCrPage}
            onCollaboratorsChange={(next) =>
              updCs({ caseCollaborators: next })
            }
            onCollaboratorsRestore={(fn) =>
              s(
                SK.cc,
                ccRef.current.map((x: any) =>
                  x.id === cs.id
                    ? { ...x, caseCollaborators: fn(x.caseCollaborators || []) }
                    : x,
                ),
                sCc,
              )
            }
            confirmAndUndo={confirmAndUndo}
            t={t}
          />
          )}
          {canViewCCClients && (
            <div
              className="csSec-ccClients"
              style={{ flex: "1 1 100%", minWidth: 0 }}
              data-secollapsed={sCol("ccClients") ? "1" : "0"}
            >
              {/* Client-retainer table */}
              {(() => {
                // 141 §1 — the cycle is gone. Three values already cost three
                // blind clicks to reach Terminated; five do not cycle. The
                // pill opens the shared StatusPill menu (136 §1) instead, and
                // a pick names its destination.
                //
                // 141 §2.2 — the colours and the option rows moved to
                // `lib/linkStatus.ts` when the case's parties pane grew the
                // same control. "Same vocabulary, same colours" is one
                // definition, not two that agree today.
                // Phase 43 CORRECTION (item 3) / 141 §1 — the per-member
                // lifecycle date slot per status value. The map itself moved to
                // `collectiveSegments.mjs` beside the vocabulary at 141 §2.1:
                // it had grown a THIRD copy (this one, `clientMatters.mjs`'s
                // three-slot twin, and `CaseClient`'s own columns), and which
                // column records a value is a fact about the value. These
                // still persist in the canonical CollectiveCase.clientLinks
                // JSON — no new column here, and none is needed.
                const linkStatusDate = (lk: any) => {
                  const col = linkStatusDateCol(lk && lk.status);
                  return (col && lk[col]) || null;
                };
                const statusOptions = linkStatusOptions(t);
                // 208 §6 — an array (its members still read loosely): the list is sorted, filtered and summed through it
                const allCls: any[] = links
                  .map((lk: any) => {
                    const c = cl.find((x: any) => x.id === lk.clId);
                    return c ? { ...lk, client: c } : null;
                  })
                  .filter(Boolean);
                // 141 §1 — five counts, lifecycle order.
                const prospectN = allCls.filter(
                  (x: any) => x.status === "Prospect",
                ).length;
                const activeN = allCls.filter(
                  (x: any) => x.status === "Active",
                ).length;
                const suspN = allCls.filter(
                  (x: any) => x.status === "Suspended",
                ).length;
                const termN = allCls.filter(
                  (x: any) => x.status === "Terminated",
                ).length;
                const archN = allCls.filter(
                  (x: any) => x.status === "Archived",
                ).length;
                // 208 §7 — the table's one predicate: the status filter and the search (names and contract
                // numbers, src/lib/memberOrder.ts `memberMatches`). The counters read `allCls` whole.
                const clShown = (lk0: unknown): boolean => {
                  const lk = lk0 as { status?: string | null; contractNumbers?: unknown; client?: (NamedClient & { contractNumber?: string | null }) | null };
                  // 211 Part Eight §17 — found by THIS CASE's numbers (ruling 12)
                  return (clFilter === "all" || lk.status === clFilter) && memberMatches(lk.client, clQ, memberNumbers(lk));
                };

                // C.2 — all round money (per-cell due/paid/status, row and
                // grand totals, amountDue) is server-computed (rcd, posted
                // from this same allCls join in the same order). Rows and
                // cells read perMember by allCls INDEX — exact alignment
                // with the posted members, even under exotic data.
                const rcMemberAt = (ai: any) =>
                  (rcd && rcd.perMember && rcd.perMember[ai]) || null;

                // 141 §1 — was `toggleStatus`, the cycle's writer; the picker
                // hands the destination in, so a re-pick of the current value
                // is a no-op write nobody needs.
                // 207 §4 — a mark checked (its `?` clicked), or a value set for
                // its cell in the app: the mark goes, in one roster write.
                /* 211 Part Eight §17 [RULED 24] — THE MEMBER'S OWN REFERENCES, WRITTEN ON ITS LINK:
                   its numbers in this case (the contract cell), its own quantum (the Quantum cell —
                   emptied, it reads the client's again). One write each, the import's mark on the
                   quantum cleared in the same one (a second write from this render would undo it). */
                const setMemberNumbers = (clId: number, next: string[]) => {
                  updCs({
                    clientLinks: (links as ImportLinkRow[]).map((x) => (x.clId === clId ? withMemberNumbers(x, next) : x)),
                  });
                };
                const setMemberQuantum = (clId: number, v: number | null) => {
                  updCs({
                    clientLinks: (links as ImportLinkRow[]).map((x) => {
                      if (x.clId !== clId) return x;
                      const own = withOwnQuantum(x, v);
                      return v === null ? own : clearImportMark(own, "quantum");
                    }),
                  });
                };
                const checkMark = (clId: number, cell: string) => {
                  const cur = (links as ImportLinkRow[]).find((x) => x.clId === clId);
                  if (!cur?.importMarks || !(cell in cur.importMarks)) return;
                  updCs({
                    clientLinks: (links as ImportLinkRow[]).map((x) =>
                      x.clId === clId ? clearImportMark(x, cell) : x,
                    ),
                  });
                };
                const tMark = t as Translate;
                // One door for every `?` the list draws, so each is one call.
                interface MarkedLink { clId: number; importMarks?: Record<string, string> }
                const markBadge = (lkT: MarkedLink, cell: string) => {
                  const raw = lkT.importMarks?.[cell];
                  if (raw == null) return null;
                  return (
                    <ImportMarkBadge
                      raw={raw}
                      locked={clLocked}
                      onCheck={() => {
                        checkMark(lkT.clId, cell);
                      }}
                      t={tMark}
                    />
                  );
                };
                /* 208 §5 (the extraction, first and alone) — 168 §1e's tail, MOVED OUT of
                   `setLinkStatus` unchanged, so the add window's « register as client » can run
                   the same promotion rather than a second copy of it. `wrote` is the roster
                   write it follows. */
                const promotionClearsTag = (clId: number, ns: string, wrote: unknown) => {
                  /* 168 §1e — THE PROMOTION CLEARS THE PERSON'S TAG (ruled
                     17 Aug, feasibility Q2 ruling 9: "promotion clears both").
                     Becoming an Active member of a case is the moment an
                     enquirer stops being one, so the roster write is followed
                     by §1d's verb on the CLIENT.

                     AFTER the roster write, and chained rather than fired
                     beside it: the two writes touch different rows but they
                     are one act, and a convert that landed while the roster
                     PUT was refused would untag somebody who is still a
                     Prospect. `updCs` is `s()`, which returns a promise.

                     `convertClient` and not a merged field: `isProspect` is
                     outside the client PUT's ALLOWED by design (§1d's own
                     note), so `s(SK.cl, …)` could not clear it — it would send
                     a PUT the server ignores and leave the tag standing.

                     THE REFRESH IS THE ROUTE'S OWN ANSWER, not a second fetch:
                     the verb returns the whole client and it merges into `cl`
                     exactly as `doArchiveClient` and the importer's orphan
                     archive merge theirs (`sCl(prev => prev.map(…))`, this
                     file at the Excel import).

                     IDEMPOTENT AT BOTH ENDS: the guard below skips a person
                     who carries no tag, and the route answers 200 without
                     writing if one slips through — a member may be promoted
                     again long after the tag is gone.

                     The payment dialog's "Make them Active?" goes through this
                     same function, so it inherits all of it. */
                  const person = cl.find((c: any) => c.id === clId);
                  if (ns !== "Active" || !person || !person.isProspect) return;
                  Promise.resolve(wrote)
                    .then(() => convertClient(clId))
                    .then((updated: any) => {
                      sCl((prev: any) =>
                        Array.isArray(prev)
                          ? prev.map((c: any) =>
                              c.id === clId ? { ...c, ...updated } : c,
                            )
                          : prev,
                      );
                    })
                    .catch((e: any) => {
                      // The membership DID change; only the person's own tag
                      // did not. Saying which is what makes the toast useful.
                      toast(
                        t("cc.member.prospect_tag_not_cleared", {
                          msg: e?.message || "unknown error",
                          fallback:
                            "⚠ Member set to Active, but this person is still marked as a prospect: {msg}",
                        }),
                      );
                    });
                };

                const setLinkStatus = (clId: number, ns: string) => {
                  const cur = links.find((x: any) => x.clId === clId);
                  if (!cur || cur.status === ns) return;
                  // Phase 43 CORRECTION (item 3) — stamp the entered status's
                  // lifecycle date at the transition (today), keeping any date
                  // already set (parity with regular-case companion dates).
                  const td = new Date().toISOString().slice(0, 10);
                  const wrote = updCs({
                    clientLinks: links.map((x: any) => {
                      if (x.clId !== clId) return x;
                      const col = linkStatusDateCol(ns);
                      const nx = { ...x, status: ns };
                      if (col && !nx[col]) nx[col] = td;
                      return nx;
                    }),
                  });
                  promotionClearsTag(clId, ns, wrote); // 168 §1e — see the function above
                };

                /* 168A §1d — `person0` is the row when the CALLER already holds
                   it. The picker's create hands back the server's own row from
                   `onCreatedRow`, and that row is not in this closure's `cl`
                   yet (the closure is the render's, the answer arrives after
                   it) — so a `cl.find` here would miss the tag and start a
                   just-created enquirer as Active, which is the one thing §1f
                   exists to prevent. Every other caller passes nothing and
                   reads `cl` exactly as before. */
                const addClient = (clId: any, person0?: any, status0?: "Prospect" | "Active", own?: { numbers: string[]; quantum: number | null }) => {
                  const id = +clId;
                  if (links.some((x: any) => x.clId === id)) return null;
                  const td = new Date().toISOString().slice(0, 10);
                  /* 168 §1f — A PROSPECT PERSON JOINS AS A PROSPECT MEMBER.
                     ADVISOR-PROPOSED, LAURENT DECIDES AT THE GLANCE.

                     A `Client` already carrying the prospect tag (brief 04
                     unit 3, ruling 10 — "prospect persons are Client rows
                     carrying the same kind of tag") is an enquirer, and adding
                     one to a roster as Active would state the opposite in the
                     one place the roster is read. Everyone else starts Active
                     exactly as before, so a firm that never tags a person sees
                     no change at all.

                     It is the mirror of §1e and only reachable together with
                     it: this enters the state §1e leaves, and §1e clears the
                     tag this reads. A person added as a Prospect member and
                     then promoted comes out Active AND untagged.

                     THE DATE COLUMN IS DERIVED, NOT SPELLED. `joinedDate` was
                     written literally here; a second status needs a second
                     column, and 141 §2.1 moved that map to
                     `collectiveSegments.mjs` precisely so nobody keeps two
                     copies of it in step. Prospect stamps `prospectDate`,
                     Active stamps `joinedDate` — the same answer this closure
                     already reads back through `linkStatusDate`. */
                  const person = person0 || cl.find((c: any) => c.id === id);
                  /* 208 §5 [RULED 13] — the add window names the status (its two buttons); the
                     person's tag decides only for a caller that names none. */
                  const status =
                    status0 ?? (person && person.isProspect ? "Prospect" : "Active");
                  const dateCol = linkStatusDateCol(status);
                  // 211 Part Eight §17 — the add window's numbers and quantum: this case's own, on the link
                  const link0 = {
                    clId: id,
                    status,
                    payments: [],
                    roles: [],
                    ...(dateCol ? { [dateCol]: td } : {}),
                  };
                  return updCs({
                    clientLinks: [...links, own ? withOwnQuantum(withMemberNumbers(link0, own.numbers), own.quantum) : link0],
                  });
                };

                const removeClient = (lk: any) => {
                  const c = cl.find((x: any) => x.id === lk.clId);
                  const nm = c ? pname(c) : "this client";
                  confirmAndUndo({
                    label: `client ${nm}`,
                    onConfirm: () =>
                      updCs({
                        clientLinks: links.filter((x: any) => x.clId !== lk.clId),
                      }),
                    undo: null,
                  });
                };

                /* 208 §5 [RULED 13] — A PICK FILLS THE WINDOW with that client's details, editable
                   and saved as the client form saves them (its record merged with the window's
                   fields, `type` and `country` as that form sends them — ClientModal's onSave). */
                const pickClient = (c0: unknown) => {
                  const c = c0 as PickableClient;
                  // each field AS STORED (a field left empty stays as it was: nothing is written the
                  // reader did not change); the national register as the wire carries it,
                  // `nationalRegister` (the API's `nationalRegisterId` is its write alias)
                  setNewClF({
                    type: c.type ?? "individual",
                    title: c.title,
                    firstName: c.firstName,
                    lastName: c.lastName,
                    companyName: c.companyName,
                    vat: c.vat,
                    nationalRegisterId: c.nationalRegister ?? c.nationalRegisterId,
                    /* 211 Part Eight §17 [RULED 24] — the window's numbers are this case's: empty for
                       a picked client. Its quantum is shown as the client's; left as shown, it writes
                       nothing (208 §5's rule), so what was shown is kept to compare. */
                    numbers: "",
                    quantum: c.quantum,
                    shownQuantum: c.quantum,
                    email: c.email,
                    phone: c.phone,
                    street: c.street,
                    postalCode: c.postalCode,
                    city: c.city,
                    country: c.country,
                    notes: c.notes,
                  });
                  setNewClPickId(c.id);
                };

                /* 208 §5 [RULED 13] — THE WINDOW'S TWO BUTTONS register the member with the status
                   each names — « register as prospect in this case » a Prospect member stamped with
                   today's prospect date (168 §1f), « register as client in this case » an Active one
                   with today's joined date — and the list, alphabetical (§6), puts it in its place.
                   A NEW CLIENT is created first and linked against the server's id (168A §1d, kept
                   below); one registered as a prospect is created with the prospect tag, as the
                   panel's « New prospect » did. A CLIENT OF THE FIRM, picked, is saved to its record
                   and linked.
                   CENSUS (208 §5) — what registering an existing prospect record as a CLIENT in a case
                   did to that record's own prospect mark: `Client.isProspect` is set only by the
                   create (routes/clients.js) and cleared only by `POST /clients/:id/convert`, which two
                   things call — this list's promotion of a member to Active (168 §1e: "promotion clears
                   both", `promotionClearsTag`) and the client form's own « Convert » (ClientModal); the
                   collective import and a regular case's parties never touch it. In this list, a
                   prospect record became a client in a case only through that promotion, which clears
                   the mark: KEPT — « register as client » is the same promotion and clears it the same
                   way, and « register as prospect » leaves an existing record's mark as it is. */
                const registerMember = (status: "Prospect" | "Active") => {
                  const d = newClF;
                  if (!d.lastName && !d.companyName) {
                    toast(
                      t("toast.name_required", {
                        fallback: "⚠ Name is required",
                      }),
                    );
                    return;
                  }
                  const pickId = newClPickId;
                  /* 211 Part Eight §17 [RULED 24] — the window's NUMBERS and QUANTUM are this case's,
                     on the member's link; its other fields go to the client's record as before (208 §5). */
                  const dv = d as { quantum?: unknown; shownQuantum?: unknown; numbers?: unknown };
                  const rec: Record<string, unknown> = { ...(d as Record<string, unknown>) };
                  delete rec.numbers;
                  delete rec.quantum;
                  delete rec.shownQuantum;
                  delete rec.contractNumber;
                  const amountOf = (v: unknown): number | null => (v === "" || v === null || v === undefined ? null : Number(String(v).replace(",", ".")));
                  const typedQ = amountOf(dv.quantum);
                  const shownQ = amountOf(dv.shownQuantum);
                  const own = {
                    numbers: parseNumbers(dv.numbers),
                    // a picked client's quantum left as the window showed it writes nothing
                    quantum: typedQ === null || !Number.isFinite(typedQ) || (pickId != null && shownQ !== null && Math.abs(typedQ - shownQ) < 0.005) ? null : typedQ,
                  };
                  if (pickId != null) {
                    const wrote = s(
                      SK.cl,
                      cl.map((x: any) =>
                        x.id === pickId ? { ...x, ...rec, type: d.type || "individual", country: d.country || null } : x,
                      ),
                      sCl,
                    );
                    const person = { ...cl.find((x: any) => x.id === pickId), ...rec };
                    const linked = addClient(pickId, person, status, own);
                    promotionClearsTag(pickId, status, Promise.all([wrote, linked]));
                    toast(
                      (t as Translate)("cc.member.registered", {
                        name: pname(person),
                        fallback: "{name} is on this case",
                      }),
                    );
                  } else {
                    const ni = {
                      ...rec,
                      type: d.type || "individual",
                      // 05n — was `|| "Belgium"`. A country nobody picked is not
                      // a country; the select now offers an empty option, so
                      // "none" is expressible and this stops inventing one.
                      country: d.country || null,
                      // 209 §5 [RULED 18] — none when none was chosen (it was « fr »): no language is ever assumed
                      lang: d.lang || null,
                      id: tempId(),
                      // 208 §5 — the prospect tag on the person the button registers as a prospect
                      ...(status === "Prospect" ? { isProspect: true } : {}),
                    };
                    // 211 Part Eight §17 — a new client's quantum from this window is this case's (`own`), not its record's
                    /* 168A §1d — THE LINK IS WRITTEN AGAINST THE SERVER'S ID, and
                       through `addClient` like every other add.

                       It used to be written against `nid(cl)` — a client-side
                       guess — in the same tick as the create. `POST /api/clients`
                       DELETES the body id (`clients.js:311`), so that guess never
                       persists, and 164 §A.6(a) measured exactly this for the
                       Excel importer and fixed it there with `onCreatedRow` (the
                       111 §5b seam, App.tsx:4908): the create's own answer, with
                       the row the server made. This is the same fix at the second
                       site, and it is what makes a prospect link possible at all
                       — the status comes from the row, and the row only exists
                       once the server has answered.

                       Going through `addClient` also means this link is now
                       shaped like every other one: `roles: []` and the DERIVED
                       date column (141 §2.1), where the open-coded literal here
                       wrote neither. And a create that FAILS now writes no link,
                       where before it wrote one pointing at nothing.

                       The toast moves into the callback for the same reason: it
                       says "created and linked", and until the answer arrives
                       neither is true. */
                    s(SK.cl, [...cl, ni], sCl, {
                      onCreatedRow: (row: any) => {
                        if (!row || row.id == null) return;
                        addClient(row.id, row, status, own);
                        toast(
                          t("toast.client_created_and_linked", {
                            fallback: "Client created and linked",
                          }),
                        );
                      },
                    });
                  }
                  setNewClDlg(false);
                  setNewClF({});
                  setNewClPickId(null);
                };

                const CL_IMPORT_COLS = [
                  // 164 §A.5(a) — identity travels with the export: the
                  // import matches on Id when present, by name only for
                  // files that have none.
                  "Id",
                  "Type",
                  "Title",
                  "FirstName",
                  "LastName",
                  "CompanyName",
                  "Email",
                  "Phone",
                  "Street",
                  "PostalCode",
                  "City",
                  "Country",
                  "VAT",
                  "National Register",
                  "Language",
                  "Status",
                  // 211 §18 — this case's numbers (« ; »), before « Quantum » (what the member reads here)
                  "Contract Number",
                  "Quantum",
                  "Case",
                  "Archived",
                  "Archived Date",
                  "JoinedDate",
                  "SuspendedDate",
                  "TerminatedDate",
                ];
                const clRowFor = (c: any, status: any, link: any) => ({
                  Id: c.id,
                  Type: c.type || "individual",
                  Title: c.title || "",
                  FirstName: c.firstName || "",
                  LastName: c.lastName || "",
                  CompanyName: c.companyName || "",
                  Email: c.email || "",
                  Phone: c.phone || "",
                  Street: c.street || "",
                  PostalCode: c.postalCode || "",
                  City: c.city || "",
                  Country: c.country || "",
                  VAT: c.vat || "",
                  "National Register": c.nationalRegisterId || "",
                  Language: storedClientLanguage(c), // 209 §5 [RULED 18] — what is stored, an empty cell when none (it wrote « fr », 207C §6)
                  Status: status || "Active",
                  // 211 §18 [RULED 24] — this case's numbers; the member's quantum in this case
                  "Contract Number": numbersWritten(memberNumbers(link as { contractNumbers?: unknown } | null)),
                  Quantum: quantumWritten(memberQuantum(link as { quantum?: unknown } | null, c as { quantum?: unknown })), // none: an empty cell, never a 0
                  Case: c.formerCaseRef || "",
                  Archived: c.archived ? "yes" : "",
                  "Archived Date": c.archivedAt
                    ? String(c.archivedAt).slice(0, 10)
                    : "",
                  // Phase 43 CORR item 3 — per-member lifecycle dates (round-trip
                  // with the import's Joined/Suspended/Terminated columns).
                  JoinedDate:
                    link && link.joinedDate
                      ? String(link.joinedDate).slice(0, 10)
                      : "",
                  SuspendedDate:
                    link && link.suspendedDate
                      ? String(link.suspendedDate).slice(0, 10)
                      : "",
                  TerminatedDate:
                    link && link.terminatedDate
                      ? String(link.terminatedDate).slice(0, 10)
                      : "",
                });
                const exportClientsXlsx = async () => {
                  // C.2 — export the server-computed values (the button is
                  // disabled until they land, so this guard is a backstop).
                  if (rounds.length > 0 && !rcd) return;
                  /* 216 §2 [RULED 71] — the members in the list's one order, by name (the table's default, 208 §6),
                     never the order they were added in. Paired with their index FIRST, then sorted, exactly as the
                     table does: each row keeps its index into the server's answer, its figures read there. The
                     order is the default's whatever column the table is sorted by on screen. */
                  const rows2 = sortMemberRows(
                    allCls.map((lk, ai) => ({ lk, ai })),
                    MEMBER_SORT_DEFAULT,
                    null,
                    KNOWN_STATUSES,
                  ).map(({ lk, ai: _ai }: any) => {
                    const c = lk.client;
                    const row = clRowFor(c, lk.status, lk);
                    const pm = rcMemberAt(_ai);
                    rounds.forEach((r: any, _ri: any) => {
                      const cell = pm ? pm.perRound[_ri] : null;
                      (row as any)["R" + r.roundNumber + "_due (info)"] = cell
                        ? cell.due
                        : null;
                      (row as any)["R" + r.roundNumber + "_paid (info)"] = cell
                        ? cell.paid
                        : null;
                    });
                    return row;
                  });
                  // LEGAL-LANE (round 2, Excel sweep) — guarded + reported.
                  try {
                    const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
                    const ws = XLSX.utils.json_to_sheet(rows2.map(xlsxRow));
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Clients");
                    // 164 §A.5(c) — the round definitions travel with the
                    // file, so a re-import can create missing rounds with
                    // their real formula instead of "0".
                    XLSX.utils.book_append_sheet(
                      wb,
                      XLSX.utils.aoa_to_sheet(xlsxAoa([
                        ["RoundNumber", "Label", "Formula", "DueDate"],
                        ...(rounds as {
                          roundNumber: number;
                          label?: string;
                          formula?: string;
                          dueDate?: string;
                        }[]).map((r) => [
                          r.roundNumber,
                          r.label || "",
                          r.formula || "",
                          r.dueDate || "",
                        ]),
                      ])),
                      "Rounds",
                    );
                    XLSX.writeFile(
                      wb,
                      `Clients ${(cs.ref || "cc").replace(/\//g, "-")} ${new Date().toISOString().slice(0, 10)}.xlsx`, // 181 (ruling 1)
                    );
                    toast(
                      t("toast.exported_n_clients", {
                        // The row's slot is `{count}` (its sibling caller in
                        // App.tsx passes that). Passing `n` left `{count}`
                        // unmatched, and `interpolate` returns an unmatched
                        // slot AS ITS OWN TEXT — so a seeded box has been
                        // showing "Exported {count} clients" to the user.
                        count: allCls.length,
                        fallback: "Exported {count} clients",
                      }),
                    );
                  } catch (e: any) {
                    toast(
                      t("toast.export_failed", {
                        msg: e?.message || "unknown error",
                        fallback: "⚠ Export failed: {msg}",
                      }),
                    );
                  }
                };
                const downloadClTemplate = async () => {
                  const exQ = 10000;
                  // C.2 — the example row's R<n>_due values are server-computed
                  // (one synthetic member at the example quantum).
                  let exCells: any[] = [];
                  if (rounds.length > 0) {
                    try {
                      const rr = (await computeRetainerRounds({
                        rounds,
                        members: [{ clId: 0, quantum: exQ, payments: [] }],
                        previewQuantum: exQ,
                      })) as { perMember?: { perRound?: unknown[] }[] };
                      exCells = rr?.perMember?.[0]?.perRound || [];
                    } catch (e: any) {
                      toast(
                        "⚠ " +
                          (e?.message ||
                            t("cc.toast.compute_failed", {
                              fallback: "compute failed",
                            })),
                      );
                      return;
                    }
                  }
                  const example: Record<string, any> = {
                    // 164 §A.5(a) — blank in the template: an Id belongs to
                    // an exported client, never to a hand-written row.
                    Id: "",
                    Type: "individual",
                    Title: "Mr.",
                    FirstName: "Jean",
                    LastName: "Exemple",
                    CompanyName: "",
                    Email: "jean.exemple@example.com",
                    Phone: "+32 470 12 34 56",
                    Street: "Rue de la Loi 1",
                    PostalCode: "1000",
                    City: "Bruxelles",
                    Country: "Belgium",
                    VAT: "",
                    "National Register": "85.01.01-123.45",
                    Language: "fr",
                    Status: "Active",
                    "Contract Number": "",
                    Quantum: exQ,
                    Case: "",
                    Archived: "",
                    "Archived Date": "",
                    JoinedDate: "",
                    SuspendedDate: "",
                    TerminatedDate: "",
                  };
                  const header = [...CL_IMPORT_COLS];
                  rounds.forEach((r: any, _ri: any) => {
                    const due = exCells[_ri] ? exCells[_ri].due : 0;
                    const dueK = "R" + r.roundNumber + "_due (info)";
                    const paidK = "R" + r.roundNumber + "_paid (info)";
                    example[dueK] = due;
                    example[paidK] = r.roundNumber === 1 ? due : 0;
                    header.push(dueK, paidK);
                  });
                  // LEGAL-LANE (round 2, Excel sweep) — guarded + reported.
                  try {
                    const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
                    const ws = XLSX.utils.json_to_sheet([xlsxRow(example)], { header });
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Template");
                    // 164 §A.5(c) — the template carries the Rounds sheet too.
                    XLSX.utils.book_append_sheet(
                      wb,
                      XLSX.utils.aoa_to_sheet(xlsxAoa([
                        ["RoundNumber", "Label", "Formula", "DueDate"],
                        ...(rounds as {
                          roundNumber: number;
                          label?: string;
                          formula?: string;
                          dueDate?: string;
                        }[]).map((r) => [
                          r.roundNumber,
                          r.label || "",
                          r.formula || "",
                          r.dueDate || "",
                        ]),
                      ])),
                      "Rounds",
                    );
                    XLSX.writeFile(wb, "Clients import template.xlsx"); // 181 (ruling 1)
                    toast(
                      t("toast.template_downloaded", {
                        fallback: "Template downloaded",
                      }),
                    );
                  } catch (e: any) {
                    toast(
                      t("toast.export_failed", {
                        msg: e?.message || "unknown error",
                        fallback: "⚠ Export failed: {msg}",
                      }),
                    );
                  }
                };

                return (
                  <>
                    <SectionHeader {...({} as any)}
                      title={t("case.parties.clients")}
                      collapsible
                      open={!sCol("ccClients")}
                      onToggle={() => tog("ccClients")}
                      style={{ margin: "10px 0 6px" }}
                      rightContent={
                        <>
                          {/* 208 §9 [RULED 11] — THE TITLE ROW. Unlocked: the title, then Contact list,
                              Bulk mailer, Broadcast invitation, Import Excel, Export Excel, Template,
                              + Add and the lock; locked: the title and the lock. The three mailing
                              doors show only while the list is UNLOCKED now (they showed only while it
                              was locked), each under its own right and its own wait for the server's
                              amounts. The status filters left this row for their own, under the
                              counters (below). */}
                          {!clLocked && canContactBuilder && allCls.length > 0 && (
                            <button
                              className="btn btn-ghost"
                              style={{ padding: "2px 7px", fontSize: FS.s10 }}
                              // C.2 — the payment-due axis needs the
                              // server-computed amounts; hold the door until
                              // they land (instant in practice).
                              disabled={rounds.length > 0 && !rcd}
                              onClick={() => setContactBuilderOpen(true)}
                              title={t("contactList.open_tooltip", {
                                fallback:
                                  "Filter members and build email Bcc / postal contact lists for mailings",
                              })}
                            >
                              <Mail size={10} />{" "}
                              {t("contactList.action", {
                                fallback: "Contact list",
                              })}
                            </button>
                          )}
                          {!clLocked && canBulkMailerView && allCls.length > 0 && (
                            <button
                              className="btn btn-ghost"
                              style={{ padding: "2px 7px", fontSize: FS.s10 }}
                              // C.2 — {amountDue} merge values are
                              // server-computed; hold the door until they land.
                              disabled={rounds.length > 0 && !rcd}
                              onClick={() => setBulkMailerMode("mailing")}
                              title={t("bulkMail.open_tooltip", {
                                fallback:
                                  "Send a collective bulk mailing (Bcc blast or paced individual run) and view the mailing log",
                              })}
                            >
                              <Send size={10} />{" "}
                              {t("bulkMail.action", { fallback: "Bulk mailer" })}
                            </button>
                          )}
                          {/* ── 138A §A1 — THE INVITATION GETS ITS OWN DOOR ───
                              [RULED, Laurent, 2026-08-26] The meeting block used
                              to sit inside the bulk mailer, "which is confusing
                              for secretaries". Same gate, same family, same
                              disabled condition as its neighbour — the two open
                              ONE component in two modes, so anything true of one
                              button's audience is true of the other's. */}
                          {!clLocked && canBulkMailerView && allCls.length > 0 && (
                            <button
                              className="btn btn-ghost"
                              style={{ padding: "2px 7px", fontSize: FS.s10 }}
                              disabled={rounds.length > 0 && !rcd}
                              onClick={() => setBulkMailerMode("invitation")}
                              title={t("bulkMail.broadcast_invitation_tooltip", {
                                fallback:
                                  "Announce an information meeting to the claimants — the same mailer, with the meeting first",
                              })}
                            >
                              <CalendarPlus size={10} />{" "}
                              {t("bulkMail.broadcast_invitation", {
                                fallback: "Broadcast invitation",
                              })}
                            </button>
                          )}
                          {!clLocked && (
                            <button
                              className="btn btn-ghost"
                              style={{ padding: "2px 7px", fontSize: FS.s10 }}
                              onClick={() => csvRef.current?.click()}
                            >
                              <Upload size={10} />{" "}
                              {t("cc.import.import_excel", { fallback: "Import Excel" })}
                            </button>
                          )}
                          {!clLocked && allCls.length > 0 && (
                            <button
                              className="btn btn-ghost"
                              style={{ padding: "2px 7px", fontSize: FS.s10 }}
                              // C.2 — the R<n>_due/_paid info columns are
                              // server-computed; hold the door until they land.
                              disabled={rounds.length > 0 && !rcd}
                              onClick={() => {
                                void exportClientsXlsx(); // 208 §1 — async: the Excel library loads on first use
                              }}
                            >
                              <Download size={10} />{" "}
                              {t("reports.export_excel", { fallback: "Export Excel" })}
                            </button>
                          )}
                          {!clLocked && (
                            <button
                              className="btn btn-ghost"
                              style={{ padding: "2px 7px", fontSize: FS.s10 }}
                              onClick={downloadClTemplate}
                              title={t("cc.import.template_tooltip", {
                                fallback:
                                  "Download Excel import template with correct column headers",
                              })}
                            >
                              <FileText size={10} />{" "}
                              {t("cc.import.template", { fallback: "Template" })}
                            </button>
                          )}
                          <input
                            ref={csvRef}
                            type="file"
                            accept=".xlsx,.xls"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              if (e.target.files?.[0])
                                setClImportFile(e.target.files[0]);
                              e.target.value = "";
                            }}
                          />
                          {!clLocked && (
                            <AddButton {...({} as any)}
                              label={t("cc.member.add", { fallback: "Add" })}
                              onClick={() => {
                                // 208 §4 [RULED 13] — the window at once, no panel: empty, with a
                                // new client's country (207 §13 — the firm's; nothing when it names none)
                                const firmCc = firmCountryCode(firmSettings?.firm, (v) => resolveTaxonomyEntry(v as never, _countryEntries as never));
                                setNewClF(firmCc ? { country: firmCc } : {});
                                setNewClPickId(null);
                                setNewClDlg(true);
                              }}
                            />
                          )}
                          <LockButton {...({} as any)}
                            locked={clLocked}
                            onToggle={() => setClLocked((v) => !v)}
                          />
                        </>
                      }
                    />

                    {canContactBuilder && contactBuilderOpen && (
                      <CollectiveContactListModal
                        cc={cs}
                        clients={cl}
                        pname={pname}
                        amountDueByClientId={rcAmountDueByClId}
                        toast={toast}
                        onClose={() => setContactBuilderOpen(false)}
                        onIndividualSend={(client: any) => {
                          if (!client || !client.email) return;
                          // Reuse the exact setMailDlg path the Parties
                          // handoff uses: Subject = case title, To = email.
                          setContactBuilderOpen(false);
                          // A8 (brief 65) — one recipient through the same seed;
                          // its guard is the `!client.email` return above.
                          setMailDlg(
                            mailDialogSeed(
                              pname(client),
                              [client],
                              { ...cs, ref: cs.ref, title: cs.title },
                              { pname, collaborators: co, currentUser },
                            ),
                          );
                        }}
                      />
                    )}

                    {canBulkMailerView && bulkMailerOpen && (
                      <LoadedOnUse load={loadBulkMailer} onClose={() => { setBulkMailerMode(null); }}>
                      {(m) => (
                      <m.default
                        cc={cs}
                        /* 138A §A1 — which of the two buttons opened it. */
                        mode={bulkMailerOpen}
                        clients={cl}
                        pname={pname}
                        amountDueByClientId={rcAmountDueByClId}
                        toast={toast}
                        canSend={canBulkMailerSend}
                        onClose={() => setBulkMailerMode(null)}
                        onSent={loadBulkMailings}
                      />
                      )}
                      </LoadedOnUse>
                    )}

                    {newClDlg && (
                      <ModalLayout
                        /* 208 §4 + §5 [RULED 13] — ONE WINDOW, opened by « + Add » at once: its name
                           field completes from the firm's clients (below the names), its foot carries
                           the two registrations — as a prospect on top, as a client under it. The
                           status is the button's, so the title names neither. */
                        title={(t as Translate)("cc.member.add_title", { fallback: "Add a client to this case" })}
                        onClose={() => {
                          setNewClDlg(false);
                          setNewClPickId(null);
                        }}
                        footer={
                          <div className="modal-footer" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
                            <SaveButton
                              label={(t as Translate)("cc.member.register_prospect", { fallback: "Register as prospect in this case" })}
                              onClick={() => {
                                registerMember("Prospect");
                              }}
                            />
                            <SaveButton
                              label={(t as Translate)("cc.member.register_client", { fallback: "Register as client in this case" })}
                              onClick={() => {
                                registerMember("Active");
                              }}
                            />
                          </div>
                        }
                      >
                            <div className="field">
                              <label>
                                {t("common.type", { fallback: "Type" })}
                              </label>
                              <MenuSelect
                                className="field-input"
                                value={newClF.type || "individual"}
                                onChange={(v) =>
                                  setNewClF((p: any) => ({
                                    ...p,
                                    type: v,
                                  }))
                                }
                                options={[
                                  [
                                    "individual",
                                    t("type.individual", {
                                      fallback: "Individual",
                                    }),
                                  ],
                                  [
                                    "company",
                                    t("type.company", { fallback: "Company" }),
                                  ],
                                ]}
                              />
                            </div>
                            {(newClF.type || "individual") === "company" ? (
                              <>
                                <div className="field">
                                  <label>
                                    {t("field.company_name_required", {
                                      fallback: "Company Name *",
                                    })}
                                  </label>
                                  <input
                                    className="field-input"
                                    value={newClF.companyName || ""}
                                    onChange={(e) =>
                                      setNewClF((p: any) => ({
                                        ...p,
                                        companyName: e.target.value,
                                      }))
                                    }
                                    autoFocus
                                  />
                                </div>
                                <div className="field">
                                  <label>
                                    {t("field.vat", { fallback: "VAT" })}
                                  </label>
                                  <input
                                    className="field-input"
                                    value={newClF.vat || ""}
                                    onChange={(e) =>
                                      setNewClF((p: any) => ({
                                        ...p,
                                        vat: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "80px 1fr",
                                    gap: 8,
                                  }}
                                >
                                  {/* 05n — see the hook block above. FieldSelect
                                      renders its own `.field` + <label>, so the
                                      wrapper goes with the <select> it held. */}
                                  <FieldSelect
                                    label={t("field.title", { fallback: "Title" })}
                                    value={newClF.title}
                                    onChange={(v: string) =>
                                      setNewClF((p: any) => ({ ...p, title: v }))
                                    }
                                    options={[
                                      ["", t("common.dashes", { fallback: "--" })],
                                      ...(_titles || []),
                                    ]}
                                    entries={_titleEntries}
                                    t={t}
                                  />
                                  <div className="field">
                                    <label>
                                      {t("field.first_name", {
                                        fallback: "First Name",
                                      })}
                                    </label>
                                    <input
                                      className="field-input"
                                      value={newClF.firstName || ""}
                                      onChange={(e) =>
                                        setNewClF((p: any) => ({
                                          ...p,
                                          firstName: e.target.value,
                                        }))
                                      }
                                      autoFocus
                                    />
                                  </div>
                                </div>
                                <div className="field">
                                  <label>
                                    {t("field.last_name_required", {
                                      fallback: "Last Name *",
                                    })}
                                  </label>
                                  <input
                                    className="field-input"
                                    value={newClF.lastName || ""}
                                    onChange={(e) =>
                                      setNewClF((p: any) => ({
                                        ...p,
                                        lastName: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                                <div className="field">
                                  <label>
                                    {t("field.national_register_id", {
                                      fallback: "National Register ID",
                                    })}
                                  </label>
                                  <input
                                    className="field-input"
                                    value={newClF.nationalRegisterId || ""}
                                    onChange={(e) =>
                                      setNewClF((p: any) => ({
                                        ...p,
                                        nationalRegisterId: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              </>
                            )}
                            {/* 208 §5 [RULED 13] — THE NAME FIELD COMPLETES from the firm's clients who are
                                not yet members: nothing is listed until a name is typed; accents and case
                                ignored (`memberNameMatches`), the identifier beside a name as the panel
                                showed it; a typed name that is already a member says so, as the panel
                                did. A pick fills the window (`pickClient`); a name nobody matches leaves
                                the form empty for a new client. */}
                            {(() => {
                              if (newClPickId != null)
                                return (
                                  <div
                                    className="ccAddPicked"
                                    style={{ display: "flex", alignItems: "center", gap: 6, fontSize: FS.s10, color: "var(--tm)", margin: "-2px 0 8px" }}
                                  >
                                    <span style={{ flex: 1 }}>
                                      {(t as Translate)("cc.member.picked", {
                                        fallback: "A client of the firm: what you change here is saved to this client’s record.",
                                      })}
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-ghost"
                                      style={{ padding: "0 6px", fontSize: FS.s10 }}
                                      title={(t as Translate)("cc.member.picked_clear", { fallback: "Not this client" })}
                                      onClick={() => {
                                        const firmCc = firmCountryCode(firmSettings?.firm, (v) => resolveTaxonomyEntry(v as never, _countryEntries as never));
                                        setNewClF(firmCc ? { country: firmCc } : {});
                                        setNewClPickId(null);
                                      }}
                                    >
                                      ×
                                    </button>
                                  </div>
                                );
                              const typed =
                                (newClF.type || "individual") === "company"
                                  ? String(newClF.companyName || "")
                                  : [newClF.firstName, newClF.lastName].filter(Boolean).join(" ");
                              if (!typed.trim()) return null;
                              const isMember = (id: unknown) => links.some((x: any) => x.clId === id);
                              const hits = (cl as PickableClient[]).filter((c) => !isMember(c.id) && memberNameMatches(c, typed)).slice(0, 8);
                              const dup = (cl as PickableClient[]).find((c) => isMember(c.id) && memberNameMatches(c, typed));
                              const idOf = (c: PickableClient) =>
                                (c.type ?? "").toLowerCase() === "company"
                                  ? c.bceNumber ?? c.vat ?? ""
                                  : c.nationalRegister ?? c.nationalRegisterId ?? "";
                              return (
                                <div className="ccAddHits" style={{ margin: "-2px 0 8px" }}>
                                  {dup && (
                                    <div style={{ fontSize: FS.s10, color: "var(--am)", marginBottom: 4 }}>
                                      {t("cc.member.already_member", {
                                        name: pname(dup),
                                        fallback: `${String(pname(dup))} is already a member.`,
                                      })}
                                    </div>
                                  )}
                                  {hits.length > 0 && (
                                    <div
                                      style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 180, overflowY: "auto", border: "1px solid var(--br)", borderRadius: RAD.r6, padding: 3, background: "var(--bi)" }}
                                    >
                                      {hits.map((c) => (
                                        <button
                                          key={c.id}
                                          type="button"
                                          className="btn"
                                          style={{ textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "4px 8px" }}
                                          onClick={() => {
                                            pickClient(c);
                                          }}
                                        >
                                          <span>{pname(c)}</span>
                                          {idOf(c) && (
                                            <span style={{ fontSize: FS.s9_5, color: "var(--tm)", fontFamily: "'JetBrains Mono',monospace" }}>
                                              {idOf(c)}
                                            </span>
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {/* 211 Part Eight §17 [RULED 24] — THIS CASE's numbers (« , » or « ; » between two),
                                empty for a picked client; they go on the member's link, never the client's record. */}
                            <div className="field">
                              <label>
                                {t("field.contractNumber", {
                                  fallback: "Contract Number",
                                })}
                              </label>
                              <input
                                className="field-input"
                                data-add-numbers=""
                                value={newClF.numbers || ""}
                                onChange={(e) =>
                                  setNewClF((p: any) => ({
                                    ...p,
                                    numbers: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div className="field">
                              <label>
                                {t("field.quantum", { fallback: "Quantum" })}
                              </label>
                              {/* 207C §2 [RULED, ruling 6] — the one amount field; it was
                                  `type="number"`: « 76,5 » typed was saved 765. */}
                              <AmountField
                                value={newClF.quantum}
                                empty=""
                                inputMode="decimal"
                                onAmount={(n) =>
                                  setNewClF((p: any) => ({
                                    ...p,
                                    quantum: n,
                                  }))
                                }
                                placeholder={t("modal.cc.zero_decimal_ph", {
                                  fallback: "0,00",
                                })}
                              />
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 8,
                              }}
                            >
                              <div className="field">
                                <label>
                                  {t("field.email", { fallback: "Email" })}
                                </label>
                                <input
                                  className="field-input"
                                  value={newClF.email || ""}
                                  onChange={(e) =>
                                    setNewClF((p: any) => ({
                                      ...p,
                                      email: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                              <div className="field">
                                <label>
                                  {t("field.phone", { fallback: "Phone" })}
                                </label>
                                <input
                                  className="field-input"
                                  value={newClF.phone || ""}
                                  onChange={(e) =>
                                    setNewClF((p: any) => ({
                                      ...p,
                                      phone: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                            </div>
                            <div className="field">
                              <label>
                                {t("field.street", { fallback: "Street" })}
                              </label>
                              <input
                                className="field-input"
                                value={newClF.street || ""}
                                onChange={(e) =>
                                  setNewClF((p: any) => ({
                                    ...p,
                                    street: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 8,
                              }}
                            >
                              <div className="field">
                                <label>
                                  {t("field.postal_code", {
                                    fallback: "Postal Code",
                                  })}
                                </label>
                                <input
                                  className="field-input"
                                  value={newClF.postalCode || ""}
                                  onChange={(e) =>
                                    setNewClF((p: any) => ({
                                      ...p,
                                      postalCode: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                              <div className="field">
                                <label>
                                  {t("field.city", { fallback: "City" })}
                                </label>
                                <input
                                  className="field-input"
                                  value={newClF.city || ""}
                                  onChange={(e) =>
                                    setNewClF((p: any) => ({
                                      ...p,
                                      city: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                            </div>
                            {/* 05n — see the hook block above. Seven countries,
                                hardcoded, English label as value. Now the
                                firm's own list, searchable. */}
                            <FieldSelect
                              label={t("field.country", { fallback: "Country" })}
                              value={newClF.country}
                              onChange={(v: string) =>
                                setNewClF((p: any) => ({ ...p, country: v }))
                              }
                              options={[
                                ["", `— ${t("common.none", { fallback: "None" })} —`],
                                ...(_countries || []),
                              ]}
                              searchable
                              entries={_countryEntries}
                              t={t}
                            />
                            <div className="field">
                              <label>
                                {t("field.notes", { fallback: "Notes" })}
                              </label>
                              <textarea
                                className="field-input"
                                rows={2}
                                value={newClF.notes || ""}
                                onChange={(e) =>
                                  setNewClF((p: any) => ({
                                    ...p,
                                    notes: e.target.value,
                                  }))
                                }
                                style={{ resize: "vertical" }}
                              />
                            </div>
                      </ModalLayout>
                    )}

                    {allCls.length > 0 && (
                      <div
                        /* Phase 29 (item 6d) — no inline column override: it
                           bypassed the <=900px media collapse (.sr -> 1fr 1fr).
                           208 §3 — five counters with the financial right
                           (`sr5`), two without (`sr2`: the clients and the
                           total quantum, shown wherever the quantum column is);
                           both classes keep the collapse (App.css). */
                        className={canSeeFin ? "stat-row sr5" : "stat-row sr2"}
                        style={{ marginBottom: 8 }}
                      >
                        <div className="stat-card">
                          <div className="stat-label">
                            {t("cc.col.clients", { fallback: "Clients" })}
                          </div>
                          <div className="stat-value" style={{ fontSize: FS.s16 }}>
                            {allCls.length}
                          </div>
                          <div className="stat-sub">
                            {prospectN}{" "}
                            {t("cc.summary.prosp", { fallback: "prosp." })} /{" "}
                            {activeN}{" "}
                            {t("cc.summary.active", { fallback: "active" })} /{" "}
                            {suspN}{" "}
                            {t("cc.summary.susp", { fallback: "susp." })} /{" "}
                            {termN}{" "}
                            {t("cc.summary.term", { fallback: "term." })} /{" "}
                            {archN}{" "}
                            {t("cc.summary.arch", { fallback: "arch." })}
                          </div>
                        </div>
                        {/* 208 §3 [RULED 12] — « Total quantum »: the members' quanta summed over the
                            members the other counters count (every member of the list, whatever the
                            filters), in the quantum's figure. */}
                        <div className="stat-card">
                          <div className="stat-label">
                            {(t as Translate)("cc.summary.total_quantum", { fallback: "Total Quantum" })}
                          </div>
                          <div className="stat-value" style={{ fontSize: FS.s16 }}>
                            {/* 211 Part Eight — Σ the members' quanta in this case (each its own, else its client's) */}
                            {fq((allCls as { quantum?: unknown; client?: { quantum?: number | string | null } }[]).reduce((sum, lk) => sum + (Number(memberQuantum(lk, lk.client)) || 0), 0))}
                          </div>
                        </div>
                        {canSeeFin && (
                          <>
                            <div className="stat-card">
                              <div className="stat-label">
                                {t("cc.summary.total_due", {
                                  fallback: "Total Due",
                                })}
                              </div>
                              <div className="stat-value" style={{ fontSize: FS.s16 }}>
                                {rcd ? fc(rcd.grandDue) : "—"}
                              </div>
                            </div>
                            <div className="stat-card">
                              <div className="stat-label">
                                {t("cc.summary.total_paid", {
                                  fallback: "Total Paid",
                                })}
                              </div>
                              <div
                                className="stat-value"
                                style={{ fontSize: FS.s16, color: "var(--gn)" }}
                              >
                                {rcd ? fc(rcd.grandPaid) : "—"}
                              </div>
                            </div>
                            <div className="stat-card">
                              <div className="stat-label">
                                {t("cc.summary.outstanding", {
                                  fallback: "Outstanding",
                                })}
                              </div>
                              <div
                                className="stat-value"
                                style={{
                                  fontSize: FS.s16,
                                  color:
                                    rcd && rcd.saldo > 0
                                      ? "var(--rd)"
                                      : "var(--gn)",
                                }}
                              >
                                {rcd ? fc(rcd.saldo) : "—"}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* 208 §9 [RULED 11] — THE FILTERS, under the counters and above the table:
                        the status filters (they stood in the title row). */}
                    {allCls.length > 0 && (
                      <div
                        className="ccMemberFilters"
                        style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}
                      >
                        {/* 208 §7 [RULED 12] — the search, left of the status filters */}
                        <SectionSearch value={clQ} onChange={setClQ} />
                        <SegmentedControl {...({} as any)}
                          value={clFilter}
                          options={[
                            ["all", t("common.all", { fallback: "All" })],
                            [
                              "Prospect",
                              t("cc.retainer.status.prospect", {
                                fallback: "Prospect",
                              }),
                            ],
                            [
                              "Active",
                              t("cc.retainer.status.active", {
                                fallback: "Active",
                              }),
                            ],
                            [
                              "Suspended",
                              t("cc.retainer.status.suspended", {
                                fallback: "Suspended",
                              }),
                            ],
                            [
                              "Terminated",
                              t("cc.retainer.status.terminated", {
                                fallback: "Terminated",
                              }),
                            ],
                            [
                              "Archived",
                              t("cc.retainer.status.archived", {
                                fallback: "Archived",
                              }),
                            ],
                          ]}
                          onChange={setClFilter}
                          counts={{
                            all: allCls.length,
                            Prospect: prospectN,
                            Active: activeN,
                            Suspended: suspN,
                            Terminated: termN,
                            Archived: archN,
                          }}
                        />
                      </div>
                    )}

                    {allCls.length > 0 && (
                      <div className="table-wrap">
                        <Table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: FS.s11,
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                background: "var(--bs)",
                                borderBottom: "1px solid var(--br)",
                              }}
                            >
                              <MemberTh k="status" sort={clSort} onSort={sortClBy}>
                                {t("cc.col.status", { fallback: "Status" })}
                              </MemberTh>
                              <MemberTh k="name" sort={clSort} onSort={sortClBy}>
                                {t("cc.col.client", { fallback: "Client" })}
                              </MemberTh>
                              {/* Phase 23 — contract number beside Quantum. */}
                              <MemberTh k="contract" sort={clSort} onSort={sortClBy}>
                                {t("field.contractNumber", {
                                  fallback: "Contract #",
                                })}
                              </MemberTh>
                              <MemberTh k="quantum" align="right" sort={clSort} onSort={sortClBy}>
                                {t("field.quantum", { fallback: "Quantum" })}
                              </MemberTh>
                              {canSeeFin ? (
                                <>
                                  {rounds.map((r0: any, ri: number) => {
                                    const r = r0 as { roundNumber: number; formula?: unknown }; // 208 §2 — the round read once, typed
                                    return (
                                    <MemberTh key={r.roundNumber} k={trancheSortKey(ri)} align="right" sort={clSort} onSort={sortClBy}>
                                      {/* 208 §2 [ADVISOR] — a tranche without a formula says so, in amber, where « (x0) » stood */}
                                      {formulaIsBlank(r.formula) ? (
                                        <span
                                          style={{ color: "var(--am)" }}
                                          title={(t as Translate)("cc.rounds.no_formula_tip", {
                                            section: (t as Translate)("case.retainer_rounds", { fallback: "Retainer Fee Rounds" }),
                                            fallback: "No formula: a member’s due that no file stated stays 0 until a formula is set in « {section} ».",
                                          })}
                                        >
                                          R{r.roundNumber} ({(t as Translate)("cc.rounds.no_formula", { fallback: "no formula" })})
                                        </span>
                                      ) : (
                                        <>R{r.roundNumber} ({fmtFormula(r.formula)})</>
                                      )}
                                    </MemberTh>
                                    );
                                  })}
                                  <MemberTh k="due" align="right" sort={clSort} onSort={sortClBy}>
                                    {t("cc.summary.total_due", {
                                      fallback: "Total Due",
                                    })}
                                  </MemberTh>
                                  <MemberTh k="paid" align="right" sort={clSort} onSort={sortClBy}>
                                    {t("cc.summary.total_paid", {
                                      fallback: "Total Paid",
                                    })}
                                  </MemberTh>
                                  <MemberTh k="balance" align="right" sort={clSort} onSort={sortClBy}>
                                    {t("cc.summary.balance", {
                                      fallback: "Balance",
                                    })}
                                  </MemberTh>
                                  <th
                                    style={{
                                      padding: "6px 8px",
                                      fontSize: FS.s9,
                                      fontWeight: 600,
                                      color: "var(--tm)",
                                      textTransform: "uppercase",
                                      letterSpacing: ".4px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {t("cc.col.retainer", {
                                      fallback: "Retainer",
                                    })}
                                  </th>
                                </>
                              ) : (
                                rounds.length > 0 && (
                                  <th
                                    style={{
                                      padding: "6px 8px",
                                      fontSize: FS.s9,
                                      fontWeight: 600,
                                      color: "var(--tm)",
                                      textTransform: "uppercase",
                                      letterSpacing: ".4px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {t("cc.col.rounds", { fallback: "Rounds" })}
                                  </th>
                                )
                              )}
                              <th style={{ width: 28 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortMemberRows(
                              allCls
                                // C.2 — carry the allCls index through the
                                // status filter: it addresses the member's row
                                // in the server-computed result (posted in this
                                // same order).
                                .map((lk, ai) => ({ lk, ai }))
                                .filter(({ lk }) => clShown(lk)),
                              // 208 §6 + §8 — the column clicked, else by name, A→Z (src/lib/memberOrder.ts);
                              // sorted AFTER the pairing: each row keeps its index into the server's answer,
                              // its money read there.
                              clSort,
                              (rcd as { perMember?: MemberMoney[] } | null)?.perMember,
                              KNOWN_STATUSES,
                            ).map(({ lk, ai: _ai }: any) => {
                                // 211 Part Eight — the member's quantum in this case (its own, else the client's)
                                const mq = memberQuantum(lk, lk.client);
                                const q = +(Number(mq) || 0);
                                const pm = rcMemberAt(_ai);
                                // 207 §4 — the member read typed where the marks are drawn.
                                const lkT = lk as {
                                  clId: number;
                                  importMarks?: Record<string, string>;
                                  client: { quantum?: number | string | null };
                                };
                                // B29 (brief 84) — the cells both row variants render identically,
                                // defined once per member and referenced by both branches below.
                                const memberCells = (
                                  <>
                                      <td style={{ padding: "5px 8px" }}>
                                        {/* 141 §1 — the shared pill (136 §1):
                                            inert display arm under the lock
                                            (Phase 25.1 r3#1 — a status write
                                            is unlock-only), menu arm when the
                                            roster is unlocked. Border stays
                                            off and 600 stays the weight —
                                            this pill never wore StatusPill's
                                            hairline. */}
                                        <StatusPill
                                          cfg={linkStatusCfg(t, lk.status)}
                                          current={lk.status}
                                          title={
                                            clLocked
                                              ? undefined
                                              : t(
                                                  "cc.tip.click_change_status",
                                                  {
                                                    fallback:
                                                      "Click to change status",
                                                  },
                                                )
                                          }
                                          options={
                                            clLocked
                                              ? undefined
                                              : statusOptions
                                          }
                                          onPick={
                                            clLocked
                                              ? undefined
                                              : (code: string) =>
                                                  setLinkStatus(
                                                    lk.clId,
                                                    code,
                                                  )
                                          }
                                          style={{
                                            border: "none",
                                            fontWeight: 600,
                                            fontFamily: "inherit",
                                          }}
                                        />
                                        {linkStatusDate(lk) && (
                                          <span
                                            style={{
                                              display: "block",
                                              fontSize: FS.s8,
                                              color: "var(--tm)",
                                              marginTop: 2,
                                              fontVariantNumeric: "tabular-nums",
                                            }}
                                          >
                                            {String(linkStatusDate(lk)).slice(0, 10)}
                                          </span>
                                        )}
                                      </td>
                                      <td style={{ padding: "5px 8px" }}>
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 2,
                                          }}
                                        >
                                          {/* Phase 25.1 B.6: name click opens the
                                              client-edit modal (mirrors amount →
                                              payment modal); gated behind the lock. */}
                                          <span
                                            className="name-cell"
                                            style={{
                                              cursor: !clLocked
                                                ? "pointer"
                                                : undefined,
                                              textDecoration: !clLocked
                                                ? "underline"
                                                : undefined,
                                            }}
                                            title={
                                              !clLocked
                                                ? t("cc.member.edit_client", {
                                                    fallback:
                                                      "Edit client record",
                                                  })
                                                : undefined
                                            }
                                            onClick={
                                              !clLocked
                                                ? () =>
                                                    globalOm &&
                                                    globalOm("cl", lk.client)
                                                : undefined
                                            }
                                          >
                                            {pname(lk.client)}
                                          </span>
                                          {/* Phase 23 — collective members carry no
                                              party role by design (Gate B dropped the
                                              server requirement); no RolePicker / no
                                              "role required" flag here. */}
                                        </div>
                                      </td>
                                  </>
                                );
                                if (canSeeFin) {
                                  return (
                                    <tr
                                      key={lk.clId}
                                      style={{
                                        borderBottom: "1px solid var(--br)",
                                        // Phase 25.1 r3#1: default cursor for the
                                        // row (overrides the global `tbody tr`
                                        // pointer); actionable cells re-enable the
                                        // pointer only when unlocked.
                                        cursor: "default",
                                      }}
                                    >
                                      {memberCells}
                                      {/* 211 Part Eight §17 — this case's numbers; unlocked, edited in place. */}
                                      <td
                                        style={{
                                          padding: "5px 8px",
                                          textAlign: "left",
                                        }}
                                      >
                                        <MemberNumbersCell
                                          numbers={memberNumbers(lk)}
                                          locked={!!clLocked}
                                          label={t("field.contractNumber", { fallback: "Contract Number" })}
                                          onSave={(next) => {
                                            setMemberNumbers(lk.clId, next);
                                          }}
                                        />
                                      </td>
                                      <td
                                        style={{
                                          padding: "5px 8px",
                                          textAlign: "right",
                                        }}
                                      >
                                        {/* 207C §1 [RULED 2026-09-24, ruling 5 — supersedes 207B's
                                            ruling 4] — an EMPTY quantum is an EMPTY cell with an amber
                                            border, white inside like its neighbours: no « ??? », no
                                            tint, nothing beside it. Laurent took the tinted « ??? » for
                                            a stale cell. The amber border stays while the quantum is
                                            empty or zero (`q > 0`), and a zero's figure stays amber.
                                            Typing saves and clears the import's mark as before; the
                                            mark stays in the data (207 §4).
                                            207C §2 [RULED, ruling 6] — the one amount field (the rate
                                            card's, src/components/AmountField.tsx): shown « 92 159,50 »,
                                            typed with a decimal comma and kept as typed while focused. It
                                            was redrawn from the stored number after every key, so « 76, »
                                            came back « 76 » and « 92 159,5 » typed was saved 921595. */}
                                        <AmountField
                                          className="field-input noSpin"
                                          inputMode="decimal"
                                          readOnly={clLocked}
                                          style={{
                                            width: 110,
                                            padding: "2px 5px",
                                            fontSize: FS.s11,
                                            margin: 0,
                                            textAlign: "right",
                                            fontFamily:
                                              "'JetBrains Mono',monospace",
                                            color:
                                              q > 0 ? undefined : "var(--am)",
                                            borderColor:
                                              q > 0 ? undefined : "var(--am)",
                                          }}
                                          value={mq}
                                          empty=""
                                          onAmount={(v) => {
                                            /* 211 Part Eight §17 [RULED 24] — this case's own quantum, on
                                               the member's link; it wrote the CLIENT's record. Emptied, the
                                               member reads the client's again. 207 §4 — a value set here
                                               clears the cell's mark (in the same write). */
                                            setMemberQuantum(lkT.clId, v);
                                          }}
                                        />
                                      </td>
                                      {rounds.map((r: any, _ri: any) => {
                                        const roundNo = (r as { roundNumber: number }).roundNumber; // 207 §4
                                        // C.2 — the cell money (due/paid/
                                        // error/status) is server-computed;
                                        // this maps the status CODE to its
                                        // label and colour.
                                        const cell = pm
                                          ? pm.perRound[_ri]
                                          : null;
                                        const expErr = cell
                                          ? cell.error
                                          : null;
                                        const due = cell ? cell.due : null;
                                        const paid = cell ? cell.paid : null;
                                        const ps = !cell
                                          ? ""
                                          : cell.status === "paid"
                                            ? t("cc.retainer.status.paid", {
                                                fallback: "Paid",
                                              })
                                            : cell.status === "partial"
                                              ? t(
                                                  "cc.retainer.status.partial",
                                                  { fallback: "Partial" },
                                                )
                                              : t("cc.retainer.status.unpaid", {
                                                  fallback: "Unpaid",
                                                });
                                        const pc = !cell
                                          ? "var(--tm)"
                                          : cell.status === "paid"
                                            ? "var(--gn)"
                                            : cell.status === "partial"
                                              ? "var(--am)"
                                              : "var(--rd)";
                                        // 213B §22 [RULED 50] — a stated (adapted) due may carry a note saying why.
                                        const statedCell = !!cell && !!cell.stated;
                                        const noteText = statedCell ? dueNoteOf(lk, roundNo) : "";
                                        const clIdQ = Number(lk.clId);
                                        return (
                                          <td
                                            key={r.roundNumber}
                                            style={{
                                              padding: "5px 8px",
                                              textAlign: "right",
                                              cursor:
                                                !cell || expErr || clLocked
                                                  ? "default"
                                                  : "pointer",
                                            }}
                                            onClick={() => {
                                              // Phase 25.1 C1: opening the payment
                                              // modal is a data-modification
                                              // action — only when unlocked
                                              // (and once the server-computed
                                              // amounts have landed — C.2).
                                              if (!cell || expErr || clLocked)
                                                return;
                                              // 213B §22 [RULED 50] — the window
                                              // also adapts the due: it carries the
                                              // note and the FORMULA's amount — the
                                              // cell's own due when the cell is not
                                              // stated; else asked of the engine
                                              // once more, for this member without
                                              // this round's override (never
                                              // computed here — one reduction of
                                              // money, 213B [METHOD] (a)).
                                              setPayDlg({
                                                clId: clIdQ,
                                                roundNumber: roundNo,
                                                due,
                                                paid,
                                                // 164 §A.7 — the dialog
                                                // reads the same cell.
                                                stated: statedCell,
                                                label: r.label,
                                                clientName: pname(lk.client),
                                                note: noteText,
                                                formulaDue: statedCell ? null : due,
                                              });
                                              setPayForm({
                                                amount: "",
                                                date: new Date()
                                                  .toISOString()
                                                  .slice(0, 10),
                                                reference: "",
                                              });
                                              setDueForm({
                                                amount: statedCell && due != null ? String(due) : "",
                                                note: noteText,
                                              });
                                              if (statedCell) {
                                                void computeRetainerRounds({
                                                  rounds: _rcRounds,
                                                  members: membersWithoutOverride(_rcMembers, clIdQ, roundNo),
                                                  previewQuantum: _rcSampleQ,
                                                })
                                                  .then((rr: unknown) => { setFormulaDue(dueInResult(rr, clIdQ, roundNo)); })
                                                  // the formula's amount then reads « — » in the window; the adapted due is unaffected
                                                  .catch(() => { setFormulaDue(null); });
                                              }
                                            }}
                                          >
                                            <div
                                              style={{
                                                fontFamily:
                                                  "'JetBrains Mono',monospace",
                                                fontSize: FS.s10_5,
                                                // 164 §A.7 — a STATED due
                                                // (dueByRound supplied it)
                                                // renders in the stated
                                                // colour, app-wide.
                                                color: expErr
                                                  ? "var(--rd)"
                                                  : cell && cell.stated
                                                    ? "var(--st)"
                                                    : undefined,
                                              }}
                                              title={
                                                expErr ||
                                                (cell && cell.stated
                                                  ? t(
                                                      "cc.retainer.stated_tip",
                                                      {
                                                        fallback:
                                                          "Stated amount — does not follow the round's formula",
                                                      },
                                                    )
                                                  : undefined)
                                              }
                                            >
                                              {expErr
                                                ? t("cc.retainer.expr_err", {
                                                    fallback: "— err",
                                                  })
                                                : cell
                                                  ? fq(due)
                                                  : "—"}
                                              {markBadge(lkT, "due:" + String(roundNo))}
                                              {/* 213B §22 — an adapted due WITH a note: a small note icon beside
                                                  the amount, the note on hover; without one the tip above stays
                                                  `cc.retainer.stated_tip`. */}
                                              {noteText ? (
                                                <span
                                                  data-due-note=""
                                                  title={noteText}
                                                  style={{ marginLeft: 4, color: "var(--st)", display: "inline-flex", verticalAlign: "middle" }}
                                                >
                                                  <StickyNote size={10} />
                                                </span>
                                              ) : null}
                                            </div>
                                            {cell && !expErr && (
                                              <span
                                                style={{
                                                  fontSize: FS.s8_5,
                                                  fontWeight: 600,
                                                  color: pc,
                                                }}
                                              >
                                                {ps}
                                                {paid > 0 && paid < due
                                                  ? " (" + fq(paid) + ")"
                                                  : ""}
                                              </span>
                                            )}
                                            {(paid == null || paid <= 0) &&
                                              markBadge(lkT, "paid:" + String(roundNo))}
                                          </td>
                                        );
                                      })}
                                      <td
                                        style={{
                                          padding: "5px 8px",
                                          textAlign: "right",
                                          fontFamily:
                                            "'JetBrains Mono',monospace",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {pm ? fq(pm.rowDue) : "—"}
                                      </td>
                                      <td
                                        style={{
                                          padding: "5px 8px",
                                          textAlign: "right",
                                          fontFamily:
                                            "'JetBrains Mono',monospace",
                                          color: "var(--gn)",
                                        }}
                                      >
                                        {pm ? fq(pm.rowPaid) : "—"}
                                      </td>
                                      <td
                                        style={{
                                          padding: "5px 8px",
                                          textAlign: "right",
                                          fontFamily:
                                            "'JetBrains Mono',monospace",
                                          fontWeight: 600,
                                          color:
                                            pm && pm.balance > 0
                                              ? "var(--rd)"
                                              : "var(--gn)",
                                        }}
                                      >
                                        {pm ? fq(pm.balance) : "—"}
                                      </td>
                                      <td
                                        style={{
                                          padding: "5px 8px",
                                          textAlign: "center",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: FS.s9,
                                            color: "var(--tm)",
                                          }}
                                        >
                                          —
                                        </span>
                                      </td>
                                      <td style={{ padding: "5px 4px" }}>
                                        {/* Phase 25.1 MF1: per-row edit/delete
                                            tools appear only when unlocked. */}
                                        {!clLocked && (
                                          <>
                                            {/* Phase 23 — open the member's client record for editing. */}
                                            <button
                                              className="btn btn-ghost"
                                              style={{
                                                padding: "1px 4px",
                                                fontSize: FS.s9,
                                                marginRight: 2,
                                              }}
                                              title={t("cc.member.edit_client", {
                                                fallback: "Edit client record",
                                              })}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (globalOm)
                                                  globalOm("cl", lk.client);
                                              }}
                                            >
                                              <Pencil size={I.s} />
                                            </button>
                                            <button
                                              className="btn btn-danger"
                                              style={{
                                                padding: "1px 4px",
                                                fontSize: FS.s9,
                                              }}
                                              onClick={() => removeClient(lk)}
                                            >
                                              <Trash2 size={I.s} />
                                            </button>
                                          </>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                }
                                // Non-financial user row (the paid count is
                                // server-computed with the rest — C.2)
                                const paidN = pm ? pm.paidCount : null;
                                return (
                                  <tr
                                    key={lk.clId}
                                    style={{
                                      borderBottom: "1px solid var(--br)",
                                      // Phase 25.1 r3#1: default row cursor
                                      // (overrides global `tbody tr` pointer).
                                      cursor: "default",
                                    }}
                                  >
                                    {memberCells}
                                    {/* 211 Part Eight §17 — this case's numbers; unlocked, edited in place. */}
                                    <td
                                      style={{
                                        padding: "5px 8px",
                                        textAlign: "left",
                                      }}
                                    >
                                      <MemberNumbersCell
                                        numbers={memberNumbers(lk)}
                                        locked={!!clLocked}
                                        label={t("field.contractNumber", { fallback: "Contract Number" })}
                                        onSave={(next) => {
                                          setMemberNumbers(lk.clId, next);
                                        }}
                                      />
                                    </td>
                                    <td
                                      style={{
                                        padding: "5px 8px",
                                        textAlign: "right",
                                        fontFamily:
                                          "'JetBrains Mono',monospace",
                                      }}
                                    >
                                      {/* 207C §1 [ADVISOR — Laurent overrules at the glance] — an
                                          EMPTY quantum prints « — », as the Contractnummer cell beside
                                          it prints an empty number, muted like it: `fq` answers « — »
                                          for an empty value (src/utils.ts). Never « 0,00 », a value
                                          nobody stored (207B). */}
                                      {mq == null || mq === "" ? (
                                        <span style={{ color: "var(--tm)" }}>{fq(mq as number | string | null | undefined)}</span>
                                      ) : (
                                        fq(q)
                                      )}
                                    </td>
                                    {rounds.length > 0 && (
                                      <td
                                        style={{
                                          padding: "5px 8px",
                                          textAlign: "center",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: FS.s10,
                                            color:
                                              paidN === rounds.length
                                                ? "var(--gn)"
                                                : paidN > 0
                                                  ? "var(--am)"
                                                  : "var(--tm)",
                                          }}
                                        >
                                          {paidN == null
                                            ? "—"
                                            : t("cc.retainer.rounds_paid", withCount(paidN, "paid", { total: rounds.length,
                                                fallback:
                                                  paidN +
                                                  "/" +
                                                  rounds.length +
                                                  " paid",
                                              }))}
                                        </span>
                                      </td>
                                    )}
                                    <td style={{ padding: "5px 4px" }}>
                                      {/* Phase 25.1 MF1: per-row edit/delete tools
                                          appear only when unlocked. */}
                                      {!clLocked && (
                                        <>
                                          {/* Phase 23 — open the member's client record for editing. */}
                                          <button
                                            className="btn btn-ghost"
                                            style={{
                                              padding: "1px 4px",
                                              fontSize: FS.s9,
                                              marginRight: 2,
                                            }}
                                            title={t("cc.member.edit_client", {
                                              fallback: "Edit client record",
                                            })}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (globalOm)
                                                globalOm("cl", lk.client);
                                            }}
                                          >
                                            <Pencil size={I.s} />
                                          </button>
                                          <button
                                            className="btn btn-danger"
                                            style={{
                                              padding: "1px 4px",
                                              fontSize: FS.s9,
                                            }}
                                            onClick={() => removeClient(lk)}
                                          >
                                            <Trash2 size={I.s} />
                                          </button>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            {/* 208 §7 — a search (or a status filter) that leaves no row says so */}
                            {!allCls.some(clShown) && (
                              <tr>
                                <td colSpan={99} style={{ padding: "8px", fontSize: FS.s10, color: "var(--tm)" }}>
                                  {(t as Translate)("cc.member.no_matches", { fallback: "No matching clients." })}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {canSeeFin &&
                      payDlg &&
                      (() => {
                        const lk2 = links.find((x: any) => x.clId === payDlg.clId);
                        const roundPayments = (lk2?.payments || []).filter(
                          (p: any) => p.roundNumber === payDlg.roundNumber,
                        );
                        // 213B §22 [RULED 50] — the window's state, read once, typed (the file's ESLint count may
                        // never rise): what the cell handed it, plus the note and the formula's amount.
                        const pd = payDlg as {
                          clId: number; roundNumber: number; due: number; paid: number; stated: boolean;
                          label: string; clientName: string; note?: string; formulaDue?: number | null;
                        };
                        // `t` and `toast` typed once for §22's calls (the file's own idiom, `tMark`): the count may not rise.
                        const tD = t as Translate;
                        const toastD = toast as (msg: string) => void;
                        // The adapted due and its note, written through the window's own save path — `updCs({
                        // clientLinks })`, the PUT that cent-rounds every due and keeps a note with its amount
                        // (pruneDueNotes); the import's mark on the cell cleared (207 §4). The window keeps
                        // showing what it holds: « Dû » becomes the adapted amount, in the stated colour.
                        const applyAdapted = () => {
                          const amt = parseAdaptedAmount(dueForm.amount);
                          if (amt == null) {
                            toastD(tD("toast.enter_valid_amount", { fallback: "⚠ Enter a valid amount" }));
                            return;
                          }
                          updCs({ clientLinks: linksWithAdaptedDue(links, pd.clId, pd.roundNumber, amt, dueForm.note) });
                          const noteNow = dueForm.note.trim();
                          setPayDlg({ ...pd, due: amt, stated: true, note: noteNow, formulaDue: pd.stated ? pd.formulaDue : pd.due });
                          setDueForm({ amount: String(amt), note: noteNow });
                          toastD(tD("toast.saved", { fallback: "Saved" }));
                        };
                        // « Revenir à la formule » — the amount and its note removed together (ruling 50).
                        const backToFormula = () => {
                          updCs({ clientLinks: linksWithFormulaDue(links, pd.clId, pd.roundNumber) });
                          const f = pd.formulaDue ?? pd.due;
                          setPayDlg({ ...pd, due: f, stated: false, note: "", formulaDue: f });
                          setDueForm({ amount: "", note: "" });
                          toastD(tD("toast.saved", { fallback: "Saved" }));
                        };
                        // C.2 — the live paid total is server-computed (the
                        // current result refreshes after every payment save/
                        // delete; the click-time snapshot bridges the one
                        // round-trip gap). Lookup mirrors the old
                        // links.find/filter: first match by clId, then by
                        // roundNumber.
                        const pmLive = rcd
                          ? rcd.perMember.find(
                              (x: any) => x.clId === payDlg.clId,
                            )
                          : null;
                        const cellLive = pmLive
                          ? (pmLive.perRound || []).find(
                              (x: any) =>
                                x.roundNumber === payDlg.roundNumber,
                            )
                          : null;
                        const totalPaid2 = cellLive
                          ? cellLive.paid
                          : payDlg.paid;
                        // Snapshot-due semantics preserved: the dialog keeps
                        // measuring against the due captured at cell click;
                        // both operands are server-handed money.
                        const remaining = Math.max(0, payDlg.due - totalPaid2);
                        const ps2 =
                          totalPaid2 >= payDlg.due && payDlg.due > 0
                            ? t("cc.retainer.status.paid", { fallback: "Paid" })
                            : totalPaid2 > 0
                              ? t("cc.retainer.status.partial", {
                                  fallback: "Partial",
                                })
                              : t("cc.retainer.status.unpaid", {
                                  fallback: "Unpaid",
                                });
                        const pc2 =
                          ps2 ===
                          t("cc.retainer.status.paid", { fallback: "Paid" })
                            ? "var(--gn)"
                            : ps2 ===
                                t("cc.retainer.status.partial", {
                                  fallback: "Partial",
                                })
                              ? "var(--am)"
                              : "var(--rd)";
                        const delPayment = (idx: any) => {
                          const matching = (lk2?.payments || []).filter(
                            (p: any) => p.roundNumber === payDlg.roundNumber,
                          );
                          const victim = matching[idx];
                          if (!victim) return;
                          confirmAndUndo({
                            label: "this payment",
                            toastName: `Removed payment ${fq(+victim.amount)} €`,
                            onConfirm: () =>
                              updCs({
                                clientLinks: links.map((x: any) =>
                                  x.clId === payDlg.clId
                                    ? {
                                        ...x,
                                        payments: (x.payments || []).filter(
                                          (p: any) => p !== victim,
                                        ),
                                      }
                                    : x,
                                ),
                              }),
                            undo: {
                              snapshot: { clId: payDlg.clId, payment: victim },
                              restore: (snap) =>
                                s(
                                  SK.cc,
                                  ccRef.current.map((c: any) =>
                                    c.id === cs.id
                                      ? {
                                          ...c,
                                          clientLinks: (
                                            c.clientLinks || []
                                          ).map((x: any) =>
                                            x.clId === snap.clId
                                              ? {
                                                  ...x,
                                                  payments: [
                                                    ...(x.payments || []),
                                                    snap.payment,
                                                  ],
                                                }
                                              : x,
                                          ),
                                        }
                                      : c,
                                  ),
                                  sCc,
                                ),
                            },
                          });
                        };
                        return (
                          <ModalLayout
                            title={t("cc.payments.title", {
                              fallback: "Payments",
                            })}
                            onClose={() => setPayDlg(null)}
                            onSave={() => {
                              // D3 (2026-07-13 fix pass) — cent-round at
                              // record time, like InvoiceView's recordPayment.
                              const amt =
                                Math.round(+payForm.amount * 100) / 100;
                              if (!amt || amt <= 0) {
                                toast(
                                  t("toast.enter_valid_amount", {
                                    fallback: "⚠ Enter a valid amount",
                                  }),
                                );
                                return;
                              }
                              const py = {
                                roundNumber: payDlg.roundNumber,
                                amount: amt,
                                date: payForm.date,
                                reference: payForm.reference,
                              };
                              const newLinks2 = links.map((x: any) =>
                                x.clId === payDlg.clId
                                  ? // 207 §4 — a payment recorded for the round clears its paid cell's mark.
                                    clearImportMark(
                                      {
                                        ...x,
                                        payments: [
                                          ...(x.payments || []),
                                          py,
                                        ],
                                      },
                                      "paid:" + String(py.roundNumber),
                                    )
                                  : x,
                              );
                              updCs({ clientLinks: newLinks2 });
                              setPayForm({
                                amount: "",
                                date: new Date()
                                  .toISOString()
                                  .slice(0, 10),
                                reference: "",
                              });
                              toast(
                                t("toast.payment_recorded", {
                                  fallback: "Payment recorded",
                                }),
                              );
                            }}
                            saveLabel={t("cc.payments.save_payment", {
                              fallback: "Save Payment",
                            })}
                            cancelLabel={t("common.close", { fallback: "Close" })}
                            maxWidth={420}
                          >
                                <div
                                  style={{
                                    marginBottom: 10,
                                    padding: "8px 10px",
                                    background: "var(--bi)",
                                    borderRadius: RAD.r8,
                                    border: "1px solid var(--br)",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: FS.s11_5,
                                      fontWeight: 600,
                                      color: "var(--tp)",
                                      marginBottom: 3,
                                    }}
                                  >
                                    {payDlg.clientName}
                                  </div>
                                  <div
                                    style={{ fontSize: FS.s10, color: "var(--ts)" }}
                                  >
                                    {t("cc.payments.round_label", {
                                      label: payDlg.label,
                                      n: payDlg.roundNumber,
                                      fallback:
                                        payDlg.label +
                                        " (Round " +
                                        payDlg.roundNumber +
                                        ")",
                                    })}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 12,
                                      marginTop: 6,
                                      alignItems: "center",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: FS.s10,
                                        color: "var(--tm)",
                                      }}
                                    >
                                      {t("cc.payments.due", {
                                        fallback: "Due",
                                      })}
                                      :{" "}
                                      <span
                                        className="mono"
                                        // 164 §A.7 — same cell, same colour.
                                        style={{
                                          color: payDlg.stated
                                            ? "var(--st)"
                                            : "var(--tp)",
                                        }}
                                        title={
                                          payDlg.stated
                                            ? t("cc.retainer.stated_tip", {
                                                fallback:
                                                  "Stated amount — does not follow the round's formula",
                                              })
                                            : undefined
                                        }
                                      >
                                        {fq(payDlg.due)}
                                      </span>
                                    </span>
                                    <span
                                      style={{
                                        fontSize: FS.s10,
                                        color: "var(--tm)",
                                      }}
                                    >
                                      {t("cc.payments.paid", {
                                        fallback: "Paid",
                                      })}
                                      :{" "}
                                      <span
                                        className="mono"
                                        style={{ color: pc2, fontWeight: 600 }}
                                      >
                                        {fq(totalPaid2)} / {fq(payDlg.due)} €
                                      </span>
                                    </span>
                                    <span
                                      style={{
                                        fontSize: FS.s9,
                                        fontWeight: 600,
                                        padding: "1px 6px",
                                        borderRadius: RAD.r8,
                                        background:
                                          ps2 ===
                                          t("cc.retainer.status.paid", {
                                            fallback: "Paid",
                                          })
                                            ? "var(--gs)"
                                            : ps2 ===
                                                t(
                                                  "cc.retainer.status.partial",
                                                  { fallback: "Partial" },
                                                )
                                              ? "var(--ams)"
                                              : "var(--rs)",
                                        color: pc2,
                                      }}
                                    >
                                      {ps2}
                                    </span>
                                  </div>
                                </div>

                                {/* 213B §22 [RULED 50] — « MONTANT DÛ »: the formula's amount for this member
                                    and round, a box to adapt it, why (optional [ADVISOR]), its own « Appliquer »
                                    [ADVISOR — the window's save records a payment and stays that]; « Revenir à la
                                    formule » only on an adapted due, removing the amount and its note together.
                                    Unlocked only: the window opens unlocked (the cell's onClick returns under
                                    clLocked), and the gate is said here too, for the rule's letter. */}
                                {!clLocked && (
                                  <div
                                    data-due-section=""
                                    style={{
                                      marginBottom: 10,
                                      padding: "8px 10px",
                                      background: "var(--bi)",
                                      borderRadius: RAD.r8,
                                      border: "1px solid var(--br)",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: FS.s9,
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: ".4px",
                                        color: "var(--tm)",
                                        marginBottom: 4,
                                      }}
                                    >
                                      {tD("cc.due.section", { fallback: "Amount due" })}
                                    </div>
                                    <div data-due-formula="" style={{ fontSize: FS.s10, color: "var(--ts)", marginBottom: 6 }}>
                                      {tD("cc.due.formula", {
                                        amount: pd.formulaDue == null ? "—" : fq(pd.formulaDue) + " €",
                                        fallback: "By the formula: {amount}",
                                      })}
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8 }}>
                                      <div className="field">
                                        <label>{tD("cc.due.adapted", { fallback: "Adapted amount" })}</label>
                                        <AmountField
                                          value={dueForm.amount}
                                          empty=""
                                          inputMode="decimal"
                                          onAmount={setDueAmount}
                                          placeholder={pd.formulaDue == null ? "0,00" : fq(pd.formulaDue)}
                                        />
                                      </div>
                                      <div className="field">
                                        <label>{tD("cc.due.note", { fallback: "Why" })}</label>
                                        <input
                                          data-due-note-input=""
                                          className={noAutofillClass("field-input")}
                                          {...NO_AUTOFILL}
                                          value={dueForm.note}
                                          onChange={(e) => { setDueNote(e.target.value); }}
                                        />
                                      </div>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                      <button type="button" className="btn" data-due-apply="" style={{ padding: "3px 9px", fontSize: FS.s10_5 }} onClick={applyAdapted}>
                                        {tD("cc.due.apply", { fallback: "Apply" })}
                                      </button>
                                      {pd.stated && (
                                        <button type="button" className="btn btn-ghost" data-due-revert="" style={{ padding: "3px 9px", fontSize: FS.s10_5 }} onClick={backToFormula}>
                                          {tD("cc.due.back_to_formula", { fallback: "Back to the formula" })}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* 141 §3.2 — THE PROMOTION IS OFFERED, NEVER
                                    SILENT [RULED 2026-08-27]. Brief 140's own
                                    shape (the folder reconcile): a fact named,
                                    a question, a button — because a correction,
                                    a partial payment or a mistyped amount must
                                    not silently change someone's standing in
                                    the case. Taking it runs the roster picker's
                                    own setLinkStatus, which stamps joinedDate
                                    if empty (Phase 43 stamp-if-empty) — the
                                    same write the pill makes, not a second
                                    path. No lock re-check: this dialog only
                                    opens unlocked (the round cell's onClick
                                    returns early under clLocked), the same
                                    boundary its own Save relies on. No warning
                                    tint: this is an offer, clicked. */}
                                {lk2 && lk2.status === "Prospect" && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      marginBottom: 10,
                                      fontSize: FS.s11,
                                      color: "var(--tm)",
                                    }}
                                  >
                                    <span style={{ flex: "1 1 auto" }}>
                                      {t("cc.pay.prospect_fact", {
                                        fallback:
                                          "This member is still a Prospect.",
                                      })}{" "}
                                      {t("cc.pay.prospect_make_active_q", {
                                        fallback: "Make them Active?",
                                      })}
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-ghost"
                                      style={{
                                        padding: "3px 8px",
                                        fontSize: FS.s10_5,
                                        flexShrink: 0,
                                      }}
                                      title={t(
                                        "cc.pay.prospect_make_active_q",
                                        { fallback: "Make them Active?" },
                                      )}
                                      onClick={() =>
                                        setLinkStatus(payDlg.clId, "Active")
                                      }
                                    >
                                      {t("cc.pay.prospect_make_active_btn", {
                                        fallback: "Make Active",
                                      })}
                                    </button>
                                  </div>
                                )}

                                {roundPayments.length > 0 && (
                                  <div style={{ marginBottom: 10 }}>
                                    <div
                                      style={{
                                        fontSize: FS.s9,
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: ".4px",
                                        color: "var(--tm)",
                                        marginBottom: 4,
                                      }}
                                    >
                                      {t("cc.payments.recorded_payments", {
                                        fallback: "Recorded payments",
                                      })}
                                    </div>
                                    {roundPayments.map((p: any, pi: any) => (
                                      <div
                                        key={pi}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 6,
                                          padding: "4px 0",
                                          borderBottom:
                                            "1px solid rgba(255,255,255,.04)",
                                          fontSize: FS.s11,
                                        }}
                                      >
                                        <span
                                          className="mono"
                                          style={{
                                            color: "var(--gn)",
                                            minWidth: 70,
                                            textAlign: "right",
                                          }}
                                        >
                                          {fq(+p.amount)}
                                        </span>
                                        <span
                                          style={{
                                            color: "var(--tm)",
                                            fontSize: FS.s10,
                                            minWidth: 65,
                                          }}
                                        >
                                          {fd(p.date)}
                                        </span>
                                        <span
                                          style={{
                                            color: "var(--ts)",
                                            flex: 1,
                                            fontSize: FS.s10,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {p.reference || "—"}
                                        </span>
                                        <button
                                          style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            color: "var(--tm)",
                                            padding: "1px",
                                            flexShrink: 0,
                                            lineHeight: 1,
                                          }}
                                          onClick={() => delPayment(pi)}
                                          title={t("cc.payments.delete_tip", {
                                            fallback: "Delete payment",
                                          })}
                                        >
                                          <Trash2 size={10} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div
                                  style={{
                                    borderTop: "1px solid var(--br)",
                                    paddingTop: 8,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: FS.s9,
                                      fontWeight: 600,
                                      textTransform: "uppercase",
                                      letterSpacing: ".4px",
                                      color: "var(--tm)",
                                      marginBottom: 6,
                                    }}
                                  >
                                    {t("cc.payments.record_payment", {
                                      fallback: "Record payment",
                                    })}
                                  </div>
                                  <div className="field">
                                    <label>
                                      {t("cc.payments.amount_eur", {
                                        fallback: "Amount (EUR)",
                                      })}
                                    </label>
                                    {/* 208 §10 — the one amount field; the dialog keeps its text contract (the save reads `+payForm.amount`) */}
                                    <AmountField
                                      value={payForm.amount}
                                      empty=""
                                      inputMode="decimal"
                                      onAmount={(n) =>
                                        setPayForm((p) => ({
                                          ...p,
                                          amount: n == null ? "" : String(n),
                                        }))
                                      }
                                      placeholder={
                                        remaining > 0 ? fq(remaining) : "0,00"
                                      }
                                    />
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: 8,
                                    }}
                                  >
                                    <div className="field">
                                      <label>
                                        {t("common.date", { fallback: "Date" })}
                                      </label>
                                      <input
                                        className="field-input"
                                        type="date"
                                        value={payForm.date}
                                        onChange={(e) =>
                                          setPayForm((p) => ({
                                            ...p,
                                            date: e.target.value,
                                          }))
                                        }
                                      />
                                    </div>
                                    <div className="field">
                                      <label>
                                        {t("cc.payments.reference", {
                                          fallback: "Reference",
                                        })}
                                      </label>
                                      <input
                                        className="field-input"
                                        value={payForm.reference}
                                        onChange={(e) =>
                                          setPayForm((p) => ({
                                            ...p,
                                            reference: e.target.value,
                                          }))
                                        }
                                        placeholder={t(
                                          "cc.payments.invoice_no_ph",
                                          { fallback: "Invoice no." },
                                        )}
                                      />
                                    </div>
                                  </div>
                                </div>
                          </ModalLayout>
                        );
                      })()}
                  </>
                );
              })()}
            </div>
          )}
          {canViewCCRetainer && (
            <div
              className="csSec-ccRetainer"
              style={{ flex: "1 1 100%", minWidth: 0 }}
              data-secollapsed={sCol("ccRetainer") ? "1" : "0"}
            >
              {/* Rounds management */}
              {canSeeFin && (
                <>
                  <SectionHeader {...({} as any)}
                    title={t("case.retainer_rounds")}
                    collapsible
                    open={!sCol("ccRetainer")}
                    onToggle={() => tog("ccRetainer")}
                    style={{ margin: "10px 0 6px" }}
                    rightContent={
                      <>
                        {!roundsLocked && (
                          <>
                            {/* Phase 25.1 C4: "Overdue after" field removed — it
                                had no functional consumer (not DB-backed; no
                                overdue derivation, badge, merge token, reminder,
                                or sort/filter). Removing it also removes the
                                header lock-toggle jump that MF2 worked around. */}
                            <AddButton {...({} as any)}
                              label={t("case.add.round")}
                              visible={rounds.length < 10}
                              onClick={() => {
                                const rn = rounds.length + 1;
                                updCs({
                                  rounds: [
                                    ...rounds,
                                    {
                                      roundNumber: rn,
                                      label: t("cc.rounds.label_n", {
                                        n: rn,
                                        fallback: "Round " + rn,
                                      }),
                                      dueDate: "",
                                      formula: "5%",
                                    },
                                  ],
                                });
                                toast(
                                  t("toast.round_added", {
                                    fallback: "Round added",
                                  }),
                                );
                              }}
                            />
                          </>
                        )}
                        <LockButton {...({} as any)}
                          locked={roundsLocked}
                          onToggle={() => setRoundsLocked((v) => !v)}
                        />
                      </>
                    }
                  />
                  {rounds.length === 0 && (
                    <p
                      style={{
                        fontSize: FS.s11,
                        color: "var(--tm)",
                        marginBottom: 8,
                      }}
                    >
                      {t("cc.rounds.empty", {
                        fallback:
                          "No retainer fee rounds configured. Add a round to define the fee structure.",
                      })}
                    </p>
                  )}
                  {rounds.length > 0 && (
                    <div className="table-wrap" style={{ marginBottom: 12 }}>
                      <Table>
                        <thead>
                          <tr>
                            <th style={{ width: 30 }}>#</th>
                            <th>
                              {t("cc.rounds.label", { fallback: "Label" })}
                            </th>
                            <th>
                              {t("cc.rounds.formula", { fallback: "Formula" })}
                            </th>
                            <th>
                              {t("field.due_date", { fallback: "Due Date" })}
                            </th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {rounds.map((r: any, i: any) => (
                            <tr key={r.roundNumber}>
                              <td className="mono" style={{ fontWeight: 600 }}>
                                {r.roundNumber}
                              </td>
                              <td>
                                <input
                                  className="field-input"
                                  style={{
                                    padding: "2px 6px",
                                    fontSize: FS.s11,
                                    margin: 0,
                                  }}
                                  value={r.label}
                                  readOnly={roundsLocked}
                                  onChange={(e) => {
                                    const u = [...rounds];
                                    u[i] = { ...u[i], label: e.target.value };
                                    updCs({ rounds: u });
                                  }}
                                />
                              </td>
                              <td>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                  }}
                                >
                                  <input
                                    className="field-input"
                                    style={{
                                      width: 170,
                                      padding: "2px 5px",
                                      fontSize: FS.s11,
                                      margin: 0,
                                      fontFamily: "'JetBrains Mono',monospace",
                                    }}
                                    value={r.formula}
                                    readOnly={roundsLocked}
                                    onChange={(e) => {
                                      const u = [...rounds];
                                      u[i] = {
                                        ...u[i],
                                        formula: e.target.value,
                                      };
                                      updCs({ rounds: u });
                                    }}
                                    placeholder={t("modal.cc.formula_ph", {
                                      fallback: "5%, 3, or =500+0.1*quantum",
                                    })}
                                    title={t("cc.rounds.formula_tooltip", {
                                      fallback:
                                        "5% = percentage · 3 = multiply quantum × 3 · =EXPR evaluates with 'quantum' variable (supports + - * / and parentheses)",
                                    })}
                                  />
                                  {(() => {
                                    const fs = String(r.formula || "").trim();
                                    if (!fs.startsWith("=")) return null;
                                    // C.2 — the preview evaluation is
                                    // server-computed (rcd.preview, aligned
                                    // with the rounds array; the formula
                                    // keystroke persists through updCs, so
                                    // the saved rounds ARE what's evaluated).
                                    // Latest-wins: the line trails the last
                                    // keystroke by one round-trip.
                                    const pv =
                                      rcd && rcd.preview
                                        ? rcd.preview[i]
                                        : null;
                                    if (!pv) return null;
                                    return pv.error ? (
                                      <span
                                        style={{
                                          fontSize: FS.s9,
                                          color: "var(--rd)",
                                        }}
                                      >
                                        ⚠{" "}
                                        {t("cc.rounds.invalid_formula", {
                                          err: pv.error,
                                          fallback:
                                            "Invalid formula: " + pv.error,
                                        })}
                                      </span>
                                    ) : (
                                      <span
                                        style={{
                                          fontSize: FS.s9,
                                          color: "var(--tm)",
                                          fontFamily:
                                            "'JetBrains Mono',monospace",
                                        }}
                                      >
                                        = {fc(pv.value)} @ Q={fq(_rcSampleQ)}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </td>
                              <td>
                                <input
                                  className="field-input"
                                  type="date"
                                  style={{
                                    padding: "2px 5px",
                                    fontSize: FS.s11,
                                    margin: 0,
                                  }}
                                  value={r.dueDate || ""}
                                  readOnly={roundsLocked}
                                  onChange={(e) => {
                                    const u = [...rounds];
                                    u[i] = { ...u[i], dueDate: e.target.value };
                                    updCs({ rounds: u });
                                  }}
                                />
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {!roundsLocked && (
                                  <button
                                    className="btn btn-danger"
                                    style={{ padding: "1px 4px", fontSize: FS.s9 }}
                                    onClick={() =>
                                      confirmAndUndo({
                                        label: `round ${r.roundNumber}`,
                                        onConfirm: () => {
                                          updCs({
                                            rounds: rounds
                                              .filter((_: any, j: any) => j !== i)
                                              .map((x: any, j: any) => ({
                                                ...x,
                                                roundNumber: j + 1,
                                              })),
                                          });
                                          toast(
                                            t("toast.round_removed", {
                                              fallback: "Round removed",
                                            }),
                                          );
                                        },
                                        undo: null,
                                      })
                                    }
                                  >
                                    <Trash2 size={I.s} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
              {/* Pro Forma & Invoices */}
              {/* Removed in Phase M+3 — see scope §5.6 (vestigial JSON column) and §6.1
    (route retirements). Real CC invoice fetch via /api/invoices?ccId=... lands
    at Gate 4. */}
            </div>
          )}
          {canViewCCDocs && (
            <div
              className="csSec-docTracking"
              style={{ flex: "1 1 100%", minWidth: 0 }}
            >
              <DocTrackingMatrix
                toast={toast}
                collectiveCaseId={cs.id}
                clients={(cs.clientLinks || [])
                  .map((lk: any) => {
                    const c2 = cl.find((x: any) => x.id === lk.clId);
                    return c2 || null;
                  })
                  .filter(Boolean)}
                canManageTypes={isPartnerStrictCc}
                collapsible
                sectionOpen={!sCol("docTracking")}
                onSectionToggle={() => tog("docTracking")}
              />
            </div>
          )}
          {canViewCalculator && (
            <div
              className="csSec-calculator"
              style={{ flex: "1 1 100%", minWidth: 0 }}
              data-secollapsed={sCol("calculator") ? "1" : "0"}
            >
              <SectionHeader {...({} as any)}
                title={t("case.calculator.title", {
                  fallback: "Interest calculator",
                })}
                collapsible
                open={!sCol("calculator")}
                onToggle={() => tog("calculator")}
              />
              <CaseCalculatorSection
                kind="cc"
                cs={cs}
                canEdit={canEditCalculator}
                toast={toast}
                hideTitle={true}
              />
            </div>
          )}
          <div
            className="csSec-fin"
            style={{
              flex: "1 1 100%",
              minWidth: 0,
              display: canSeeFin ? undefined : "none",
            }}
            data-secollapsed={sCol("fin") ? "1" : "0"}
          >
            <SectionHeader {...({} as any)}
              // Brief 86 §2b, CCV only — "Financials" becomes "Financial
              // summary", matching CaseView's case.financial_summary. Same
              // title, two implementations: the mechanism below stays CCV's
              // own, per §0. The seeded en/fr/nl rows move with the fallback.
              title={t("cc.fin.title", { fallback: "Financial summary" })}
              collapsible
              open={!sCol("fin")}
              onToggle={() => tog("fin")}
            />

            {/* Financials */}
            {(() => {
              if (!canSeeFin) return null;
              const cte = (te || []).filter((e2: any) => e2.ccId === cs.id);
              if (!cte.length && !(cs.referenceHourlyRate != null)) return null;
              // C.2 — the table's money is server-handed (ccFinCalc block for
              // this cc); the view renders what it was given ("—" until the
              // first compute lands). The cte filter above is row SELECTION
              // (the render gate), not money — it stays.
              const fin = ccFinCalc?.[cs.id]?.block || null;
              // 161 §8 [R6] — see CaseView: the recap's ten strip-basis rows.
              const recap = fin ? fin.recap : null;
              const isPartnerOnly = canTerms;
              const paid = recap ? recap.paid : null;
              const due = recap ? recap.due : null;
              const staffCost = recap ? recap.staffCost : null;
              const overheads = recap ? recap.overheads : null;
              const grossMargin = recap ? recap.grossMargin : null;
              const marginPct = recap ? recap.marginPct : null;
              const marginPctColor =
                marginPct == null
                  ? "var(--tm)"
                  : marginPct > 30
                    ? "var(--gn)"
                    : marginPct >= 10
                      ? "var(--am)"
                      : "var(--rd)";
              /* 199 §F — the two lines' figures, read off the served block
                 through named shapes (F.3: the server's costsLine and activity —
                 the builders Frais et honoraires reads — nothing computed here).
                 The activity rows: the `activity_code` taxonomy in its order
                 (`tkTypes`, App's own list) for the codes THIS matter carries,
                 plus any code the rows carry that the taxonomy no longer names
                 (the union rule), each with §C.0's three figures. */
              interface FinCostsLine {
                costsRatio?: number | null; totalRate?: number | null; pctByPartner?: number | null;
                avgRatePerH?: number | null; effectiveRatePerH?: number | null; totalMin?: number | null;
                totalInvoiced?: number | null; totalPaidExVat?: number | null; totalDueExVat?: number | null;
                effectiveMargin?: number | null; discardedPayments?: number; discardedGross?: number;
                discardedForeign?: Record<string, number> | null; discardedDueDocs?: number; discardedDueGross?: number;
              }
              interface FinActivityCell { min?: number | null; cost?: number | null; pct?: number | null; wc?: number | null /* 214 addendum 1 §13 */ }
              interface FinRecapSeam { marginBase?: number | null; marginPct?: number | null /* 214 addendum 1 §11 */ }
              interface FinActivity { byType?: Record<string, FinActivityCell | undefined> | null }
              /* 181's rule for a file whose props arrive as `any`: the translator
                 narrowed ONCE at this seam, so the rows below are typed calls. */
              const tt: Translate = t;
              const __finB = fin as { costsLine?: FinCostsLine | null; activity?: FinActivity | null; recap?: FinRecapSeam | null } | null;
              const __cl = __finB?.costsLine ?? null;
              // 214 addendum 1 §11 [RULED 57] — the margin's base, served beside the margin (recapBlock's `marginBase`),
              // and the margin's percentage in the reader's number format (« 90,2 % » in fr).
              const marginBase = __finB?.recap?.marginBase ?? null;
              const marginPctText = pct1(__finB?.recap?.marginPct ?? null, __locale);
              const __clDiscarded = (() => {
                const count = __cl?.discardedPayments ?? 0;
                if (count <= 0) return undefined;
                const parts: string[] = [];
                if ((__cl?.discardedGross ?? 0) > 0) parts.push(fc(__cl?.discardedGross ?? 0));
                for (const [code, v] of Object.entries(__cl?.discardedForeign ?? {})) parts.push(`${String(Math.floor(v))} ${code}`);
                const gross = parts.length ? parts.join(" + ") : fc(0);
                return tt("costs.col.effective_rate_h_discarded", { count, gross, fallback: `${String(count)} payment(s) discarded — no pro forma with a VAT split behind them — ${gross} gross; their hours still count` });
              })();
              const __clDueDiscarded = (() => {
                const count = __cl?.discardedDueDocs ?? 0;
                if (count <= 0) return undefined;
                const gross = fc(__cl?.discardedDueGross ?? 0);
                return tt("costs.col.total_due_ex_vat_discarded", { count, gross, fallback: `${String(count)} sent document(s) discarded — no VAT split stored — ${gross} still owed on them, gross` });
              })();
              const __byType = __finB?.activity?.byType ?? {};
              const __actRows = (() => {
                const out: { code: string; label: string; min: number | null; cost: number | null; pct: number | null; wc: number | null }[] = [];
                const seen = new Set<string>();
                const push = (code: string, label: string) => {
                  const cell = __byType[code];
                  if (!cell || seen.has(code)) return;
                  seen.add(code);
                  // 214 addendum 1 §13 — `wc`, the activity's fees + costs ex VAT, served on the block.
                  out.push({ code, label, min: cell.min ?? null, cost: cell.cost ?? null, pct: cell.pct ?? null, wc: cell.wc ?? null });
                };
                for (const ty of ((tkTypes as { code?: string; desc?: string }[] | null | undefined) ?? [])) {
                  if (ty.code) push(ty.code, ty.desc ?? ty.code);
                }
                for (const code of Object.keys(__byType)) push(code, code);
                return out;
              })();
              // A1 (brief 65) — TH2/MC2/ML2 now live in src/lib/moneyTableCells,
              // imported under their old names so every call site is unchanged.
              //
              // Brief 86 §2b — RP hours / collaborator hours / total hours, the
              // same shape as CaseView.tsx's, computed here rather than in
              // ccBlock() for the same reason: caseFinancials.js is a RECORDED
              // compute (recordings/c2-financials, two bit-exact verifiers) and
              // minutes need no rate resolution. `cte` above is the identical
              // predicate the server applies (caseFinancials.js:163), so
              // hTot.tot equals the block's own totM by construction.
              type HrRow = {
                coId?: number | null; min?: number | null; billed?: boolean;
                mark?: string | null; invoiceId?: number | null;
                type?: string | null; // 214 addendum 1 §13 — the activity code, for the partner / collaborators split per activity
              };
              const _rpSet = new Set(cs.responsiblePartners || []);
              const _hrs = (rows: HrRow[]) => {
                const rp = rows.reduce(
                  (s2: number, e2: HrRow) => s2 + (_rpSet.has(e2.coId) ? e2.min || 0 : 0),
                  0,
                );
                const all = rows.reduce((s2: number, e2: HrRow) => s2 + (e2.min || 0), 0);
                return { rp, col: all - rp, tot: all };
              };
              // 214 addendum 1 §13 — the matter's rows, narrowed ONCE (181's rule) for the per-activity split below.
              const __cte: HrRow[] = cte as HrRow[];
              // 161 §8 — the five hour rows take the SAME predicates as the
              // server's recap; see CaseView.
              const _sentInv = new Set(
                (inv || [])
                  .filter((i2: any) => i2.ccId === cs.id && !i2.deletedAt
                    && i2.paymentStatus !== "cancelled" && (i2.sentToClientAt || i2.sentToPeppolAt))
                  .map((i2: any) => i2.id),
              );
              const hUnm = _hrs(cte.filter((e2: HrRow) => e2.invoiceId == null && e2.mark !== "billable" && e2.mark !== "unbillable"));
              const hUnb = _hrs(cte.filter((e2: HrRow) => e2.invoiceId == null && e2.mark === "unbillable"));
              const hBil = _hrs(cte.filter((e2: HrRow) => e2.invoiceId == null && e2.mark === "billable"));
              const hInv = _hrs(cte.filter((e2: HrRow) => e2.invoiceId != null));
              const hSent = _hrs(cte.filter((e2: HrRow) => e2.invoiceId != null && _sentInv.has(e2.invoiceId)));
              return (
                <div>
                  <div className="table-wrap" data-fin-table="recap">
                    <Table style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          {TH2("")}
                          {/* Brief 86 §2b — the three hour columns, BOTH
                              views, before the fees columns. The keys are
                              `fin.col.*` and not `cc.fin.col.*`: they are new,
                              they are the same three words in both tables, and
                              brief 59's finding was one widget carrying five
                              keys and five wordings. A namespace belonging to
                              neither view is what stops that here — and the
                              two tables stay two implementations regardless,
                              per §0, so nothing about this shares code. */}
                          {TH2(
                            /* Brief 90 §2b — see CaseView: full words, and a
                               NEW key because the old one is already seeded. */
                            t("fin.col.rp_hours_full", {
                              fallback: "Responsible Partner hours",
                            }),
                            1,
                          )}
                          {TH2(
                            t("fin.col.collab_hours", {
                              fallback: "Collaborator hours",
                            }),
                            1,
                          )}
                          {TH2(
                            t("fin.col.total_hours", {
                              fallback: "Total hours",
                            }),
                            1,
                          )}
                          {/* Brief 87 §5 — the adaptive gap, CCV's half. See
                              CaseView: one `grow` flag on the first fees column
                              opens the slack between the hours and the fees. */}
                          {TH2(
                            t("cc.fin.col.fees_only", {
                              fallback: "Fees only",
                            }),
                            1,
                            1,
                          )}
                          {TH2(
                            t("cc.fin.col.fees_costs", {
                              fallback: "Fees + Costs",
                            }),
                            1,
                          )}
                          {TH2(
                            t("cc.fin.col.fees_costs_vat", {
                              fallback: "Fees + Costs + VAT",
                            }),
                            1,
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {/* 161 §8 [R6] — TEN ROWS, the strip's items in the
                            strip's order; see CaseView for the colour rule and
                            the money-column convention. The CC recap gains the
                            paid/due pair it never had — one basis per row on
                            BOTH surfaces is the ruling's point. */}
                        <tr style={{ background: "rgba(52,211,153,.04)" }}>
                          {ML2(t("cc.fin.row.unmarked", { fallback: "Unmarked" }), "var(--gn)")}
                          {HC2(hUnm.rp, "var(--gn)")}
                          {HC2(hUnm.col, "var(--gn)")}
                          {HC2(hUnm.tot, "var(--gn)")}
                          {MC2(recap?.unmarked?.fo, "var(--gn)")}
                          {MC2(recap?.unmarked?.wc, "var(--gn)")}
                          {MC2(recap?.unmarked?.vt, "var(--gn)")}
                        </tr>
                        <tr style={{ background: "rgba(248,113,113,.04)" }}>
                          {ML2(t("cc.fin.row.unbillable", { fallback: "Unbillable" }), "var(--rd)")}
                          {HC2(hUnb.rp, "var(--rd)")}
                          {HC2(hUnb.col, "var(--rd)")}
                          {HC2(hUnb.tot, "var(--rd)")}
                          {MC2(recap?.unbillable?.fo, "var(--rd)")}
                          {MC2(recap?.unbillable?.wc, "var(--rd)")}
                          {MC2(recap?.unbillable?.vt, "var(--rd)")}
                        </tr>
                        <tr style={{ background: "rgba(251,191,36,.05)" }}>
                          {ML2(t("cc.fin.row.billable", { fallback: "Billable" }), "var(--am)")}
                          {HC2(hBil.rp, "var(--am)")}
                          {HC2(hBil.col, "var(--am)")}
                          {HC2(hBil.tot, "var(--am)")}
                          {MC2(recap?.billable?.fo, "var(--am)")}
                          {MC2(recap?.billable?.wc, "var(--am)")}
                          {MC2(recap?.billable?.vt, "var(--am)")}
                        </tr>
                        <tr style={{ borderTop: "1px solid var(--br)" }}>
                          {ML2(t("cc.fin.row.invoiced", { fallback: "Invoiced" }), "var(--ac)")}
                          {HC2(hInv.rp, "var(--ac)")}
                          {HC2(hInv.col, "var(--ac)")}
                          {HC2(hInv.tot, "var(--ac)")}
                          {MC2(recap?.invoiced?.fo, "var(--ac)", undefined, foreignOf(recap, "invoiced", "fo"))}
                          {MC2(recap?.invoiced?.wc, "var(--ac)", undefined, foreignOf(recap, "invoiced", "wc"))}
                          {MC2(recap?.invoiced?.vt, "var(--ac)", undefined, foreignOf(recap, "invoiced", "vt"))}
                        </tr>
                        <tr>
                          {ML2(t("cc.fin.row.sent", { fallback: "Sent" }), "var(--tm)")}
                          {HC2(hSent.rp, "var(--tm)")}
                          {HC2(hSent.col, "var(--tm)")}
                          {HC2(hSent.tot, "var(--tm)")}
                          {MC2(recap?.sent?.fo, "var(--tm)", undefined, foreignOf(recap, "sent", "fo"))}
                          {MC2(recap?.sent?.wc, "var(--tm)", undefined, foreignOf(recap, "sent", "wc"))}
                          {MC2(recap?.sent?.vt, "var(--tm)", undefined, foreignOf(recap, "sent", "vt"))}
                        </tr>
                        {isPartnerOnly && (
                          <>
                            {/* 214 addendum 1 §10 [RULED 55] — « Payé » and « Dû »: the five
                                cells that can hold no figure are EMPTY (`EC`), where `HC(null)`
                                / `MC(null)` printed « — » — « columns where no figure is
                                supposed to appear should not show a line like it's missing
                                something » (Laurent, 03/10). A dash stays only where a figure
                                belongs and cannot be given: the summary not served yet, a
                                computed 0 €/h (198 A.3a). */}
                            <tr style={{ background: "rgba(52,211,153,.05)" }} data-fin-row="paid">
                              {ML2(t("cc.fin.row.paid", { fallback: "Paid" }), "var(--gn)")}
                              {EC()}
                              {EC()}
                              {EC()}
                              {EC()}
                              {EC()}
                              {MC2(paid, "var(--gn)", undefined, foreignOf(recap, "paid"))}
                            </tr>
                            <tr
                              style={{
                                background:
                                  (due ?? 0) > 0
                                    ? "rgba(248,113,113,.06)"
                                    : "rgba(52,211,153,.06)",
                              }}
                              data-fin-row="due"
                            >
                              {ML2(
                                t("cc.fin.row.due", { fallback: "Due" }),
                                (due ?? 0) > 0 ? "var(--rd)" : "var(--gn)",
                                600,
                              )}
                              {EC()}
                              {EC()}
                              {EC()}
                              {EC()}
                              {EC()}
                              {MC2(due, (due ?? 0) > 0 ? "var(--rd)" : "var(--gn)", 600, foreignOf(recap, "due"))}
                            </tr>
                          </>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  {/* 214 addendum 1 §11 [RULED 55, 57] — THE PART OF ITS OWN, the collective case's twin (86 §0: two implementations; see CaseView for the reasoning). The table above
                      keeps the strip's seven lines under its hour and money columns; every
                      other line stood under « Honoraires + frais », which none of them is
                      (161 §8 [R6] placed the costs and the margin there, 199 §F.2 the rest).
                      Below the table, under the partner gate as before, each line is its
                      label and its figure on one right edge — no column headers, so no empty
                      cell [ADVISOR]. Three groups: « Coûts et marge » (staff costs ·
                      overheads · the margin's BASE, served as `marginBase` and printed just
                      above the margin [RULED 57] · the gross margin, its percentage in the
                      reader's number format — `pct1`), « Frais et honoraires » (199 §F's
                      members, « Total facturé » gone: it is the table's « Facturé », §12),
                      and « Par activité » (§13 — a small table of its own). Colours and tips
                      as before, line for line. The figures stay the SERVER's (199 §F.3). */}
                  {isPartnerOnly && (
                    <div data-fin-part style={{ marginBottom: 10 }}>
                      <div className="table-wrap">
                        <Table style={{ width: "100%" }}>
                          <tbody>
                            {SH(tt("fin.part.costs_margin", { fallback: "Costs and margin" }), 2)}
                            <tr style={{ background: "rgba(248,113,113,.04)" }} data-fin-line="staff_cost">
                              {ML2(tt("cc.fin.staff_cost", { fallback: "Staff costs" }), "var(--rd)")}
                              {MC2(staffCost, "var(--rd)")}
                            </tr>
                            <tr style={{ background: "rgba(248,113,113,.04)" }} data-fin-line="overheads">
                              {ML2(tt("cc.fin.overheads", { fallback: "Overheads" }), "var(--rd)")}
                              {MC2(overheads, "var(--rd)")}
                            </tr>
                            <tr data-fin-line="margin_base">
                              {ML2(tt("fin.row.margin_base", { fallback: "Invoiced (pro formas, after discount)" }), "var(--ac)")}
                              {MC2(marginBase, "var(--ac)")}
                            </tr>
                            <tr
                              style={{
                                background:
                                  (grossMargin ?? 0) >= 0
                                    ? "rgba(52,211,153,.06)"
                                    : "rgba(248,113,113,.06)",
                              }}
                              data-fin-line="gross_margin"
                            >
                              {ML2(
                                <span>
                                  {tt("cc.fin.row.gross_margin", { fallback: "Gross Margin" })}
                                  {marginPctText != null && (
                                    <span
                                      data-fin-margin-pct
                                      style={{
                                        fontSize: FS.s9_5,
                                        marginLeft: 6,
                                        color: marginPctColor,
                                        fontWeight: 600,
                                        fontFamily:
                                          "'JetBrains Mono',monospace",
                                      }}
                                    >
                                      {marginPctText}
                                    </span>
                                  )}
                                </span>,
                                (grossMargin ?? 0) >= 0 ? "var(--gn)" : "var(--rd)",
                                600,
                              )}
                              {MC2(
                                grossMargin,
                                (grossMargin ?? 0) >= 0 ? "var(--gn)" : "var(--rd)",
                                600,
                              )}
                            </tr>
                            {SH(tt("nav.costs"), 2)}
                            <tr data-fin-line="pct_costs">
                              {ML2(tt("costs.col.pct_costs", { fallback: "% costs" }), "var(--tm)")}
                              {TC(pctCo(__cl?.costsRatio))}
                            </tr>
                            <tr data-fin-line="total_rate">
                              {ML2(tt("costs.col.total_rate", { fallback: "Total rate" }), "var(--tm)")}
                              {TC(ratCo(__cl?.totalRate), undefined, undefined, tt("costs.col.total_rate_title", { fallback: "Reference rate × (1 + % costs / 100)" }))}
                            </tr>
                            <tr data-fin-line="pct_by_partner">
                              {ML2(tt("costs.col.pct_by_partner"), "var(--tm)")}
                              {TC(pctCo(__cl?.pctByPartner), undefined, undefined, tt("costs.col.pct_by_partner_title", { fallback: "Minutes worked by the fee-earning roles ÷ every minute worked on the matter" }))}
                            </tr>
                            <tr data-fin-line="avg_rate_h">
                              {ML2(tt("costs.col.avg_rate_h"), "var(--tm)")}
                              {TC(ratCo(__cl?.avgRatePerH))}
                            </tr>
                            <tr data-fin-line="effective_rate_h">
                              {ML2(tt("costs.col.effective_rate_h", { fallback: "Paid/h." }), "var(--tm)")}
                              {TC(ratCo(__cl?.effectiveRatePerH), undefined, undefined, __clDiscarded ?? tt("costs.col.effective_rate_h_title", { fallback: "Payments received ex-VAT ÷ every hour worked, all marks (unmarked, unbillable, billable, invoiced)" }))}
                            </tr>
                            <tr data-fin-line="total_time">
                              {ML2(tt("costs.col.total_time"), "var(--tm)")}
                              {HC2(__cl?.totalMin ?? null, "var(--tp)")}
                            </tr>
                            <tr data-fin-line="total_paid_ex_vat">
                              {ML2(tt("costs.col.total_paid_ex_vat", { fallback: "Paid ex VAT" }), "var(--gn)")}
                              {TC(__cl?.totalPaidExVat != null ? fc(__cl.totalPaidExVat) : null, "var(--gn)", undefined, __clDiscarded)}
                            </tr>
                            <tr data-fin-line="total_due_ex_vat">
                              {ML2(tt("costs.col.total_due_ex_vat", { fallback: "Due ex VAT" }), (__cl?.totalDueExVat ?? 0) > 0 ? "var(--rd)" : "var(--gn)")}
                              {TC(__cl?.totalDueExVat != null ? fc(__cl.totalDueExVat) : null, (__cl?.totalDueExVat ?? 0) > 0 ? "var(--rd)" : "var(--gn)", undefined, __clDueDiscarded ?? tt("costs.col.total_due_ex_vat_title", { fallback: "What the sent documents still owe, each at its own VAT split" }))}
                            </tr>
                            <tr data-fin-line="effective_margin">
                              {ML2(tt("costs.col.effective_margin", { fallback: "Effective margin" }), (__cl?.effectiveMargin ?? 0) >= 0 ? "var(--gn)" : "var(--rd)", 600)}
                              {TC(__cl?.effectiveMargin != null ? fc(__cl.effectiveMargin) : null, (__cl?.effectiveMargin ?? 0) >= 0 ? "var(--gn)" : "var(--rd)", 600, tt("costs.col.effective_margin_title", { fallback: "Paid ex VAT − staff costs − overheads" }))}
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                      {/* 214 addendum 1 §13 [RULED 56] — « PAR ACTIVITÉ », A SMALL TABLE OF ITS
                          OWN: per activity, the responsible partner's hours, the collaborators'
                          hours, the total (`_hrs` over the activity's rows, as the table's hour
                          lines — 86 §2b), its fees + costs ex VAT (`wc`, served on the block —
                          the sum of what those entries show in the Amount column, in cents;
                          docs/214-deploy-note.md §7.13) and its internal cost (199 §C.0's
                          figure, in the neutral colour, no longer red under a fees header
                          [ADVISOR]). The label keeps 205 §1's chip, name and share. */}
                      {__actRows.length > 0 && (
                        <div className="table-wrap" style={{ marginTop: 6 }} data-fin-activity-table>
                          <Table style={{ width: "100%" }}>
                            <thead>
                              {SH(tt("costs.section.by_activity", { fallback: "By activity" }), 6)}
                              <tr>
                                {TH2("")}
                                {TH2(tt("fin.col.rp_hours_full", { fallback: "Responsible Partner hours" }), 1)}
                                {TH2(tt("fin.col.collab_hours", { fallback: "Collaborator hours" }), 1)}
                                {TH2(tt("fin.col.total_hours", { fallback: "Total hours" }), 1)}
                                {TH2(tt("cc.fin.col.fees_costs", { fallback: "Fees + Costs" }), 1, 1)}
                                {TH2(tt("fin.col.internal_cost", { fallback: "Internal cost" }), 1)}
                              </tr>
                            </thead>
                            <tbody>
                              {__actRows.map((a) => {
                                const h = _hrs(__cte.filter((e) => String(e.type ?? "") === a.code));
                                return (
                                  <tr key={a.code} data-fin-activity={a.code}>
                                    {ML2(
                                      <span>
                                        {/* 205 §1 — the one chip (`TYPE_CHIP`), the name, the share (206 §1: `wide`). */}
                                        <span style={{ ...TYPE_CHIP, marginRight: 6 }}>{a.code}</span>
                                        {a.label}
                                        <span data-fin-activity-share style={{ fontSize: FS.s9_5, marginLeft: 6, color: "var(--tm)", fontWeight: 600, fontFamily: "'JetBrains Mono',monospace" }}>
                                          {pctCo(a.pct)}
                                        </span>
                                      </span>,
                                      "var(--ts)",
                                      undefined,
                                      true,
                                    )}
                                    {HC2(h.rp, "var(--tp)")}
                                    {HC2(h.col, "var(--tp)")}
                                    {HC2(h.tot, "var(--tp)")}
                                    {MC2(a.wc, "var(--tp)")}
                                    {MC2(a.cost, "var(--tp)")}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}
                  {/* 158 §5 — the SAME table the Invoices section
                      draws, since that is where this component now
                      comes from. `scope` says the host already names
                      the matter, so the CASE and TITLE columns stay
                      folded — the shape this view always had. */}
                  {/* 161 §7 — the 159 PROVISIONS block is RETIRED (see CV):
                      a provision is a `retainer` pro forma in the table below. */}
                  <InvoiceTable
                    invoices={(inv || []).filter((i: any) => i.ccId === cs.id)}
                    scope={{ type: "cc", id: cs.id }}
                    labelled
                    inv={inv}
                    sInv={sInv}
                    toast={toast}
                    onViewInvoice={(id: any) => setPfMod({ invoiceId: id })}
                    /* 179 B.6 — the approval pill's switch. Typed at the seam so the
                       host's `any` does not add an unsafe assignment (the eslint ratchet). */
                  />
                </div>
              );
            })()}
          </div>
          {canViewTime && (
            /* BRIEF 86 PHASE 2 STEP 1 — the same component CaseView mounts.
               CCV's own copy carried three pieces of drift and they go with it:
               the dead `visibleTimeEntries` call over rows the server had
               already filtered, `canDelegate={canMarkBilled || canFin}` (RULED
               narrow 2026-08-17), and a second i18n key for one button. */
            <CaseTimeEntriesSection
              {...({} as any)}
              kind="cc"
              cs={cs}
              co={co}
              currentUser={currentUser}
              /* 198 §C — the SETTINGS OBJECT, not the resolved axis: the period
                 filter's two accounting-year kinds read the firm's start off it
                 (D.3); the work week resolves from it just the same. */
              schedAxis={firmSettings}
              te={te}
              sTe={sTe}
              s={s}
              SK={SK}
              ca={[]}
              cc={cc}
              inv={inv}
              canSeeCounters={canSeeTeCounters}
              canImportExport={canTeImportExport}
              canPillColumn={canTePillColumn}
              tkTypes={tkTypes}
              canTerms={canTerms}
              canEditTime={canEditTime}
              canMarkBilled={canMarkBilled}
              canBulkEdit={canBulkEdit}
              onBulkEdit={(ids: number[], onDone: (r: TeBulkUpdateResult) => void) =>
                globalOm("teBulk", { _teIds: ids, _onDone: onDone })
              }
              canBulkDelete={canBulkDelete}
              confirmAndUndo={confirmAndUndo}
              nid={nid}
              _roleRows={_roleRows}
              openTimeModal={globalOm}
              onDelete={(e2row: any) =>
                confirmAndUndo({
                  label: "this time entry",
                  toastName: `Removed "${e2row.desc || "time entry"}"`,
                  onConfirm: () => s(SK.te, te.filter((x2: any) => x2.id !== e2row.id), sTe),
                  undo: {
                    snapshot: e2row,
                    restore: (snap: any) => s(SK.te, [...teRef.current, snap], sTe),
                  },
                })
              }
              onProForma={(p: any) => setPfMod({ ...(Array.isArray(p) ? { entryIds: p } : p), csId: cs.id, csType: "cc" })}
              sCol={sCol}
              tog={tog}
              t={t}
              toast={toast}
              headerStyle={{ margin: "10px 0 6px" }}
            />
          )}
          {/* Phase 9 Stage 2 (Item 10) — Open CONFID folder; gated server-side. */}

          {/* Phase 9 Stage 2 (Item 9) — CDD section for CollectiveCase. Full-width
    grid row; CddCaseSection self-gates on caseView.cdd.view. */}

        </div>
        {!canViewDesc &&
          !canViewParties &&
          !canViewAgenda &&
          !canViewTasks &&
          !canViewDocs &&
          !canViewTimeline &&
          !canFin &&
          !canTerms &&
          !canViewTime &&
          !canViewCCClients &&
          !canViewCCRetainer &&
          !canViewCCDocs && (
            <p
              style={{
                textAlign: "center",
                color: "var(--tm)",
                fontSize: FS.s11_5,
                padding: "16px 12px",
                fontStyle: "italic",
              }}
            >
              {t("cc.no_section_access", {
                fallback:
                  "You don't have access to any sections of this collective case. Contact the Responsible Partner if you need access.",
              })}
            </p>
          )}
        {modal}
        {pfMod && (
          <ProFormaModal {...({} as any)}
            key={pfMod?.invoiceId ? `pf-view-${pfMod.invoiceId}` : "pf-create"}
            pfMod={pfMod}
            invoiceId={pfMod?.invoiceId || null}
            onClose={() => setPfMod(null)}
            onRemake={(p: any) => setPfMod(p)}
            te={te}
            sTe={sTe}
            ca={ca || []}
            cc={cc}
            cl={cl}
            co={co}
            inv={inv}
            sInv={sInv}
            s={s}
            toast={toast}
            tkTypes={tkTypes}
            tk={tk}
            sTk={sTk}
            currentUser={currentUser}
            firmSettings={firmSettings}
          />
        )}
        {clImportFile && (
          <ClientImportModal
            clImportFile={clImportFile}
            setClImportFile={setClImportFile}
            cl={cl}
            sCl={sCl}
            s={s}
            SK={SK}
            links={links}
            rounds={rounds}
            cs={cs}
            cc={cc}
            ca={ca}
            updCs={updCs}
            t={t}
            toast={toast}
          />
        )}
        <ConfirmDeleteDialog dlg={confirmDlg} onCancel={cancelConfirm} />
        <UndoToast toast={undoToast} onUndo={runUndo} />
      </>
    );
  }

  // Brief 57 §3c — the list is a CONFIG now (src/lib/matterListCfg.tsx),
  // rendered by EntityList's declared shell; one builder for both matter
  // lists. What this view still owns: ccMetrics, nextEvDate, statusDateCc,
  // the list state, the import modal and its file plumbing, and the dialogs.
  const ccIds14 = new Set(cc.map((c: any) => c.id));
  const ccListCfg = buildMatterListCfg({
    kind: "cc",
    customFieldCols, // 189 §B
    /* 190 round 5 [R] — the column switchboard's gate: the load window keeps
       every column (viewer null), then `can(furniture.list.<k>)`. */
    colCan: columnGateOf({ viewer: _permViewer, can: _permCan }), // 191 fold-in 2 — the one gate
    t,
    fd,
    fc,
    rows: cc,
    ju,
    co,
    currentUser,
    metrics: ccMetrics,
    nextEvDate,
    statusDate: statusDateCc,
    todayStr: todayStrCc,
    sort,
    setSort,
    chipsValue: filter,
    onChips: setFilter,
    myToggle: currentUser
      ? {
          active: ccMy,
          onToggle: () => setCcMy((v) => !v),
          count: cc.filter((c: { responsiblePartners?: number[] }) =>
            (c.responsiblePartners || []).includes(currentUser),
          ).length,
        }
      : null,
    // 139 §2.7 — the second axis on this list too.
    prospectFilter: { value: ccProspect, onChange: setCcProspect },
    /* 140 §3.1 (brief 04 unit 8) — the second create button, withheld
       deliberately in 139 §2.5 until this brief. `addCtl.second` is already
       built in the shared config and the cc create arm is already built in
       `om` above (`genRef(true)` → the zero series); this prop is the wire
       between them, and the label is the seeded `matters.prospect.add.new`.
       The seed passed here is a SEED, not a form: `om` reads `isProspect &&
       !id` as "prefill a new prospect" and everything else as an edit. */
    onAddProspect: () => om({ isProspect: true }),
    searchText: searchQ,
    onSearch: setSearchQ,
    /* 195 §C.3e — 57c-iii's `searchExtra: globalQ` is gone with the page-level
       box it read: MEASURED always "" on this section (the box never drew on
       "cc", every navigation reset it); nothing this list matches moves. */
    locked: ccListLocked,
    onLockToggle: () => setCcListLocked((v: any) => !v),
    onImportOpen: () => ccFileRef.current?.click(),
    onFolderCheck: onFolderCheck as (() => void) | undefined, // 212 §27 — typed at the seam
    canCreate: _permCan("lateral.collectiveCases.create"),
    onAdd: () => om(null),
    caseTypeEntries: _caseTypeEntries,
    caseStatusEntries: _caseStatusEntries,
    caseStageEntries: _caseStageEntries,
    // 58 §5b — THE FIRM'S AXIS, and it was the one §4/§5 prop neither matter
    // list passed. `matterListCfg.tsx:641` calls `axisDayOf(schedAxis, dw)`
    // unguarded, so both lists threw on their first render and showed nothing.
    // The view already receives it from App; only this hop was missing.
    schedAxis,
    // 132 §6 — the same hop the axis needed: the view receives it from App
    // and the strip lives in matterListCfg.
    holidays,
    // 133 §4 — the same hop again: the three calendar-display switches are
    // resolved in App and only the strip reads them here.
    calDisp,
    agTypeFilt,
    setAgTypeFilt,
    canSeeFin,
    getInvoiceHeaderLabel,
    evtTypes,
    stripEvents: (ds: string) =>
      ag.filter(
        (a: any) =>
          a.date &&
          a.date.slice(0, 10) === ds &&
          a.ccId &&
          ccIds14.has(a.ccId),
      ),
    stripMatterOf: (ev: any) => cc.find((c: any) => c.id === ev.ccId),
    stripClick: (ev: any, cs14: any) => {
      if (cs14) {
        queueScrollToEv(ev.id);
        sDid(cs14.id);
      }
    },
    navAgenda: () => nav("ag"),
    rowClick: (c: any) => sDid(c.id),
  });
  /* 190 §D — THE BOARD over the collective list (surface 3's second half) —
     round 2 [B], RULED: no more Liste | Tableau strip; the list Bento is a
     capability EntityList carries (`bento`): its icon sits in the filter row,
     and while open the pane (CaseBoardView — the picker, the board) renders
     in place of the list body only, over the rows the filters left. A card
     moves when the viewer holds `surface.bento.list`, and the move writes
     ONE BentoPlacement row (itemKind 'cc'), firm-wide, nothing else (190
     (3)). The open state is App's transient `ccMode` (190 round 6 [Z] — it was the `ccListMode` preference). `t` and the
     mode setter narrowed at the seam (185's method). */
  const tB = t as BentoTFn;
  const setCcModeB = setCcMode as BentoDispatch<"list" | "board">;
  const sDidB = sDid as BentoDispatch<number>;
  const ccModeB: "list" | "board" = ccMode === "board" ? "board" : "list";
  const boardMovable = _permCan("surface.bento.list");
  const ccBento = _permCan("surface.bento.list")
    ? {
        active: ccModeB === "board",
        onToggle: () => { setCcModeB(ccModeB === "board" ? "list" : "board"); },
        title: tB("bento.list_title", { fallback: "List Bento" }),
        /* 190 [I] — the picker, in the icon's row right of it (EntityList renders it while active). */
        picker: <BentoPicker bento={listBento} id="bento-template-cclist" />,
        pane: (rows: unknown[], ctx: unknown) => (
          <CaseBoardView
            /* [S](i) — the template spanning both kinds: this list's filtered rows, then every non-archived case behind them, tagged. */
            cases={listBento.allMatters
              ? [...(rows as BoardCase[]), ...(ca as BoardCase[]).map((c) => ({ ...c, kind: "ca" as const }))]
              : (rows as BoardCase[])}
            kind="cc"
            bentoOther={listBento.allMatters ? listBentoOther : null}
            /* [C] — the template's fields resolved to the list's OWN columns
               (label and cell renderer, bound to the list's context), so the
               card shows a field exactly as the list does. */
            fields={listBento.fields.flatMap((k): BoardField[] => {
              const col = (ccListCfg.cols.t as BoardColumn[]).find((x) => x.k === k);
              if (!col) return [];
              const label = col.raw === true && typeof col.l === "string" ? col.l : tB(col.lk ?? `list.cc.col.${k}`, { fallback: col.l ?? k });
              return [{ k, label, render: (c) => col.r(c, ctx) }];
            })}
            bento={listBento}
            movable={boardMovable}
            /* 190 round 6 [Z] — a card click leaves the Bento (as the Eisenhower's does). */
            onOpen={({ kind, id }) => { setCcModeB("list"); if (kind === "ca") _navCase("ca", id); else sDidB(id); }}
          />
        ),
      }
    : undefined;
  // List view
  return (
    <>
      {modal}
      <EntityList
        {...ccListCfg}
        bento={ccBento}
        t={t}
        toast={toast}
        detailId={null}
        listSort={{}}
      />
      {/* 209 §19 — THE IMPORT'S FILE INPUT, BACK. EntityList keeps no <input> (it stays
          hookless, EntityList.tsx:109-112) and leaves it to the owning view; 57 §3c-ii
          (64c48743, 2026-08-10) moved this list into that shell and took the input out
          with the old toolbar, so « Import » clicked a ref nothing carried and the import
          could not be opened. Restored as it stood (287588a3). */}
      <input
        ref={ccFileRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files?.[0]) setCcImportFile(e.target.files[0]);
          e.target.value = "";
        }}
      />
      {ccImportFile && (
        <CollectiveCaseImportModal
          ccImportFile={ccImportFile}
          setCcImportFile={setCcImportFile}
          cc={cc}
          sCc={sCc}
          s={s}
          SK={SK}
          nid={nid}
          t={t}
          toast={toast}
          pools={listPools(undefined, op, ocu, ex, cp, oc, me, ar, op2, co, cl)} // 214 addendum 4 §25 — the window names a list entry
        />
      )}
      <ConfirmDeleteDialog dlg={confirmDlg} onCancel={cancelConfirm} />
      <UndoToast toast={undoToast} onUndo={runUndo} />
    </>
  );
};

export default CollectiveCaseView;
